import { Component, Fragment, useCallback, useEffect, useMemo, useRef, useState, type ErrorInfo, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { findLatestSummaryTurn, isSummaryPrompt, SUMMARY_JUMP_EVENT, summaryJumpTarget } from "../summary-turns.js";
import { awaitOpsHistoryRefresh, connectOpsHistoryRefresh, feedCacheTurns, opsFeedClearedAt, opsHistoryPageCursor, pendingTurnsAfterFeedClear } from "../clear-chat.js";
import { createTrailingRefresh } from "../use-board.js";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Check, Copy, LoaderCircle, MessageCircle, MessageSquareText, Paperclip, Phone, RotateCcw, ShieldCheck, Square, X } from "lucide-react";
import {
  OpsAssistantArtifactSchema,
  OpsAssistantPublicResponseSchema,
  OpsAssistantReferenceSchema,
  OpsImageJobProjectionSchema,
  OPS_EVIDENCE_ANCHOR_SCHEMA,
  OpsPublicProofSummarySchema,
  OpsPublicTurnEnvelopeSchema,
  decodeOpsPublicTurnEnvelope,
  bindOpsOrderedAnswerClaimSpans,
  isUnicodeScalarTextWithinLength,
  opsAnswerBusinessText,
  opsPublicResponseMatches,
  opsSha256,
  opsSha256Utf8,
  opsVisualProofKey,
  safeExternalHttpUrl,
  safeGoogleWorkspaceUrl,
  unicodeScalarPrefix,
  type AgentProposal,
  type OpsAnswerSourceAvailability,
  type OpsAssistantPublicResponse,
  type OpsEvidenceAnchor,
  type OpsPublicProposalProjection,
  type OpsPublicTurnEnvelope,
  type OpsSha256
} from "@business-ops/domain";
import { api, ApiError, type OpsAssistantCoverageSource, type OpsAssistantHistoryMessage, type OpsAssistantMutationCommand, type OpsAssistantResponse, type OpsAssistantWorkspaceContext, type OpsAssistantWorkspaceReference, type WhatsAppProofReplyContext } from "../api.js";
import {
  containsPersonalPhoneNumber,
  maskPersonalPhoneNumbers,
  privateModePresentationPrivacy,
  privateModePhoneMaskingFromRoot,
  projectPrivateTextSpan,
  usePrivateModePhoneMasking
} from "../privacy-mode.js";
import { useOpsMediaOwner, type OpsMediaOwnerController } from "./ops-media-owner.js";
import { OpsVoiceControl } from "./ops-voice-control.js";
import {
  beginOpsVoiceSyncTiming,
  useOpsVoice,
  type OpsRealtimeTurnPresentation,
  type OpsVoiceState
} from "./use-ops-voice.js";
import { opsLiveTurnFailureMessage, requestLiveLunaOps, type OpsLiveTurnProgress } from "./ops-live-turn.js";
import { SafeLinkedText } from "./safe-linked-text.js";
import { OPS_CHAT_FILE_ACCEPT, validateOpsChatFileSelection } from "./ops-file-selection.js";
import { OpsImageArtifactCard } from "./ops-image-artifact.js";
import { OpsDocumentArtifactCard } from "./ops-document-artifact.js";
import { OpsSharedAttachments } from "./ops-shared-attachment.js";
import { parseSharedAttachments, reconcileSharedAttachments, sharedUploadSourceIds, type SharedAttachment } from "./ops-shared-attachment-data.js";
import { OpsSourceFileAction } from "./ops-source-file-viewer.js";
import {
  acceptedWorkMutationFeedback,
  clearWorkMutationFeedback,
  targetedWorkMutationFeedback,
  workMutationFeedback,
  workMutationTarget,
  type WorkMutationFeedbackUpdate
} from "../work-mutation-feedback.js";

type Notify = (text: string, tone?: "success" | "error") => void;
type ConversationTail = ReactNode | ((renderedProposalIds: ReadonlySet<string>) => ReactNode);
const NO_CURRENT_REVIEW_PROPOSAL_IDS: ReadonlySet<string> = new Set();

export interface OpsProposalRenderGroup {
  id: string;
  proposalIds: readonly string[];
}

export interface Turn {
  id: string;
  prompt: string;
  response: OpsAssistantResponse | null;
  publicTurn?: OpsPublicTurnEnvelope | undefined;
  publicTurnSessionFresh?: boolean | undefined;
  // Set only by the authenticated history read; never restored from storage.
  historyVerified?: boolean | undefined;
  /** Authenticated database order only; not restored as browser authority. */
  historyOrderKey?: string | undefined;
  failed?: boolean;
  /** A transport-lost turn with no active owner/final is not safe to retry automatically. */
  recoveryUncertain?: boolean;
  pendingFile?: File | null;
  /** Authenticated retained-upload descriptors; never restored from browser cache. */
  attachments?: SharedAttachment[];
  progress?: string | undefined;
  spokenChatMessages?: readonly string[] | undefined;
  spokenAcknowledgmentPresentationId?: string | undefined;
  spokenAcknowledgmentRevision?: number | undefined;
  spokenAcknowledgmentMessageIndex?: number | undefined;
  textProgressPresentation?: true | undefined;
  realtimeFinal?: boolean | undefined;
  /** Local presentation provenance only; never model/history/effect authority. */
  realtimeInputSource?: "voice" | "typed" | undefined;
  realtimeCanonical?: boolean | undefined;
  realtimeCanonicalResponse?: OpsAssistantResponse | undefined;
  realtimeTranscript?: string | undefined;
  cancelled?: boolean | undefined;
  cancellationOwner?: "TEXT_STOP" | "VOICE_STOP" | undefined;
  /** Scoped browser-cache presentation only; never model, fact, or effect authority. */
  localPresentationHistory?: boolean | undefined;
  /** Verified Voice-ready presentation kind; never model/history authority. */
  localPresentationKind?: "voice-ready-welcome" | undefined;
  /** Cache-only placement anchor captured before this presentation was added. */
  localPresentationAfterTurnId?: string | null | undefined;
  imageNotice?: { text: string; tone: "status" | "error" } | undefined;
}

export interface OpsPendingTurnProgressTimer {
  report(progress: OpsLiveTurnProgress): void;
  stop(): void;
}

function opsPendingTurnStageLabel(progress: OpsLiveTurnProgress): string | null {
  return progress.phase === "connected"
    ? "Luna is responding…"
    : progress.phase === "checking"
      ? "Give me a few while I check."
      : null;
}

function opsPendingTurnElapsedLabel(requiresCheck: boolean, elapsedMs: number): string {
  const seconds = Math.max(1, Math.floor(elapsedMs / 1_000));
  return requiresCheck
    ? `Still checking · ${seconds}s`
    : `Luna is responding · ${seconds}s`;
}

// WHY [BOL-OPS-TEXT-PROGRESS-CADENCE-001]: server heartbeats prove stream
// health but can be 15 seconds apart, so they cannot own or re-anchor the
// visible wall clock. Capture one monotonic start for the Text turn; later
// receipts may change only the server-reported stage. Never synthesize a
// transport phase or feed these local ticks into Voice. Falsifier: the label
// jumps 1→15→30, a ticker survives its turn, or a local tick becomes a Voice
// transport event.
export function startOpsPendingTurnProgressTimer(
  publishLabel: (label: string) => void,
  now: () => number = () => performance.now()
): OpsPendingTurnProgressTimer {
  const startedAtMs = now();
  let latestProgress: OpsLiveTurnProgress | null = null;
  let stopped = false;
  const publishElapsed = (): void => {
    if (!latestProgress || stopped) return;
    publishLabel(opsPendingTurnElapsedLabel(
      latestProgress.requiresCheck,
      Math.max(0, now() - startedAtMs)
    ));
  };
  const timer = globalThis.setInterval(() => {
    publishElapsed();
  }, 1_000);
  return {
    report(progress) {
      if (stopped) return;
      latestProgress = progress;
      const stageLabel = opsPendingTurnStageLabel(progress);
      if (stageLabel) publishLabel(stageLabel);
      else publishElapsed();
    },
    stop() {
      if (stopped) return;
      stopped = true;
      latestProgress = null;
      globalThis.clearInterval(timer);
    }
  };
}

export interface OpsWorkspaceReferenceSummary {
  eyebrow: string;
  title: string;
  detail: string;
}

export function presentOpsWorkspaceReference(
  response: OpsAssistantResponse,
  openWorkspace: ((reference: OpsAssistantWorkspaceReference) => void) | undefined
): boolean {
  if (response.kind !== "ANSWER" || response.workspaceReference?.behavior !== "OPEN" || !openWorkspace) return false;
  openWorkspace(response.workspaceReference);
  return true;
}

export function defaultOpsWorkspaceReferenceSummary(reference: OpsAssistantWorkspaceReference): OpsWorkspaceReferenceSummary {
  const title = reference.surface === "CALENDAR"
    ? "Calendar"
    : reference.surface === "APPROVALS"
      ? "Approvals"
      : reference.workLens === "TIMELINE"
        ? "Timeline"
        : reference.workLens === "TABLE"
          ? "Task table"
          : "Board";
  const day = reference.focusDay
    ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(new Date(`${reference.focusDay}T12:00:00`))
    : null;
  return {
    eyebrow: "Live workspace",
    title: day ? `${title} · ${day}` : title,
    detail: `Open the current ${title.toLocaleLowerCase("en")} view.`
  };
}

export function opsWelcomeTourResponse(): OpsAssistantResponse {
  return {
    kind: "ANSWER",
    text: "Ask me in Text or Voice to summarize WhatsApp, find missed work or follow-ups, manage tasks, show Calendar or Board, read a file, research the web, or draft and schedule messages.\n\nI’ll show any action here for you to edit and approve. Nothing changes or sends before approval.\n\nTry: “Summarize the last 3 days and list anything still open.”",
    evidenceLabel: "Business rules",
    workItemIds: [],
    operatingModelVersion: 0
  };
}

export interface OpsPromptSlot {
  token: string;
  turnId: string;
  controller: AbortController;
  phase: "STARTING" | "UPLOAD" | "HISTORY" | "TURN";
  stopRequested: boolean;
  stopPromise: Promise<"CANCELLED" | "COMPLETED" | "NOT_ACTIVE" | "FAILED"> | null;
}

export function createOpsPromptSlot(
  token: string,
  turnId: string,
  controller: AbortController = new AbortController()
): OpsPromptSlot {
  return {
    token,
    turnId,
    controller,
    phase: "STARTING",
    stopRequested: false,
    stopPromise: null
  };
}

export function isOpsPromptSlotBusy(slot: OpsPromptSlot | null): boolean {
  return slot !== null;
}

export function settleOpsPromptSlot(slot: OpsPromptSlot | null, token: string): OpsPromptSlot | null {
  return slot?.token === token ? null : slot;
}

export function stopOpsPromptSlot(slot: OpsPromptSlot | null): boolean {
  if (!slot || slot.controller.signal.aborted || slot.stopRequested) return false;
  slot.stopRequested = true;
  return true;
}

export function acknowledgeOpsPromptStop(
  slot: OpsPromptSlot,
  outcome: "CANCELLED" | "COMPLETED" | "NOT_ACTIVE" | "FAILED"
): boolean {
  // WHY [BOL-TEXT-STOP-ACK-001]: local fetch abort used to paint "Stopped"
  // while the authenticated server turn could still commit. Only a settled
  // server outcome may release the slot. During sequential upload/history,
  // COMPLETED/NOT_ACTIVE also proves no model turn is active; the browser owns
  // the still-unstarted transition and must not launch it after Stop.
  // Falsifier: a model/tool starts after that acknowledgment, or a COMMITTING
  // model turn is hidden by local cancelled state instead of its real result.
  if (outcome === "CANCELLED"
    || ((slot.phase === "UPLOAD" || slot.phase === "HISTORY") && (outcome === "COMPLETED" || outcome === "NOT_ACTIVE"))) {
    if (!slot.controller.signal.aborted) slot.controller.abort();
    return true;
  }
  slot.stopRequested = false;
  slot.stopPromise = null;
  return false;
}

export function cancelOpsTextTurn(turns: readonly Turn[], turnId: string): Turn[] {
  return turns.map((turn) => turn.id === turnId
    && turn.response === null
    && !turn.publicTurn
    && !turn.failed
    && !turn.cancelled
    ? {
        ...turn,
        progress: undefined,
        cancelled: true,
        cancellationOwner: "TEXT_STOP"
      }
    : turn);
}

// WHY [BOL-OPS-LOCAL-FAILURE-KIND-001]: transport, schema, release-envelope,
// and local Voice failures are browser-owned failure state, never Gemini asking
// a question. Calling them CLARIFICATION made an unavailable/broken transport
// look like model-owned ambiguity. Keep the row failed and therefore excluded
// from history/persistence/effect authority while rendering its truthful retry
// affordance. Falsifier: any local failure presents as CLARIFICATION, enters a
// later model request or reload, gains an action control, or changes a signed
// public CLARIFICATION.
export function presentOpsLocalTurnFailure(turn: Turn, message: string): Turn {
  return {
    ...turn,
    response: {
      kind: "FAILURE",
      text: message,
      operatingModelVersion: 0
    },
    failed: true,
    progress: undefined,
    realtimeFinal: false,
    realtimeCanonical: false,
    realtimeCanonicalResponse: undefined,
    realtimeTranscript: undefined,
    cancelled: false
  };
}

export function applyOpsRealtimeTurnPresentation(
  turns: readonly Turn[],
  event: OpsRealtimeTurnPresentation
): Turn[] {
  const update = (turnId: string, apply: (turn: Turn) => Turn): Turn[] => turns.map((turn) => (
    turn.id === turnId ? apply(turn) : turn
  ));
  if (event.type === "input-final") {
    const existing = turns.some((turn) => turn.id === event.turnId);
    if (existing) {
      return update(event.turnId, (turn) => ({
        ...turn,
        prompt: event.transcript,
        response: null,
        failed: false,
        pendingFile: null,
        progress: undefined,
        spokenChatMessages: [],
        realtimeFinal: false,
        realtimeInputSource: event.source,
        realtimeCanonical: false,
        realtimeCanonicalResponse: undefined,
        realtimeTranscript: undefined,
        cancelled: event.control === "explicit_stop",
        cancellationOwner: event.control === "explicit_stop" ? "VOICE_STOP" : undefined
      }));
    }
    return [...turns, {
      id: event.turnId,
      prompt: event.transcript,
      response: null,
      pendingFile: null,
      progress: undefined,
      spokenChatMessages: [],
      realtimeFinal: false,
      realtimeInputSource: event.source,
      realtimeCanonical: false,
      cancelled: event.control === "explicit_stop",
      ...(event.control === "explicit_stop" ? { cancellationOwner: "VOICE_STOP" as const } : {})
    }];
  }
  if (event.type === "input-revision") {
    return update(event.turnId, (turn) => ({ ...turn, prompt: event.transcript }));
  }
  if (event.type === "checking") {
    return update(event.turnId, (turn) => turn);
  }
  // WHY [BOL-VOICE-SPOKEN-CHAT-APPEND-001]: Voice acknowledgment and later
  // truthful status cues previously shared one transient `progress` slot, so
  // each new spoken phrase erased the prior one and the final card erased both.
  // Keep every actually emitted phrase as an ordered, turn-bound Chat row. A
  // streaming ACK's stable presentation ID revises only its own prefix row;
  // progress and final rows remain append-only and cannot be overwritten.
  // canonical response and model history remain owned solely by the signed
  // public turn. Authenticated history restores separately receipted phrases.
  // Falsifier: acknowledgment, later progress, or the final result replaces,
  // covers, reorders, persists as model history, or hides an earlier utterance.
  const appendSpokenChatMessage = (turn: Turn, text: string): Turn => ({
    ...turn,
    progress: undefined,
    spokenChatMessages: [...(turn.spokenChatMessages ?? []), text],
    cancelled: false
  });
  if (event.type === "acknowledgment") {
    return update(event.turnId, (turn) => {
      if (turn.response) return turn;
      const presentationId = event.presentationId ?? `legacy-ack-${event.turnId}`;
      const revision = event.revision ?? 0;
      if (turn.spokenAcknowledgmentPresentationId === presentationId
        && turn.spokenAcknowledgmentMessageIndex !== undefined) {
        if (revision <= (turn.spokenAcknowledgmentRevision ?? -1)) return turn;
        const messages = [...(turn.spokenChatMessages ?? [])];
        if (turn.spokenAcknowledgmentMessageIndex >= messages.length) return turn;
        messages[turn.spokenAcknowledgmentMessageIndex] = event.text;
        return {
          ...turn,
          progress: undefined,
          spokenChatMessages: messages,
          spokenAcknowledgmentRevision: revision,
          cancelled: false
        };
      }
      const messageIndex = (turn.spokenChatMessages ?? []).length;
      const appended = appendSpokenChatMessage(turn, event.text);
      return {
        ...appended,
        spokenAcknowledgmentPresentationId: presentationId,
        spokenAcknowledgmentRevision: revision,
        spokenAcknowledgmentMessageIndex: messageIndex
      };
    });
  }
  if (event.type === "model-progress") {
    return update(event.turnId, (turn) => turn.response ? turn : appendSpokenChatMessage(turn, event.text));
  }
  if (event.type === "status-cue") {
    const presentationOnly = event.cueId === "voice_ready_welcome" && Boolean(event.presentationId);
    if (presentationOnly) {
      const presentationTurnId = event.presentationId!;
      const existingPresentation = turns.find((turn) => turn.id === presentationTurnId);
      if (existingPresentation) return [...turns];
      // WHY [BOL-VOICE-READY-PRESENTATION-001]: the receiver-started welcome
      // is real assistant speech, but it has no user input turn. Give it a
      // separate deterministic presentation identity so the first microphone
      // turn cannot overwrite it; keep it local/presentation-only so it never
      // becomes model history, a spinner, a fact, or an effect authority.
      // Falsifier: the welcome is absent after reload, creates a user prompt or
      // model-history message, or an orphan non-welcome cue creates a row.
      return [...turns, {
        id: presentationTurnId,
        prompt: "",
        response: null,
        spokenChatMessages: [event.text],
        textProgressPresentation: true,
        cancelled: true,
        localPresentationHistory: true,
        localPresentationKind: "voice-ready-welcome",
        localPresentationAfterTurnId: turns.at(-1)?.id ?? null
      }];
    }
    return update(event.turnId, (turn) => turn.response ? turn : appendSpokenChatMessage(turn, event.text));
  }
  if (event.type === "public-turn") {
    // WHY [BOL-VOICE-CANONICAL-CARD-001]: the final spoken bytes, evidence,
    // proposal ledger, hashes, and release identity arrive as one server-owned
    // envelope. Install response+identity+fresh-session capability atomically;
    // raw transcript/restored history can never manufacture approval controls.
    // response.text alone owns the readable/spoken answer. The same primary
    // TurnResult separately carries ordered claim-bound proof; neither Web nor
    // Voice reconstructs one field from the other or concatenates proof into
    // history. Falsifier: Text and Voice differ, a later event replaces the envelope,
    // proof text, URLs, timestamps, group/source labels, or navigation words
    // enter realtimeTranscript/history, or stale history becomes executable.
    return update(event.turnId, (turn) => {
      const installed = installFreshOpsPublicTurn(turn, event.publicTurn);
      return installed ? {
        ...installed,
        failed: false,
        progress: undefined,
        realtimeFinal: true,
        realtimeCanonical: true,
        realtimeCanonicalResponse: undefined,
        realtimeTranscript: event.publicTurn.result.response.text,
        cancelled: false
      } : turn;
    });
  }
  if (event.type === "assistant-partial" || event.type === "assistant-final") {
    return update(event.turnId, (turn) => turn.publicTurn ? turn : ({
      ...turn,
      response: realtimeResponseWithTranscript(undefined, event.transcript),
      failed: false,
      progress: undefined,
      realtimeFinal: event.type === "assistant-final",
      realtimeCanonical: Boolean(turn.realtimeCanonicalResponse),
      realtimeTranscript: event.transcript,
      cancelled: false
    }));
  }
  if (event.type === "failed") {
    return update(event.turnId, (turn) => {
      const hasCompleteAnswer = Boolean(turn.publicTurn
        || (turn.response && turn.realtimeTranscript !== undefined));
      if (hasCompleteAnswer) return { ...turn, progress: undefined, cancelled: false };
      return presentOpsLocalTurnFailure(turn, event.message);
    });
  }
  // WHY [BOL-VOICE-CONTROL-NOT-CHAT-001]: interruption is transport control,
  // never a Luna answer. Keep any words Luna already rendered, settle the
  // exact pending row, and do not manufacture cancellation chatter that looks
  // like a conversational reply. Falsifier: a normal turn-two/three partial,
  // barge, Stop, reconnect, or terminal cleanup inserts a new assistant
  // "Voice turn cancelled" message.
  return update(event.turnId, (turn) => ({
    ...turn,
    progress: undefined,
    cancelled: turn.publicTurn ? false : !turn.realtimeFinal
  }));
}

export function applyOpsTextModelProgressPresentation(
  turns: readonly Turn[],
  turnId: string,
  text: string
): Turn[] {
  return applyOpsRealtimeTurnPresentation(turns, {
    type: "model-progress",
    turnId,
    text
  }).map((turn) => turn.id === turnId && turn.response === null
    ? { ...turn, textProgressPresentation: true }
    : turn);
}

function realtimeResponseWithTranscript(
  canonical: OpsAssistantResponse | undefined,
  transcript: string
): OpsAssistantResponse {
  return canonical ? { ...canonical, text: transcript } : {
    kind: "ANSWER",
    text: transcript,
    evidenceLabel: "Business sources",
    workItemIds: [],
    operatingModelVersion: 0
  };
}

interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface StoredConversationV1 {
  version: 1;
  expiresAt: number;
  draft: string;
  turns: Array<{ id: string; prompt: string; response: Exclude<OpsAssistantResponse, { kind: "MUTATION_PREVIEW" }> }>;
}

interface StoredConversationV2 {
  version: 2;
  expiresAt: number;
  draft: string;
  clearedAt?: string | null;
  turns: Array<{
      id: string;
      prompt: string;
      response: OpsAssistantPublicResponse | Exclude<OpsAssistantResponse, { kind: "MUTATION_PREVIEW" }>;
      publicTurn?: OpsPublicTurnEnvelope;
      messages?: readonly string[];
    } | {
      id: string;
      prompt: string;
      presentationOnly: true;
      messages: string[];
      stopped?: true;
      presentationKind?: "voice-ready-welcome";
      afterTurnId?: string | null;
    }>;
}

type StoredConversation = StoredConversationV1 | StoredConversationV2;

const STORAGE_PREFIX = "business_ops_ops_chat_v1";
const PENDING_TEXT_PREFIX = "business_ops_pending_text_v1";
const PENDING_TEXT_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;
const HISTORY_TTL_MS = 7 * 24 * 60 * 60 * 1_000;
const MAX_TURNS = 20;
const MAX_OWNER_MESSAGE_CHARS = 20_000;
const MAX_USER_HISTORY_PROJECTION_SCALARS = 2_000;
const MAX_STORED_ANSWER_CHARS = 20_000;
const MAX_ASSISTANT_HISTORY_PROJECTION_SCALARS = MAX_STORED_ANSWER_CHARS;
const MAX_STORED_WORK_IDS = 200;
const MAX_STORED_PROPOSAL_IDS = 100;
const MAX_STORED_EVIDENCE_SOURCES = 100;
const MAX_STORED_EVIDENCE_EXCERPT_CHARS = 8_000;
const MAX_STORED_WEB_SOURCES = 50;

type OpsMutationApplyClient = Pick<typeof api,
  "applyOpsMutation" | "prepareOpsWhatsAppDraft" | "attachProposalFile" | "adoptOpsPublicTurnProposal">;
type OpsProposalReader = Pick<typeof api, "proposal">;
type OpsAskClient = Pick<typeof api, "askOps">;
type OpsThreadScrollBox = Pick<HTMLElement, "scrollTop" | "clientHeight" | "scrollHeight">;

export function isOpsThreadNearBottom(thread: OpsThreadScrollBox, tolerancePx = 48): boolean {
  return thread.scrollHeight - thread.clientHeight - thread.scrollTop <= tolerancePx;
}

export function opsThreadFollowLatestAfterScroll(
  thread: OpsThreadScrollBox,
  previousScrollTop: number | null,
  wasFollowing: boolean
): boolean {
  // WHY [BOL-OPS-CHAT-SCROLL-DIRECTION-001]: the first upward finger movement
  // can remain inside the old 48px "near bottom" band. Treat direction as the
  // user's receipt, then require an intentionally reached bottom before
  // rearming follow; otherwise the next transcript delta snaps the reader back.
  // Falsifier: a 30px upward move during speech still follows, or merely
  // scrolling near (but not to) the bottom rearms autonomous settling.
  if (previousScrollTop !== null && thread.scrollTop < previousScrollTop - 0.5) return false;
  if (wasFollowing) return previousScrollTop === null
    ? isOpsThreadNearBottom(thread)
    : true;
  return isOpsThreadNearBottom(thread, 1);
}

export function shouldReleaseOpsThreadAutoFollow(input:
  | { kind: "wheel"; deltaY: number }
  | { kind: "touch"; previousY: number; currentY: number }
): boolean {
  if (input.kind === "wheel") return input.deltaY < 0;
  // A finger moving down moves the viewport toward older content.
  return input.currentY - input.previousY > 3;
}

export function opsThreadFollowLatestAfterInputFinal(
  wasFollowing: boolean,
  source: "voice" | "typed"
): boolean {
  // WHY [BOL-OPS-CHAT-VOICE-FINAL-SCROLL-001]: a finalized Voice utterance is
  // conversation content, not a new scroll-intent receipt. Re-enabling follow
  // here revoked an operator's explicit upward release on every later spoken
  // turn (including Stop) and the turn-render effect pulled the phone back to
  // the bottom. Preserve the existing Voice state: tail followers keep
  // following, while readers stay put until they deliberately reach bottom or
  // press Latest. A typed Send remains an intentional request to reveal its new
  // row. Falsifier: a Voice final rearms after an upward release, Voice at the
  // tail stops following, or typed Send/Latest no longer reveal the new tail.
  return source === "typed" ? true : wasFollowing;
}

export interface OpsVoiceChatDraftPresentation {
  turnId: string;
  text: string;
}

export function opsVoiceChatDraftPresentation(
  state: Pick<OpsVoiceState, "mode" | "phase" | "turnId" | "transcript">,
  turns: readonly Pick<Turn, "id">[]
): OpsVoiceChatDraftPresentation | null {
  // WHY [BOL-VOICE-INTERIM-CHAT-DRAFT-001]: partial ASR exists only in the
  // controller until input-final creates the canonical turn. Removing the
  // duplicate composer transcript must not make those already-visible words
  // disappear. Present the explicit current Voice/listening turn as one
  // transient user row in Chat; the canonical turn ID atomically suppresses it
  // on final/Stop. This value never enters turns, persistence, model history,
  // effects, ordering, or the follow-latest receipt. Falsifier: interim words
  // vanish or appear below Chat, a final appears twice, or the draft changes
  // authenticated history/scroll ownership.
  const text = state.transcript.trim();
  if (state.mode !== "voice" || state.phase !== "listening" || !state.turnId || !text) return null;
  if (turns.some((turn) => turn.id === state.turnId)) return null;
  return { turnId: state.turnId, text };
}

export function OpsVoiceChatDraft({ draft }: { draft: OpsVoiceChatDraftPresentation }) {
  return (
    <div className="ops-turn" data-ops-voice-draft={draft.turnId}>
      <div className="ops-message ops-message--user"><p><SafeLinkedText text={draft.text} /></p></div>
    </div>
  );
}

export function settleOpsThreadToLatest(thread: Pick<HTMLElement, "scrollTop" | "scrollHeight">, followLatest: boolean): boolean {
  if (!followLatest) return false;
  thread.scrollTop = thread.scrollHeight;
  return true;
}

type VisiblePublicTurnCacheEntry = { signature: string; parsed: OpsPublicTurnEnvelope };
const visiblePublicTurnCache = new WeakMap<object, VisiblePublicTurnCacheEntry>();
type ParsedPublicTurnCacheEntry = { signature: string; parsed: OpsPublicTurnEnvelope | null };
const parsedPublicTurnCache = new WeakMap<object, ParsedPublicTurnCacheEntry>();

// WHY [BOL-VOICE-PUBLIC-TURN-VALIDATION-CACHE-001]: R350 and the R707 literal
// edit/Voice timings found thousands of repeated full envelope+response parses
// on the main thread. An object-identity memo would trust in-place edits; a
// render-only memo would leave history/persistence readers revalidating. This
// unambiguous full-content signature admits only ordinary data objects and
// arrays, includes undefined and every own key, and fails open to the original
// validator for accessors/exotic/cyclic shapes. It is an equality token, not a
// replacement for the first schema/hash/release/response check. Falsifier:
// nested byte/response/turn/release edits hit the cache, a returned object can
// poison a later read, or unchanged reads repeat schema parsing.
function visiblePublicTurnSignature(value: unknown): string | null {
  const parts: string[] = [];
  const seen = new WeakSet<object>();
  const append = (next: unknown): boolean => {
    if (next === null) { parts.push("n;"); return true; }
    if (next === undefined) { parts.push("u;"); return true; }
    if (typeof next === "string") { parts.push("s", String(next.length), ":", next); return true; }
    if (typeof next === "boolean") { parts.push(next ? "b1;" : "b0;"); return true; }
    if (typeof next === "number") {
      if (!Number.isFinite(next)) return false;
      parts.push("d", Object.is(next, -0) ? "-0" : String(next), ";");
      return true;
    }
    if (typeof next !== "object" || seen.has(next)) return false;
    seen.add(next);
    const keys = Reflect.ownKeys(next);
    if (Array.isArray(next)) {
      if (Object.getPrototypeOf(next) !== Array.prototype || keys.length !== next.length + 1) return false;
      parts.push("a", String(next.length), ":");
      for (let index = 0; index < next.length; index += 1) {
        const descriptor = Object.getOwnPropertyDescriptor(next, String(index));
        if (!descriptor || !descriptor.enumerable || !Object.hasOwn(descriptor, "value")
          || !append(descriptor.value)) return false;
      }
    } else {
      const prototype = Object.getPrototypeOf(next);
      if (prototype !== Object.prototype && prototype !== null) return false;
      parts.push("o", String(keys.length), ":");
      for (const key of keys) {
        if (typeof key !== "string") return false;
        const descriptor = Object.getOwnPropertyDescriptor(next, key);
        if (!descriptor || !descriptor.enumerable || !Object.hasOwn(descriptor, "value")) return false;
        parts.push("k", String(key.length), ":", key);
        if (!append(descriptor.value)) return false;
      }
    }
    seen.delete(next);
    parts.push(";");
    return true;
  };
  try { return append(value) ? parts.join("") : null; } catch { return null; }
}

// Session-history identity and old-release fallback must share the first
// envelope validation. The private snapshot is never returned to a caller.
function parsedPublicTurnEnvelope(value: unknown): OpsPublicTurnEnvelope | null {
  const signature = visiblePublicTurnSignature(value);
  const key = value && typeof value === "object" ? value : null;
  const cached = key && signature ? parsedPublicTurnCache.get(key) : undefined;
  if (cached?.signature === signature) return cached.parsed;
  const parsed = OpsPublicTurnEnvelopeSchema.safeParse(value);
  if (key && signature && typeof structuredClone === "function") {
    try {
      const snapshot = parsed.success ? structuredClone(parsed.data) : null;
      parsedPublicTurnCache.set(key, { signature, parsed: snapshot });
      return snapshot;
    } catch { /* Unsupported clone shape uses the original validator. */ }
  }
  return parsed.success ? parsed.data : null;
}

export function opsVisiblePublicTurn(
  turn: Pick<Turn, "id" | "response" | "publicTurn">,
  expectedReleaseId?: OpsSha256 | null
): OpsPublicTurnEnvelope | null {
  const finishTiming = beginOpsVoiceSyncTiming("visiblePublicTurn");
  try {
  if (!turn.publicTurn || !turn.response || expectedReleaseId === null) return null;
  const signature = visiblePublicTurnSignature([turn.id, expectedReleaseId, turn.publicTurn, turn.response]);
  const cacheKey = typeof turn.publicTurn === "object" ? turn.publicTurn : null;
  const cached = cacheKey && signature ? visiblePublicTurnCache.get(cacheKey) : undefined;
  if (cached?.signature === signature && typeof structuredClone === "function") {
    try { return structuredClone(cached.parsed); } catch { visiblePublicTurnCache.delete(cacheKey!); }
  }
  const parsed = parsedPublicTurnEnvelope(turn.publicTurn);
  if (!parsed
    || parsed.turnId !== turn.id
    || (expectedReleaseId !== undefined && parsed.releaseId !== expectedReleaseId)
    || !opsPublicResponseMatches(parsed.result.response, turn.response)) return null;
  if (cacheKey && signature && typeof structuredClone === "function") {
    try { visiblePublicTurnCache.set(cacheKey, { signature, parsed: structuredClone(parsed) }); }
    catch { /* Unsupported clone shape uses the uncached validated result. */ }
  }
  // The shared envelope snapshot is private even when this was a first read.
  if (cacheKey && signature && typeof structuredClone === "function") {
    try { return structuredClone(parsed); } catch { /* Uncacheable shape below. */ }
  }
  return parsed;
  } finally { finishTiming?.(); }
}

function opsVisibleHistoryTurn(
  turn: Pick<Turn, "id" | "response" | "publicTurn" | "historyVerified">,
  expectedReleaseId?: OpsSha256 | null
): OpsPublicTurnEnvelope | null {
  return opsVisiblePublicTurn(turn, expectedReleaseId)
    ?? (turn.historyVerified === true ? opsVisiblePublicTurn(turn) : null);
}

export function acceptOpsPublicTurnRevision<T extends Pick<Turn, "id" | "response" | "publicTurn">>(
  currentTurn: T,
  nextValue: unknown
): (T & { response: OpsAssistantResponse; publicTurn: OpsPublicTurnEnvelope }) | null {
  const current = opsVisiblePublicTurn(currentTurn);
  const next = OpsPublicTurnEnvelopeSchema.safeParse(nextValue);
  // This is a consumer CAS, never a browser publisher: only a complete next
  // result from the existing server seam may settle visible/proposal/receipt
  // state. Falsifier: a poll-derived string or stale same-revision packet can
  // replace the exact result already painted for this turn.
  if (!current || !next.success
    || next.data.turnId !== current.turnId
    || next.data.requestId !== current.requestId
    || next.data.releaseId !== current.releaseId
    || next.data.revision !== current.revision + 1) return null;
  return {
    ...currentTurn,
    response: next.data.result.response as OpsAssistantResponse,
    publicTurn: next.data
  };
}

export function installFreshOpsPublicTurn(
  currentTurn: Turn,
  nextValue: unknown
): Turn | null {
  const next = OpsPublicTurnEnvelopeSchema.safeParse(nextValue);
  if (!next.success || next.data.turnId !== currentTurn.id) return null;
  const installed = currentTurn.publicTurn
    ? acceptOpsPublicTurnRevision(currentTurn, next.data)
    : {
        ...currentTurn,
        response: next.data.result.response as OpsAssistantResponse,
        publicTurn: next.data
      };
  if (!installed) return null;
  return {
    ...installed,
    // Browser persistence deliberately omits this capability bit. Only a
    // just-returned server envelope in the active Text/Voice session can make
    // its immutable preview reachable; restored history remains inert.
    publicTurnSessionFresh: next.data.result.response.kind === "MUTATION_PREVIEW"
  };
}

export function presentOpsApplyFailure(
  turn: Turn,
  failedTurnId: string,
  fallback: OpsAssistantResponse
): Turn {
  // A rejected/failed request does not tell Web whether authority changed
  // before or after a durable effect. It therefore cannot truthfully replace
  // the server-authored result; keeping response+envelope intact also preserves
  // the exact visible fallback if React presentation fails.
  return turn.id === failedTurnId && !turn.publicTurn
    ? { ...turn, response: fallback }
    : turn;
}

// WHY [BOL-OPS-VISIBLE-HISTORY-IDENTITY-001]: a stored Luna result is operator
// history, not hidden model context. Dedupe only one immutable turn identity;
// repeated prompts with different IDs remain distinct. A conflicting request,
// release, or same-revision result poisons that identity instead of choosing a
// convenient copy. Falsifier: reload hides a valid answer/proposal, merges two
// intentional prompts, or paints either side of an identity conflict.
export function opsVisibleSessionTurns<T extends Pick<Turn, "id" | "response" | "publicTurn" | "historyVerified">>(
  turns: readonly T[],
  _restoredIds?: ReadonlySet<string>,
  expectedReleaseId?: OpsSha256 | null
): T[] {
  const finishTiming = beginOpsVoiceSyncTiming("visibleSessionTurns");
  try {
  const selected = new Map<string, { turn: T; publicTurn: OpsPublicTurnEnvelope | null; order: number }>();
  const poisoned = new Set<string>();
  turns.forEach((turn, order) => {
    const parsedIdentity = turn.publicTurn ? parsedPublicTurnEnvelope(turn.publicTurn) : null;
    const identity = parsedIdentity ? parsedIdentity.turnId : turn.id;
    if (poisoned.has(identity)) return;
    if (turn.publicTurn) {
      const publicTurn = opsVisibleHistoryTurn(turn, expectedReleaseId);
      if (!publicTurn) {
        poisoned.add(identity);
        selected.delete(identity);
        return;
      }
      const existing = selected.get(identity);
      if (!existing) {
        selected.set(identity, { turn, publicTurn, order });
        return;
      }
      if (existing.publicTurn
        && (existing.publicTurn.requestId !== publicTurn.requestId
          || existing.publicTurn.releaseId !== publicTurn.releaseId
          || (existing.publicTurn.revision === publicTurn.revision
            && existing.publicTurn.resultHash !== publicTurn.resultHash))) {
        poisoned.add(identity);
        selected.delete(identity);
        return;
      }
      if (!existing.publicTurn || publicTurn.revision > existing.publicTurn.revision) {
        selected.set(identity, { turn, publicTurn, order: existing.order });
      }
      return;
    }
    const existing = selected.get(identity);
    if (!existing) selected.set(identity, { turn, publicTurn: null, order });
  });
  return [...selected.values()].sort((left, right) => left.order - right.order).map(({ turn }) => turn);
  } finally { finishTiming?.(); }
}

export function submitTypedOpsPrompt(input: {
  historyReady?: () => boolean;
  voiceSelected: boolean;
  submitRealtimeText: () => boolean;
  selectText: () => void;
  runPrompt: () => Promise<OpsAssistantResponse | null>;
}): Promise<OpsAssistantResponse | null> {
  // History admission precedes mode changes and draft consumption in both
  // transports. A loading/failed read is not an empty authorized conversation.
  if (input.historyReady?.() === false) return Promise.resolve(null);
  // WHY [BOL-TYPED-LIVE-CONTINUITY-001]: the persistent Live controller now
  // emits the same release-bound public turn/Manual card as Text. Forcing a
  // plain typed turn through the old HTTP branch closed Voice, cold-started a
  // second request, and recreated the user's slow/stuck split conversation.
  // Keep selected Voice on its existing socket; if admission is still opening,
  // preserve the draft and fail closed instead of silently invoking HTTP.
  // Falsifier: a Voice-selected typed turn changes mode, creates two answers,
  // loses its canonical approval card, or bypasses the Text path in Text mode.
  if (input.voiceSelected) {
    input.submitRealtimeText();
    return Promise.resolve(null);
  }
  input.selectText();
  return input.runPrompt();
}

const OPS_PROPOSAL_TERMINAL_STATES = new Set<AgentProposal["state"]>([
  "SUCCEEDED", "FAILED", "DELIVERY_UNCERTAIN", "CANCELLED", "REJECTED", "EXPIRED"
]);
const OPS_PROPOSAL_POLL_DELAYS_MS = [0, 350, 650, 1_000, 1_500, 2_500, 4_000, 6_000, 8_000] as const;

export async function waitForProposalOutcome(
  proposalId: string,
  reader: OpsProposalReader = api,
  pause: (milliseconds: number) => Promise<void> = (milliseconds) => new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds))
): Promise<AgentProposal | null> {
  // WHY [BOL-OPS-SEND-SETTLEMENT-001]: approval is not proof of a WhatsApp
  // send. Poll only the exact approved proposal for a bounded 24 seconds and
  // stop at its terminal ledger state; never infer success from queue removal.
  // Falsifier: the chat says Sent while the proposal is non-terminal/failed,
  // or one approval creates any retry/second transport effect.
  for (const delay of OPS_PROPOSAL_POLL_DELAYS_MS) {
    if (delay) await pause(delay);
    try {
      const proposal = await reader.proposal(proposalId);
      if (OPS_PROPOSAL_TERMINAL_STATES.has(proposal.state)) return proposal;
    } catch {
      // A transient read failure grants no success claim. The next bounded
      // observation may still recover; exhaustion leaves the honest pending text.
    }
  }
  return null;
}

export function describeApprovedWhatsAppOutcome(proposal: AgentProposal): { text: string; tone?: "error" } {
  const destination = proposal.destinationLabel ?? "the verified WhatsApp destination";
  if (proposal.state === "SUCCEEDED") return { text: `Sent to ${destination}.` };
  if (proposal.state === "DELIVERY_UNCERTAIN") {
    return { text: `Delivery to ${destination} could not be confirmed. Check WhatsApp before retrying.`, tone: "error" };
  }
  return { text: `Nothing was sent to ${destination}. Final safety checks stopped this action.`, tone: "error" };
}

export async function applyOpsMutationWithOptionalFile(
  command: OpsAssistantMutationCommand,
  file: File | null,
  requestKey: string,
  publicTurn: Pick<OpsPublicTurnEnvelope, "turnId" | "requestId" | "revision" | "releaseId" | "resultHash">,
  client: OpsMutationApplyClient = api
): Promise<{ text: string; workItemId: string | null; proposalId: string | null; publicTurn: OpsPublicTurnEnvelope }> {
  if (command.action !== "DRAFT_WHATSAPP" || file === null) {
    return client.applyOpsMutation(command, requestKey, publicTurn);
  }

  const prepared = await client.prepareOpsWhatsAppDraft(command, requestKey);
  const reviewed = await client.attachProposalFile(prepared.id, prepared.version, file, `${requestKey}-file`);
  if (reviewed.effect.kind !== "SEND_MESSAGE"
    || reviewed.conversationId === null
    || reviewed.effect.conversationId !== reviewed.conversationId
    || !reviewed.destinationPolicyReady
    || reviewed.destinationPolicyId === null
    || reviewed.destinationPolicyVersion === null
    || reviewed.destinationPolicyVersion !== reviewed.expectedVersions.conversationPolicyVersion) {
    throw new Error("That WhatsApp destination changed before approval. Ask again to prepare a fresh action.");
  }
  const approval = {
    expectedVersion: reviewed.version,
    revision: reviewed.currentRevision,
    destinationConfirmation: {
      conversationId: reviewed.conversationId,
      policyId: reviewed.destinationPolicyId,
      policyVersion: reviewed.destinationPolicyVersion
    }
  };
  // WHY [BOL-OPS-FILE-ONE-CLICK-001]: prepare and attachment are inert. For a
  // canonical public preview the server adopts and approves the exact attached
  // revision in one row-locked transaction; approving first in Web allowed a
  // concurrent Reject to win after the send job existed. No unbound browser
  // approval path remains. Falsifier: a stale public turn can enqueue, or two
  // clicks can create two receipt/jobs.
  const adoptedPublicTurn = await client.adoptOpsPublicTurnProposal(
    publicTurn,
    reviewed.id,
    approval,
    `${requestKey}-approve`
  );
  return {
    text: `Approved for ${reviewed.destinationLabel ?? command.recipientLabel}. Final safety checks are running; it is not reported sent until WhatsApp confirms it.`,
    workItemId: null,
    proposalId: reviewed.id,
    publicTurn: adoptedPublicTurn
  };
}

export function OpsAssistantPanel({
  notify,
  afterBoardChange,
  embedded = false,
  historyScope = "default",
  publicReleaseId,
  openWork,
  workTitle,
  renderProposal,
  proposalRenderGroups = [],
  currentReviewProposalIds = NO_CURRENT_REVIEW_PROPOSAL_IDS,
  unmaterializedWorkProposalIds = NO_CURRENT_REVIEW_PROPOSAL_IDS,
  currentWorkItemIds = NO_CURRENT_REVIEW_PROPOSAL_IDS,
  conversationHeader,
  workspaceSurface,
  composerControls,
  attachmentTools,
  beforeConversation,
  afterConversation,
  conversationPriorityKey,
  mediaOwner,
  openWorkspace,
  workspaceReferenceSummary,
  workspaceContext,
  privateMode = true,
  onVoicePresentationPrivacyLockChange,
  openConversations,
  onWorkMutationFeedback
}: {
  notify: Notify;
  afterBoardChange: () => Promise<void>;
  embedded?: boolean;
  historyScope?: string;
  publicReleaseId?: OpsSha256 | null;
  openWork?: (id: string) => void;
  workTitle?: (id: string) => string;
  renderProposal?: (
    id: string,
    settlePublicTurn?: () => Promise<void>,
    publicProposals?: readonly OpsPublicProposalProjection[]
  ) => ReactNode;
  proposalRenderGroups?: readonly OpsProposalRenderGroup[];
  currentReviewProposalIds?: ReadonlySet<string>;
  unmaterializedWorkProposalIds?: ReadonlySet<string>;
  currentWorkItemIds?: ReadonlySet<string>;
  conversationHeader?: ConversationTail;
  workspaceSurface?: ConversationTail;
  composerControls?: ReactNode;
  attachmentTools?: ReactNode;
  beforeConversation?: ReactNode;
  afterConversation?: ConversationTail;
  conversationPriorityKey?: string | null;
  welcomeOnEntry?: boolean;
  mediaOwner?: OpsMediaOwnerController;
  openWorkspace?: (reference: OpsAssistantWorkspaceReference) => void;
  workspaceReferenceSummary?: (reference: OpsAssistantWorkspaceReference, workItemIds: readonly string[]) => OpsWorkspaceReferenceSummary;
  workspaceContext?: OpsAssistantWorkspaceContext | null;
  privateMode?: boolean;
  onVoicePresentationPrivacyLockChange?: (locked: boolean) => void;
  openConversations?: ((sourceEventId?: string) => void) | undefined;
  onWorkMutationFeedback?: (feedback: WorkMutationFeedbackUpdate) => void;
}) {
  const finishTiming = beginOpsVoiceSyncTiming("assistantRender");
  try {
  const turnIdentityId = publicReleaseId ?? null;
  const [restored] = useState(() => restoreOpsConversation(
    browserStorage(),
    historyScope,
    Date.now(),
    turnIdentityId
  ));
  const [text, setText] = useState(restored.draft);
  // Cached hashes establish integrity, not member authorization. Saved turns
  // become visible only after the authenticated server history read below.
  const [turns, setTurns] = useState<Turn[]>([]);
  const [turnsScope, setTurnsScope] = useState(historyScope);
  const [historyHydratedScope, setHistoryHydratedScope] = useState<string | null>(null);
  const [feedClearedAt, setFeedClearedAt] = useState<string | null>(null);
  const [earlierShown, setEarlierShown] = useState(false);
  const [historyCursor, setHistoryCursor] = useState<string | null>(null);
  const [earlierLoading, setEarlierLoading] = useState(false);
  const [clearPending, setClearPending] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const historyAdmissionScopeRef = useRef<string | null>(null);
  const currentHistoryScopeRef = useRef(historyScope);
  currentHistoryScopeRef.current = historyScope;
  const historyOwnerScopeRef = useRef(historyScope);
  const historyEpochRef = useRef(0);
  const feedClearedAtRef = useRef<string | null>(null);
  const earlierShownRef = useRef(false);
  const earlierRequestRef = useRef<AbortController | null>(null);
  const clearRequestRef = useRef(false);
  const refreshHistoryRef = useRef<() => Promise<boolean>>(async () => false);
  const pendingHistoryScrollRef = useRef<{ top: number; height: number } | null>(null);
  const turnsRef = useRef(turns);
  const identityTurns = useMemo(
    () => turnsScope === historyScope
      ? opsVisibleSessionTurns(turns, undefined, publicReleaseId ?? null) : [],
    [historyScope, publicReleaseId, turns, turnsScope]
  );
  // WHY [BOL-VOICE-HISTORY-RENDER-MEMO-001]: Voice state publishes rerender
  // this panel without changing its authenticated conversation. Rebuilding the
  // same fully validated history on every audio/status render spent measured
  // synchronous main-thread time. Its only semantic inputs are the validated
  // turn collection and release identity. Keep Text dispatch on turnsRef plus
  // the current request ID because an upload yield can expose newer history and
  // its pending request.
  // Falsifier: unchanged Voice publishes rerun requestHistory, or a new turn or
  // release opens/replaces a Voice session with stale conversation history.
  const realtimeHistory = useMemo(
    () => opsRequestHistory(identityTurns, turnIdentityId),
    [identityTurns, turnIdentityId]
  );
  const historyReady = historyHydratedScope === historyScope && turnsScope === historyScope;
  const evidenceHistorySettledRef = useRef(historyReady || historyError !== null);
  evidenceHistorySettledRef.current = historyReady || historyError !== null;
  const hasAuthorizedHistory = () => currentHistoryScopeRef.current === historyScope
    && historyAdmissionScopeRef.current === historyScope;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const activePromptRef = useRef<OpsPromptSlot | null>(null);
  // The old-turn cancellation window, never the new request lifetime.
  // A token prevents an old finally from clearing a newer replacement owner.
  const replacementPendingRef = useRef<string | null>(null);
  const upscaleRequestKeysRef = useRef(new Map<string, string>());
  const [promptRevision, setPromptRevision] = useState(0);
  const [applying, setApplying] = useState<string | null>(null);
  const [followingLatest, setFollowingLatest] = useState(true);
  const [upscaling, setUpscaling] = useState<{ turnId: string; factor: 2 | 4 } | null>(null);
  const threadRef = useRef<HTMLDivElement>(null);
  const followLatestThreadRef = useRef(true);
  const pendingEvidenceThreadReturnRef = useRef<OpsEvidenceThreadReturnReceipt | null>(
    typeof window === "undefined" ? null : opsEvidenceThreadReturnFromHistory(window.history.state)
  );
  const previousThreadScrollTopRef = useRef<number | null>(null);
  const threadTouchYRef = useRef<number | null>(null);
  const threadGestureActiveRef = useRef(false);
  const threadSettleFramesRef = useRef<{ layout: number; final: number }>({ layout: 0, final: 0 });
  const shownPriorityKeyRef = useRef<string | null>(null);
  const settlementStartedRef = useRef(new Set<string>());
  const publicTurnSettlementRef = useRef(new Map<string, Promise<void>>());
  const openedWorkspaceTurnIdsRef = useRef(new Set<string>());
  const mountedRef = useRef(true);
  const localMediaOwner = useOpsMediaOwner();
  const activeMediaOwner = mediaOwner ?? localMediaOwner;
  const updateFollowLatest = (next: boolean) => {
    followLatestThreadRef.current = next;
    setFollowingLatest((current) => current === next ? current : next);
  };
  const cancelPendingThreadSettle = () => {
    const frames = threadSettleFramesRef.current;
    if (frames.layout) window.cancelAnimationFrame(frames.layout);
    if (frames.final) window.cancelAnimationFrame(frames.final);
    frames.layout = 0;
    frames.final = 0;
  };
  const restoreLatestThread = () => {
    cancelPendingThreadSettle();
    pendingEvidenceThreadReturnRef.current = null;
    updateFollowLatest(true);
    const thread = threadRef.current;
    if (thread && settleOpsThreadToLatest(thread, true)) {
      previousThreadScrollTopRef.current = thread.scrollTop;
    }
  };
  // WHY [BOL-SUMMARY-JUMP-001]: the Summary button in the counts strip asks for
  // this jump by event. Releasing auto-follow here, before the scroll, is what
  // keeps the settle-to-latest frame from snapping the reader back down.
  const jumpToLatestSummaryTurn = () => {
    cancelPendingThreadSettle();
    pendingEvidenceThreadReturnRef.current = null;
    const thread = threadRef.current;
    const target = thread ? findLatestSummaryTurn(thread) : null;
    if (!thread || !target) return;
    updateFollowLatest(false);
    summaryJumpTarget(target).scrollIntoView({ block: "start" });
    previousThreadScrollTopRef.current = thread.scrollTop;
  };
  const jumpToLatestSummaryTurnRef = useRef(jumpToLatestSummaryTurn);
  jumpToLatestSummaryTurnRef.current = jumpToLatestSummaryTurn;
  useEffect(() => {
    const onJump = () => jumpToLatestSummaryTurnRef.current();
    window.addEventListener(SUMMARY_JUMP_EVENT, onJump);
    return () => window.removeEventListener(SUMMARY_JUMP_EVENT, onJump);
  }, []);
  // WHY [BOL-CLEAR-CHAT-001]: clearing records the boundary on the server first,
  // then empties the visible thread; the next ask therefore carries no earlier
  // history. "Show earlier messages" reads past the boundary without deleting it.
  const clearChat = async () => {
    if (clearRequestRef.current || !hasAuthorizedHistory()) return;
    clearRequestRef.current = true;
    const epoch = historyEpochRef.current;
    try {
      const result = await api.opsFeedClear();
      if (currentHistoryScopeRef.current !== historyScope || epoch !== historyEpochRef.current) return;
      historyEpochRef.current += 1;
      earlierRequestRef.current?.abort();
      earlierRequestRef.current = null;
      setEarlierLoading(false);
      const retained = pendingTurnsAfterFeedClear(turnsRef.current,
        isOpsPromptSlotBusy(activePromptRef.current) ? activePromptRef.current?.turnId : undefined);
      turnsRef.current = retained;
      setTurns(retained);
      feedClearedAtRef.current = result.clearedAt;
      setFeedClearedAt(result.clearedAt);
      earlierShownRef.current = false;
      setEarlierShown(false);
      setHistoryCursor(null);
      setClearPending(false);
      persistOpsConversation(browserStorage(), historyScope, text, retained, Date.now(), turnIdentityId, false, result.clearedAt);
      void refreshHistoryRef.current();
    } catch {
      if (currentHistoryScopeRef.current !== historyScope) return;
      setClearPending(false);
      setHistoryError("Clear chat didn’t work. Nothing changed.");
    } finally {
      clearRequestRef.current = false;
    }
  };
  const showEarlierTurns = async () => {
    if (earlierRequestRef.current || !hasAuthorizedHistory()) return;
    const controller = new AbortController();
    earlierRequestRef.current = controller;
    const epoch = historyEpochRef.current;
    const isCurrent = () => !controller.signal.aborted && currentHistoryScopeRef.current === historyScope
      && epoch === historyEpochRef.current;
    setEarlierLoading(true);
    try {
      const raw = await api.opsHistoryAll(historyCursor, controller.signal);
      const history = await loadOpsHistoryForScope(() => Promise.resolve(raw), historyScope, turnsRef.current, isCurrent);
      if (!history) return;
      // A concurrent cross-device clear wins over an older-page read. Never
      // resurrect that page under the new boundary; re-anchor through latest.
      if (opsFeedClearedAt(raw) !== feedClearedAtRef.current) {
        await refreshHistoryRef.current();
        return;
      }
      cancelPendingThreadSettle();
      pendingEvidenceThreadReturnRef.current = null;
      updateFollowLatest(false);
      const thread = threadRef.current;
      if (thread) pendingHistoryScrollRef.current = { top: thread.scrollTop, height: thread.scrollHeight };
      const merged = mergeOpsHistory(turnsRef.current, history, [], "earlier");
      turnsRef.current = merged;
      setTurns(merged);
      earlierShownRef.current = true;
      setEarlierShown(true);
      setHistoryCursor(opsHistoryPageCursor(raw));
      setHistoryError(null);
    } catch {
      if (!isCurrent()) return;
      setHistoryError("Earlier messages couldn’t load. Reload to try again.");
    } finally {
      if (earlierRequestRef.current === controller) {
        earlierRequestRef.current = null;
        setEarlierLoading(false);
      }
    }
  };
  const restoreEvidenceThread = (receipt: OpsEvidenceThreadReturnReceipt) => {
    pendingEvidenceThreadReturnRef.current = receipt;
    cancelPendingThreadSettle();
    updateFollowLatest(false);
    const settleScroll = () => {
      const thread = threadRef.current;
      if (!thread) return "UNCHANGED" as const;
      const outcome = settleOpsThreadAfterEvidenceRestore(thread, false, receipt);
      if (outcome === "RESTORED") previousThreadScrollTopRef.current = thread.scrollTop;
      return outcome;
    };
    settleScroll();
    threadSettleFramesRef.current.layout = window.requestAnimationFrame(() => {
      threadSettleFramesRef.current.layout = 0;
      settleScroll();
      threadSettleFramesRef.current.final = window.requestAnimationFrame(() => {
        threadSettleFramesRef.current.final = 0;
        const outcome = settleScroll();
        if (pendingEvidenceThreadReturnRef.current !== receipt) return;
        // WHY [BOL-OPS-EVIDENCE-RETURN-REPLAY-001]: this content-free receipt
        // belongs to the history entry, not one component mount. Keep it so
        // Forward back into a cited source and Back to chat can restore the
        // same turn/scroll again. Unrelated app routes sanitize copied state.
        const decision = opsEvidenceThreadRestoreDecision(
          outcome,
          evidenceHistorySettledRef.current
        );
        // WHY [BOL-OPS-EVIDENCE-RETURN-HYDRATION-001]: Back to chat remounts
        // with no authorized turns while its history request is still pending.
        // Two layout frames cannot prove the cited turn is absent. Retain the
        // content-free receipt until history settles, then either restore the
        // exact offset or take the bounded Latest fallback. Explicit reader
        // navigation clears the receipt at its event owner above/below.
        if (decision === "WAIT_FOR_HISTORY") return;
        pendingEvidenceThreadReturnRef.current = null;
        if (decision === "FALLBACK_LATEST") restoreLatestThread();
      });
    });
  };
  const voice = useOpsVoice({
    mediaOwner: activeMediaOwner,
    historyReady,
    ...(publicReleaseId ? { publicReleaseId } : {}),
    realtimeHistory,
    workspaceContext,
    presentationPrivacy: privateModePresentationPrivacy(privateMode),
    ...(onVoicePresentationPrivacyLockChange
      ? { onPresentationPrivacyLockChange: onVoicePresentationPrivacyLockChange }
      : {}),
    onRealtimeTurn: (event) => {
      // WHY [BOL-LUNA-LIVE-PRESENTATION-001]: native Live transcript/audio are
      // the one answer, not material to feed back through the Text HTTP Luna
      // branch. Present revisions in the existing chat while Manual Apply stays
      // the only effect surface. Falsifier: one voice final causes askOps(), an
      // assistant_text frame, a duplicate user row, or an actionable preview.
      if (event.type === "input-final") {
        updateFollowLatest(opsThreadFollowLatestAfterInputFinal(
          followLatestThreadRef.current,
          event.source
        ));
      }
      if (event.type === "public-turn" && !openedWorkspaceTurnIdsRef.current.has(event.turnId)) {
        if (presentOpsWorkspaceReference(event.publicTurn.result.response, openWorkspace)) {
          openedWorkspaceTurnIdsRef.current.add(event.turnId);
        }
      }
      if (event.type === "public-turn") {
        const targeted = targetedWorkMutationFeedback(event.publicTurn.result.response as OpsAssistantResponse, event.turnId);
        if (targeted) onWorkMutationFeedback?.(targeted);
      }
      setTurns((current) => applyOpsRealtimeTurnPresentation(current, event));
      if (event.type === "input-final" && event.source === "typed") {
        setText("");
        setSelectedFile(null);
      }
      if (event.type === "public-turn") {
        return waitForOpsAnswerPaint(event.publicTurn, () => true);
      }
    }
  });
  // The mutable Text request slot provides immediate serialization; this
  // revision schedules a render only when that exact request starts or settles.
  void promptRevision;
  const activePrompt = activePromptRef.current;
  const busy = isOpsPromptSlotBusy(activePrompt);
  const workActive = busy
    || applying !== null
    || turns.some((turn) => turn.response === null && !turn.cancelled);
  const proposalOwners = useMemo(
    () => proposalRenderOwners(identityTurns, proposalRenderGroups, currentReviewProposalIds),
    [identityTurns, proposalRenderGroups, currentReviewProposalIds]
  );
  const renderedProposalIds = useMemo(
    () => new Set(renderProposal ? proposalOwners.keys() : []),
    [proposalOwners, renderProposal]
  );
  const renderedConversationHeader = typeof conversationHeader === "function"
    ? conversationHeader(renderedProposalIds)
    : conversationHeader;
  const renderedWorkspaceSurface = typeof workspaceSurface === "function"
    ? workspaceSurface(renderedProposalIds)
    : workspaceSurface;

  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    historyEpochRef.current += 1;
    feedClearedAtRef.current = null;
    earlierShownRef.current = false;
    setFeedClearedAt(null);
    setEarlierShown(false);
    setHistoryCursor(null);
    historyAdmissionScopeRef.current = null;
    setHistoryHydratedScope(null);
    setHistoryError(null);
    if (historyOwnerScopeRef.current !== historyScope) {
      activePromptRef.current?.controller.abort();
      historyOwnerScopeRef.current = historyScope;
      turnsRef.current = [];
      setTurns([]);
      setTurnsScope(historyScope);
      setText(restoreOpsConversation(browserStorage(), historyScope).draft);
    }
    const cache = restoreOpsConversation(browserStorage(), historyScope);
    const isCurrent = () => current && currentHistoryScopeRef.current === historyScope;
    // WHY [BOL-CLEAR-CHAT-001]: the server reports the member's feed boundary with
    // the history; a cleared feed never resurrects turns cached in this browser.
    let refreshed = false;
    const refresh = createTrailingRefresh(async () => {
      const epoch = historyEpochRef.current;
      refreshed = false;
      try {
        const raw = await api.opsHistory(controller.signal);
        const clearedAt = opsFeedClearedAt(raw);
        const cachedTurns = feedCacheTurns(cache.turns, clearedAt, cache.clearedAt);
        const history = await loadOpsHistoryForScope(() => Promise.resolve(raw), historyScope, cachedTurns,
          () => isCurrent() && epoch === historyEpochRef.current);
        if (!history) return;
        const boundaryChanged = clearedAt !== feedClearedAtRef.current;
        if (boundaryChanged) {
          historyEpochRef.current += 1;
          earlierRequestRef.current?.abort();
          earlierRequestRef.current = null;
          setEarlierLoading(false);
          earlierShownRef.current = false;
          setEarlierShown(false);
          setClearPending(false);
        }
        feedClearedAtRef.current = clearedAt;
        setFeedClearedAt(clearedAt);
        // If more than one page arrived while asleep, restart the bounded page
        // frontier at the new head, preserving already painted older pages. An
        // old oldest cursor would otherwise skip the entire intervening gap.
        const currentIds = new Set(turnsRef.current.map((turn) => turn.id));
        const disconnectedHead = history.length > 0 && !history.some((turn) => currentIds.has(turn.id));
        if (!earlierShownRef.current || disconnectedHead) setHistoryCursor(opsHistoryPageCursor(raw));
        // Publish the authorized collection before admission. React may defer
        // its render; a captured callback must read this current owner, not [].
        const retained = boundaryChanged ? pendingTurnsAfterFeedClear(turnsRef.current,
          isOpsPromptSlotBusy(activePromptRef.current) ? activePromptRef.current?.turnId : undefined) : turnsRef.current;
        const merged = mergeOpsHistory(retained, history, cachedTurns);
        turnsRef.current = merged;
        setTurns(merged);
        historyAdmissionScopeRef.current = historyScope;
        setHistoryHydratedScope(historyScope);
        setHistoryError(null);
        refreshed = true;
      } catch {
        if (!isCurrent()) return;
        setHistoryError("Conversation history couldn’t load. Reload to try again.");
        // No empty authoritative result was received. The persistence owner
        // retains the previous cache while new draft/current turns remain usable.
      }
    });
    const synchronize = async () => { await refresh(); return isCurrent() && refreshed; };
    refreshHistoryRef.current = synchronize;
    void synchronize();
    const disconnect = connectOpsHistoryRefresh(historyScope, () => { void synchronize(); }, { window, document });
    return () => {
      current = false;
      controller.abort();
      earlierRequestRef.current?.abort();
      earlierRequestRef.current = null;
      if (refreshHistoryRef.current === synchronize) refreshHistoryRef.current = async () => false;
      disconnect();
    };
  }, [historyScope]);

  useEffect(() => {
    if (!historyReady || !turnIdentityId || activePromptRef.current) return;
    const hint = readPendingOpsTextTurn(browserStorage(), historyScope, turnIdentityId);
    if (!hint) return;
    if (turnsRef.current.some((turn) => turn.id === hint.turnId && turn.publicTurn)) {
      forgetPendingOpsTextTurn(browserStorage(), historyScope, hint.turnId);
      return;
    }
    const slot = createOpsPromptSlot(crypto.randomUUID(), hint.turnId);
    slot.phase = "TURN";
    activePromptRef.current = slot;
    setPromptRevision((current) => current + 1);
    setTurns((current) => current.some((turn) => turn.id === hint.turnId) ? current : [...current, {
      id: hint.turnId,
      prompt: hint.prompt,
      response: null,
      progress: "Checking Luna’s result…",
      cancelled: false
    }]);
    // A stored prompt is only local presentation. The authenticated history
    // read, not this hint or status polling, supplies the final prompt/context.
    void awaitOpsTextTurnStatus({
      turnId: hint.turnId,
      releaseId: turnIdentityId,
      signal: slot.controller.signal,
      readStatus: api.opsTurnStatus
    }).then(async (final) => {
      if (!mountedRef.current || activePromptRef.current?.token !== slot.token) return;
      // A status response may finish before an in-flight exact-ID Stop or
      // replacement ACK. That pending control owns the verdict; NOT_ACTIVE
      // alone must not clear its slot or repaint a cancelled turn uncertain.
      if (slot.stopRequested && slot.stopPromise) {
        const outcome = await slot.stopPromise;
        if (!mountedRef.current || activePromptRef.current?.token !== slot.token) return;
        if ((replacementPendingRef.current === slot.token && outcome !== "FAILED") || outcome === "CANCELLED") return;
        if (outcome === "COMPLETED" && !final) {
          final = await awaitOpsTextTurnStatus({ turnId: hint.turnId, releaseId: turnIdentityId,
            signal: slot.controller.signal, readStatus: api.opsTurnStatus });
        }
      }
      if (replacementPendingRef.current === slot.token) return;
      if (final) {
        const retained = turnsRef.current.filter((turn) => turn.id !== hint.turnId || turn.publicTurn);
        turnsRef.current = retained;
        setTurns(retained);
        if (await refreshHistoryRef.current()) {
          forgetPendingOpsTextTurn(browserStorage(), historyScope, hint.turnId);
        } else {
          setTurns((current) => current.some((turn) => turn.id === hint.turnId) ? current : [...current,
            { ...presentOpsLocalTurnFailure({
              id: hint.turnId, prompt: hint.prompt, response: null, cancelled: false
            }, "Luna finished, but the saved result could not be loaded. Reload to check it."), recoveryUncertain: true }
          ]);
        }
      } else {
        forgetPendingOpsTextTurn(browserStorage(), historyScope, hint.turnId);
        setTurns((current) => current.map((turn) => turn.id === hint.turnId
          ? { ...presentOpsLocalTurnFailure(turn,
              "Luna's result could not be confirmed. Your question was not sent again."), recoveryUncertain: true }
          : turn));
      }
    }).catch((error: unknown) => {
      if (slot.controller.signal.aborted || !mountedRef.current) return;
      if (replacementPendingRef.current === slot.token) return;
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        setTurns((current) => current.map((turn) => turn.id === hint.turnId
          ? { ...presentOpsLocalTurnFailure(turn,
              "Sign in to check Luna's result. Your question was not sent again."), recoveryUncertain: true }
          : turn));
        return;
      }
      // Invalid/absent authority is not a second answer or a retry license.
      setTurns((current) => current.map((turn) => turn.id === hint.turnId
        ? { ...presentOpsLocalTurnFailure(turn,
            "Luna's result could not be verified. Your question was not sent again."), recoveryUncertain: true }
        : turn));
    }).finally(() => {
      if (activePromptRef.current?.token !== slot.token) return;
      if (replacementPendingRef.current === slot.token) return;
      activePromptRef.current = null;
      if (mountedRef.current) setPromptRevision((current) => current + 1);
    });
    return () => slot.controller.abort();
  }, [historyReady, historyScope, turnIdentityId]);

  useEffect(() => {
    // State from the previous member must never be written under the new key
    // in the same effect flush that clears it.
    if (turnsScope !== historyScope) return;
    turnsRef.current = turns;
    persistOpsConversation(browserStorage(), historyScope, text, turns, Date.now(), turnIdentityId,
      historyHydratedScope !== historyScope, feedClearedAt);
  }, [historyScope, historyHydratedScope, text, turnIdentityId, turns, turnsScope, feedClearedAt]);

  const settlePublicTurn = (turnId: string): Promise<void> => {
    const previous = publicTurnSettlementRef.current.get(turnId) ?? Promise.resolve();
    const settlement = previous.then(async () => {
      const turn = turnsRef.current.find((candidate) => candidate.id === turnId);
      if (turn?.historyVerified) return;
      const current = turn ? opsVisiblePublicTurn(turn, turnIdentityId) : null;
      if (!current) return;
      try {
        const next = await api.settleOpsPublicTurn({
          turnId: current.turnId,
          requestId: current.requestId,
          revision: current.revision,
          releaseId: current.releaseId,
          resultHash: current.resultHash
        });
        // Publish the accepted CAS into the imperative chain before React's
        // updater runs. A terminal callback can arrive immediately after the
        // APPROVED callback; it must read revision N+1, not race stale N.
        turnsRef.current = turnsRef.current.map((candidate) => {
          if (candidate.id !== turnId) return candidate;
          return acceptOpsPublicTurnRevision(candidate, next) ?? candidate;
        });
        setTurns((existing) => {
          const updated = existing.map((candidate) => {
            if (candidate.id !== turnId) return candidate;
            const accepted = acceptOpsPublicTurnRevision(candidate, next);
            return accepted ?? candidate;
          });
          return updated;
        });
      } catch (error) {
        if (!(error instanceof ApiError && error.status === 409)) {
          notify("The action is still recorded, but its latest result could not refresh yet.", "error");
        }
      }
    });
    publicTurnSettlementRef.current.set(turnId, settlement);
    void settlement.finally(() => {
      if (publicTurnSettlementRef.current.get(turnId) === settlement) {
        publicTurnSettlementRef.current.delete(turnId);
      }
    });
    return settlement;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Release this browser's transport on unmount. The authenticated API
      // Text run now owns completion independently; the scoped pending hint
      // lets the next mounted owner recover it without a second POST.
      activePromptRef.current?.controller.abort();
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const initialReceipt = pendingEvidenceThreadReturnRef.current;
    if (initialReceipt) restoreEvidenceThread(initialReceipt);
    return listenOpsEvidenceThreadRestore(window, restoreEvidenceThread);
  }, []);

  useEffect(() => {
    const targets = turns.flatMap((turn) => !turn.publicTurn
      && turn.response?.kind === "ANSWER"
      && turn.response.evidenceLabel === "WhatsApp route"
      && turn.response.text.startsWith("Approved for ")
      ? (turn.response.proposalIds ?? []).map((proposalId) => ({ turnId: turn.id, proposalId, operatingModelVersion: turn.response?.operatingModelVersion ?? 0 }))
      : []);
    for (const target of targets) {
      if (settlementStartedRef.current.has(target.proposalId)) continue;
      settlementStartedRef.current.add(target.proposalId);
      void waitForProposalOutcome(target.proposalId).then(async (proposal) => {
        if (!proposal || !mountedRef.current) return;
        const outcome = describeApprovedWhatsAppOutcome(proposal);
        setTurns((current) => current.map((turn) => turn.id === target.turnId ? {
          ...turn,
          response: {
            kind: "ANSWER",
            text: outcome.text,
            evidenceLabel: "WhatsApp route",
            workItemIds: [],
            operatingModelVersion: target.operatingModelVersion
          }
        } : turn));
        await afterBoardChange();
        if (mountedRef.current) notify(outcome.text, outcome.tone);
      });
    }
  }, [afterBoardChange, notify, turns]);

  useEffect(() => {
    const thread = threadRef.current;
    const priorityChanged = Boolean(conversationPriorityKey)
      && shownPriorityKeyRef.current !== conversationPriorityKey;
    shownPriorityKeyRef.current = conversationPriorityKey ?? null;
    const hasVisibleConversation = identityTurns.length > 0;
    // Restored results are ordinary visible history. This mounted conversation
    // still owns bottom settling, including the first paint after reload.
    if (!thread) return;
    const evidenceReturn = pendingEvidenceThreadReturnRef.current;
    if (evidenceReturn) {
      // WHY [BOL-OPS-EVIDENCE-RETURN-SCROLL-001]: a proof opened from an older
      // turn owns a content-free turn/scroll receipt. It must settle that
      // summary before the ordinary Latest policy can move the thread.
      // Falsifier: proof Back/reload remounts at the newest turn, or restoring
      // the receipt changes the document viewport instead of the chat thread.
      restoreEvidenceThread(evidenceReturn);
      return cancelPendingThreadSettle;
    }
    if (!busy && !hasVisibleConversation && !priorityChanged) {
      if (!hasVisibleConversation) {
        thread.scrollTop = 0;
        previousThreadScrollTopRef.current = 0;
      }
      return;
    }
    const historyScroll = pendingHistoryScrollRef.current;
    pendingHistoryScrollRef.current = null;
    if (historyScroll && !threadGestureActiveRef.current && thread.scrollTop === historyScroll.top) {
      thread.scrollTop = historyScroll.top + thread.scrollHeight - historyScroll.height;
      previousThreadScrollTopRef.current = thread.scrollTop;
    }
    // WHY [BOL-OPS-LATEST-VISIBLE-001]: every conversation path now ends in the
    // newest answer or prepared approval directly above the composer. Keep that
    // bottom edge visible after late task links/layout settle; never scroll the
    // page or put autonomous approvals in a separate top queue. Falsifier: a
    // new prepared action opens above old chat or remains off-screen.
    // WHY [BOL-OPS-CHAT-USER-SCROLL-001]: native Live publishes transcript
    // revisions while Aoede is still speaking. Unconditionally settling every
    // revision trapped the phone at the bottom and fought the operator reading
    // earlier evidence. Follow a newly submitted turn by default, but re-check
    // the mutable user-scroll receipt inside every animation frame so the first
    // upward scroll wins immediately. Falsifier: speech drags an operator back
    // down, or a new self-submitted turn starts off-screen.
    const settleScroll = () => {
      if (threadGestureActiveRef.current) return;
      if (settleOpsThreadToLatest(thread, followLatestThreadRef.current)) {
        previousThreadScrollTopRef.current = thread.scrollTop;
      }
    };
    cancelPendingThreadSettle();
    settleScroll();
    threadSettleFramesRef.current.layout = window.requestAnimationFrame(() => {
      threadSettleFramesRef.current.layout = 0;
      settleScroll();
      threadSettleFramesRef.current.final = window.requestAnimationFrame(() => {
        threadSettleFramesRef.current.final = 0;
        settleScroll();
      });
    });
    return cancelPendingThreadSettle;
  }, [busy, conversationPriorityKey, historyError, historyReady, identityTurns, turns]);

  const runPrompt = async (
    prompt: string,
    existingId?: string,
    pendingFile: File | null = null
  ): Promise<OpsAssistantResponse | null> => {
    if (!hasAuthorizedHistory()) return null;
    const normalized = prompt.trim();
    if (!normalized) return null;
    const activeRequest = activePromptRef.current;
    if (isOpsPromptSlotBusy(activeRequest)) return null;
    const requestToken = crypto.randomUUID();
    const id = existingId ?? crypto.randomUUID();
    const requestPrompt = normalized;
    // The Text request slot serializes only this HTTP path. Realtime Voice owns
    // its own server turn ledger and never enters this callback.
    const promptSlot = createOpsPromptSlot(requestToken, id);
    activePromptRef.current = promptSlot;
    updateFollowLatest(true);
    setPromptRevision((current) => current + 1);
    if (!existingId) {
      setText("");
      setSelectedFile(null);
    }
    setTurns((current) => existingId
      ? current.map((turn) => turn.id === id
          ? { ...turn, response: null, failed: false, pendingFile, progress: "Connecting to Luna…", cancelled: false }
          : turn)
      : [...current, {
          id,
          prompt: normalized,
          response: null,
          pendingFile,
          progress: pendingFile ? "Reading file…" : "Connecting to Luna…",
          cancelled: false
        }]);
    let visibleProgressTimer: OpsPendingTurnProgressTimer | null = null;
    try {
      // WHY [BOL-OPS-CHAT-FILE-BROWSER-001]: a selected browser File used to
      // become only a filename sentence in the model prompt. Upload and finish
      // server-side inspection first, then pass the stable source ID to the
      // exact same Luna turn. The File stays on this turn so a later Manual
      // WhatsApp approval can bind the exact bytes; upload alone never sends.
      // Falsifier: the chip appears but no binary request occurs, Luna cites a
      // different source, or selecting a file creates an effect before review.
      const fileSelectionError = pendingFile ? validateOpsChatFileSelection(pendingFile) : null;
      if (fileSelectionError) throw new Error(fileSelectionError);
      promptSlot.phase = pendingFile ? "UPLOAD" : "TURN";
      const uploaded = pendingFile ? await api.uploadOpsFile(pendingFile, `${id}-file`, promptSlot.controller.signal) : null;
      // Playback-only videos persist on the turn without entering inspected evidence.
      const { sourceEventIds, presentationSourceEventIds } = sharedUploadSourceIds(uploaded);
      promptSlot.controller.signal.throwIfAborted();
      if (uploaded && hasAuthorizedHistory() && activePromptRef.current?.token === requestToken) {
        const attachments = parseSharedAttachments(uploaded.attachments).filter(item => item.sourceEventId === uploaded.sourceEventId);
        setTurns(current => current.map(turn => turn.id === id ? { ...turn, attachments } : turn));
      }
      if (pendingFile && promptSlot.stopRequested && promptSlot.stopPromise) {
        const outcome = await promptSlot.stopPromise;
        if (outcome === "CANCELLED" || outcome === "COMPLETED" || outcome === "NOT_ACTIVE") {
          if (!promptSlot.controller.signal.aborted) promptSlot.controller.abort();
          if (mountedRef.current) setTurns((current) => cancelOpsTextTurn(current, id));
          return null;
        }
      }
      promptSlot.phase = "TURN";
      if (pendingFile) {
        setTurns((current) => current.map((turn) => turn.id === id
          ? { ...turn, progress: "Connecting to Luna…" }
          : turn));
      }
      // WHY [BOL-LUNA-TEXT-HTTP-OWNER-001]: this is the one Text HTTP answer
      // path. Realtime Voice presents the same canonical server result through
      // its authenticated session bridge and must never inject a callback here.
      // Falsifier: a Voice transcript calls requestLiveLunaOps from the browser,
      // races a second answer, or creates Manual authority outside its ledger.
      const paintVisibleProgress = (label: string) => {
        if (activePromptRef.current?.token !== requestToken || promptSlot.controller.signal.aborted) return;
        setTurns((current) => current.map((turn) => turn.id === id
          ? { ...turn, progress: label }
          : turn));
      };
      const reportProgress = (progress: OpsLiveTurnProgress) => {
        if (activePromptRef.current?.token !== requestToken || promptSlot.controller.signal.aborted) return;
        visibleProgressTimer?.report(progress);
      };
      const reportModelProgress = (message: string) => {
        if (activePromptRef.current?.token !== requestToken || promptSlot.controller.signal.aborted) return;
        visibleProgressTimer?.stop();
        visibleProgressTimer = null;
        setTurns((current) => applyOpsTextModelProgressPresentation(current, id, message));
      };
      if (!turnIdentityId) {
        throw new ApiError(503, "RELEASE_IDENTITY_REQUIRED", "Luna is not available until this release is verified.");
      }
      // An upload or async callback can outlive a member/scope switch.
      if (!hasAuthorizedHistory()) {
        promptSlot.controller.abort();
        promptSlot.controller.signal.throwIfAborted();
      }
      // WHY [BOL-OPS-HISTORY-MOUNTED-001]: a tab may submit before the remote
      // clear hint arrives, or after an upload yields. Re-read the current
      // member boundary before deriving Text context; failed/stale reads do
      // not dispatch a model call. The active request and Stop owner survive.
      promptSlot.phase = "HISTORY";
      if (!await awaitOpsHistoryRefresh(refreshHistoryRef.current, promptSlot.controller.signal)) {
        throw new Error("Conversation history couldn’t refresh. Try again.");
      }
      if (promptSlot.stopRequested && promptSlot.stopPromise) {
        const outcome = await promptSlot.stopPromise;
        acknowledgeOpsPromptStop(promptSlot, outcome);
      }
      promptSlot.controller.signal.throwIfAborted();
      if (!hasAuthorizedHistory()) return null;
      promptSlot.phase = "TURN";
      visibleProgressTimer = startOpsPendingTurnProgressTimer(paintVisibleProgress);
      rememberPendingOpsTextTurn(browserStorage(), historyScope, {
        turnId: id, prompt: requestPrompt, releaseId: turnIdentityId
      });
      let publicTurn: OpsPublicTurnEnvelope;
      try {
        publicTurn = await requestLiveLunaOps(
        requestPrompt,
        opsRequestHistory(turnsRef.current, turnIdentityId, id),
        {
          expectedReleaseId: turnIdentityId,
          requestKey: id,
          sourceEventIds,
          ...(presentationSourceEventIds.length ? { presentationSourceEventIds } : {}),
          workspaceContext: workspaceContext ?? null,
          signal: promptSlot.controller.signal,
          onProgress: reportProgress,
          onModelProgress: reportModelProgress,
          onTerminal: (receipt) => {
            if (receipt.outcome !== "COMPLETED_FINAL" && receipt.outcome !== "SERVER_CLARIFICATION") {
              console.warn("business_ops_public_turn_terminal", receipt);
            }
          }
        }
        );
      } catch (error) {
        // WHY [BOL-TEXT-DETACHED-CONTINUITY-001]: an interrupted response is
        // not permission to run the same paid model again. Recover the exact
        // authenticated turn while it is still in the server registry, or the
        // signed final after commit. Never turn a missing status into a POST.
        if (!(error instanceof ApiError)
          || !["STREAM_TRANSPORT_FAILED", "STREAM_PREMATURE_EOF"].includes(error.code)
          || promptSlot.controller.signal.aborted) throw error;
        const recovered = await awaitOpsTextTurnStatus({
          turnId: id,
          releaseId: turnIdentityId,
          signal: promptSlot.controller.signal,
          readStatus: api.opsTurnStatus,
          onAttachments: attachments => {
            if (!hasAuthorizedHistory() || activePromptRef.current?.token !== requestToken) return;
            setTurns(current => current.map(turn => turn.id === id ? { ...turn, attachments: reconcileSharedAttachments(turn.attachments, attachments) } : turn));
          }
        });
        if (!recovered) {
          throw new ApiError(502, "TEXT_TURN_RESULT_UNCONFIRMED",
            "Luna's result could not be confirmed. Your question was not sent again.");
        }
        publicTurn = recovered;
      }
      if (promptSlot.stopRequested && promptSlot.stopPromise) {
        const outcome = await promptSlot.stopPromise;
        if (outcome === "CANCELLED") {
          if (!promptSlot.controller.signal.aborted) promptSlot.controller.abort();
          if (mountedRef.current) setTurns((current) => cancelOpsTextTurn(current, id));
          return null;
        }
        if (replacementPendingRef.current === requestToken && outcome !== "FAILED") return null;
      }
      promptSlot.controller.signal.throwIfAborted();
      if (activePromptRef.current?.token !== requestToken) return null;
      const response = publicTurn.result.response;
      if (!openedWorkspaceTurnIdsRef.current.has(id) && presentOpsWorkspaceReference(response, openWorkspace)) {
        openedWorkspaceTurnIdsRef.current.add(id);
      }
      const targeted = targetedWorkMutationFeedback(response as OpsAssistantResponse, id);
      if (targeted) onWorkMutationFeedback?.(targeted);
      setTurns((current) => current.map((turn) => {
        if (turn.id !== id) return turn;
        const installed = installFreshOpsPublicTurn(turn, publicTurn);
        return installed
          ? { ...installed, failed: false, progress: undefined, cancelled: false }
          : turn;
      }));
      forgetPendingOpsTextTurn(browserStorage(), historyScope, id);
      return response;
    } catch (error) {
      // A superseded local presentation cannot repaint the authoritative old
      // result as Stopped after replacement has resolved its server owner.
      if (!mountedRef.current || activePromptRef.current?.token !== requestToken) return null;
      if (promptSlot.stopRequested && promptSlot.stopPromise) {
        const outcome = await promptSlot.stopPromise;
        if (outcome === "CANCELLED"
          || ((promptSlot.phase === "UPLOAD" || promptSlot.phase === "HISTORY") && (outcome === "COMPLETED" || outcome === "NOT_ACTIVE"))) {
          if (!promptSlot.controller.signal.aborted) promptSlot.controller.abort();
          if (mountedRef.current) setTurns((current) => cancelOpsTextTurn(current, id));
          return null;
        }
        if (replacementPendingRef.current === requestToken && outcome !== "FAILED") return null;
      }
      if (promptSlot.controller.signal.aborted) {
        if (mountedRef.current) {
          setTurns((current) => cancelOpsTextTurn(current, id));
        }
        return null;
      }
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        setTurns((current) => current.map((turn) => turn.id === id
          ? { ...presentOpsLocalTurnFailure(turn,
              "Sign in to check Luna's result. Your question was not sent again."), recoveryUncertain: true }
          : turn));
        return null;
      }
      const fileFailure = pendingFile
        ? error instanceof Error ? error.message : "I couldn't read that file."
        : null;
      const uncertain = error instanceof ApiError && error.code === "TEXT_TURN_RESULT_UNCONFIRMED";
      if (uncertain) forgetPendingOpsTextTurn(browserStorage(), historyScope, id);
      setTurns((current) => current.map((turn) => turn.id === id
        ? { ...presentOpsLocalTurnFailure(
            turn,
            fileFailure
              ? `${fileFailure} Nothing changed or sent.`
              : opsLiveTurnFailureMessage(error)
          ), ...(uncertain ? { recoveryUncertain: true } : {}) }
        : turn));
      return null;
    } finally {
      visibleProgressTimer?.stop();
      // WHY [BOL-TEXT-REPLACEMENT-ACK-002]: the response may finish while
      // exact cancellation is still waiting for its server reply. That local
      // finish cannot release the slot and silently drop the queued changed
      // prompt. Only the replacement controller releases it after ACK.
      // Falsifier: old HTTP terminal precedes cancel ACK and Replace sends 0
      // or 2 new POSTs, or a COMMITTING old final is repainted Stopped.
      if (replacementPendingRef.current !== requestToken) {
        const settled = settleOpsPromptSlot(activePromptRef.current, requestToken);
        if (settled !== activePromptRef.current) {
          activePromptRef.current = settled;
          if (mountedRef.current) setPromptRevision((current) => current + 1);
        }
      }
    }
  };

  const appendLocalAnswer = (prompt: string, response: OpsAssistantResponse) => {
    setText("");
    setSelectedFile(null);
    updateFollowLatest(true);
    setTurns((current) => [...current, {
      id: crypto.randomUUID(),
      prompt,
      response,
      pendingFile: null,
      realtimeFinal: true,
      realtimeCanonical: true,
      cancelled: false
    }]);
    return response;
  };

  const runTextPrompt = async (prompt: string, existingId?: string, pendingFile: File | null = null) => {
    if (hasAuthorizedHistory()) voice.cancelRetainedCanonicalTurn();
    return submitTypedOpsPrompt({
      historyReady: hasAuthorizedHistory,
      voiceSelected: !existingId && pendingFile === null && voice.state.mode === "voice",
      submitRealtimeText: () => voice.submitRealtimeText(prompt),
      selectText: voice.selectText,
      runPrompt: () => runPrompt(prompt, existingId, pendingFile)
    });
  };

  const stopTextPrompt = () => {
    const slot = activePromptRef.current;
    if (!stopOpsPromptSlot(slot) || !slot) return;
    const stopPromise = api.cancelOpsTextTurn(slot.turnId)
      .then((result) => result.outcome)
      .catch(() => "FAILED" as const);
    slot.stopPromise = stopPromise;
    // "Stopped" is terminal only after the authenticated server owner has
    // settled the same turn. If result commit already won, keep the real
    // response instead of replacing it with local cancellation state.
    void stopPromise.then((outcome) => {
      if (activePromptRef.current?.token !== slot.token) return;
      if (acknowledgeOpsPromptStop(slot, outcome)) {
        forgetPendingOpsTextTurn(browserStorage(), historyScope, slot.turnId);
        if (mountedRef.current) setTurns((current) => cancelOpsTextTurn(current, slot.turnId));
      }
      if (mountedRef.current) setPromptRevision((current) => current + 1);
    });
    // Keep the request slot occupied until its exact promise settles so a new
    // Text turn cannot overlap cancellation cleanup.
    setPromptRevision((current) => current + 1);
  };

  const replaceTextPrompt = async (prompt: string): Promise<void> => {
    const slot = activePromptRef.current;
    if (!slot || replacementPendingRef.current !== null || !prompt.trim() || !hasAuthorizedHistory()) return;
    replacementPendingRef.current = slot.token;
    setPromptRevision((current) => current + 1);
    try {
      // WHY [BOL-TEXT-REPLACEMENT-ACK-001]: replacing a long Text request is
      // an authenticated cancellation of its exact owner, not a local fetch
      // abort or a second parallel model. The existing server endpoint waits
      // for RUNNING to unwind, or reports a COMMITTING result that already won.
      // Only after that outcome may the changed prompt dispatch. Falsifier:
      // the new POST races old model/proposal work, a completed old answer
      // is falsely painted Stopped, or old local cleanup drops the new draft.
      if (!slot.stopRequested) {
        stopOpsPromptSlot(slot);
        slot.stopPromise = api.cancelOpsTextTurn(slot.turnId)
          .then((result) => result.outcome, () => "FAILED" as const);
      }
      const outcome = await slot.stopPromise;
      if (activePromptRef.current?.token !== slot.token || !hasAuthorizedHistory()) return;
      if (outcome === "FAILED") {
        if (replacementPendingRef.current === slot.token) replacementPendingRef.current = null;
        acknowledgeOpsPromptStop(slot, outcome);
        return;
      }
      if (outcome === "CANCELLED") {
        setTurns((current) => cancelOpsTextTurn(current, slot.turnId));
      } else {
        const status = await api.opsTurnStatus(slot.turnId);
        if (activePromptRef.current?.token !== slot.token || !hasAuthorizedHistory()) return;
        if (status.turnId !== slot.turnId || status.outcome === "RUNNING") return;
        if (status.outcome === "COMPLETED") {
          const final = turnIdentityId && decodeOpsPublicTurnEnvelope(status.publicTurn, turnIdentityId);
          if (!final || final.turnId !== slot.turnId || final.requestId !== slot.turnId) return;
          const retained = turnsRef.current.filter((turn) => turn.id !== slot.turnId || turn.publicTurn);
          turnsRef.current = retained;
          setTurns(retained);
          if (!await refreshHistoryRef.current() || !hasAuthorizedHistory()) return;
        } else {
          setTurns((current) => current.map((turn) => turn.id === slot.turnId
            ? { ...presentOpsLocalTurnFailure(turn,
                "Luna's previous result could not be confirmed. It was not sent again."), recoveryUncertain: true }
            : turn));
        }
      }
      forgetPendingOpsTextTurn(browserStorage(), historyScope, slot.turnId);
      if (activePromptRef.current?.token === slot.token) {
        activePromptRef.current = null;
        setPromptRevision((current) => current + 1);
      }
      slot.controller.abort();
      if (!hasAuthorizedHistory()) return;
      // WHY [BOL-TEXT-REPLACEMENT-HANDOFF-001]: the old cancellation owner
      // ends at this handoff. Holding its flag until the new answer finishes
      // disabled Stop/Replace on a long replacement. Release only this token
      // before dispatch; an old finally may never clear a newer owner.
      // Falsifier: the new in-flight turn has no Stop, or its next replacement
      // posts before exact cancellation ACK or loses its pending owner.
      if (replacementPendingRef.current === slot.token) replacementPendingRef.current = null;
      setPromptRevision((current) => current + 1);
      void runTextPrompt(prompt);
    } catch {
      // A lost cancellation/status acknowledgement is not authority to start
      // another model. Keep the old slot and the user's replacement draft.
    } finally {
      if (replacementPendingRef.current === slot.token) replacementPendingRef.current = null;
      if (mountedRef.current) setPromptRevision((current) => current + 1);
    }
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (activePromptRef.current) await replaceTextPrompt(text);
    else await runTextPrompt(text, undefined, selectedFile);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    void submit();
  };

  const apply = async (
    turn: Turn & {
      response: Extract<OpsAssistantResponse, { kind: "MUTATION_PREVIEW" }>;
      publicTurn: OpsPublicTurnEnvelope;
    },
    file: File | null,
    commandOverride?: OpsAssistantMutationCommand
  ) => {
    if (turn.historyVerified) return;
    const command = commandOverride ?? turn.response.preview.command;
    const action = command.action;
    const isWhatsAppAction = action === "DRAFT_WHATSAPP" || action === "SCHEDULE_WHATSAPP";
    const isWhatsAppRouteConfiguration = action === "CONFIGURE_WHATSAPP_ROUTE";
    const isScheduled = action === "SCHEDULE_WHATSAPP";
    const changing = workMutationFeedback(command, turn.id, "CHANGING");
    if (changing) onWorkMutationFeedback?.(changing);
    setApplying(turn.id);
    try {
      const applied = await applyOpsMutationWithOptionalFile(
        command,
        file,
        turn.id,
        turn.publicTurn,
        api
      );
      const accepted = acceptedWorkMutationFeedback(command, turn.id, applied.workItemId);
      if (accepted) onWorkMutationFeedback?.(accepted);
      const installApplied = (item: Turn): Turn => {
        if (item.id !== turn.id) return item;
        if (item.publicTurn) {
          return applied.publicTurn
            ? acceptOpsPublicTurnRevision(item, applied.publicTurn) ?? item
            : item;
        }
        return {
          ...item,
          response: {
          kind: "ANSWER",
          text: applied.text,
          evidenceLabel: isWhatsAppAction || isWhatsAppRouteConfiguration ? "WhatsApp route" : "Current board",
          workItemIds: applied.workItemId ? [applied.workItemId] : [],
          ...(applied.proposalId ? { proposalIds: [applied.proposalId] } : {}),
          operatingModelVersion: turn.response.operatingModelVersion
          }
        };
      };
      turnsRef.current = turnsRef.current.map(installApplied);
      setTurns((current) => current.map(installApplied));
      if (turn.publicTurn && applied.proposalId) {
        void waitForProposalOutcome(applied.proposalId)
          .then(() => settlePublicTurn(turn.id));
      }
      try {
        await afterBoardChange();
      } catch {
        if (accepted && accepted.stage !== "FAILED") {
          onWorkMutationFeedback?.({
            ...accepted,
            stage: "FAILED",
            failure: "The change was accepted, but canonical Tasks could not refresh."
          });
        }
        notify("The change was applied, but Tasks could not refresh yet.", "error");
        return;
      }
      if (command.action === "CREATE" || ["CREATE", "DEADLINE", "WORK_SCHEDULE", "SCHEDULE_WHATSAPP"].includes(action)) {
        openWorkspace?.({ surface: "CALENDAR", behavior: "OPEN", focusDay: null, workLens: null });
      } else if (action === "TASK_RANGE") {
        openWorkspace?.({ surface: "BOARD", behavior: "OPEN", focusDay: null, workLens: "TIMELINE" });
      } else if (["STATUS", "ASSIGNMENT", "NOTE"].includes(action)) {
        openWorkspace?.({ surface: "BOARD", behavior: "OPEN", focusDay: null, workLens: "BOARD" });
      }
      notify(isScheduled
        ? "Scheduled. It has not been sent."
        : isWhatsAppAction
          ? "Approved. Final safety checks are running; nothing is marked sent yet."
          : isWhatsAppRouteConfiguration
            ? "WhatsApp access updated. Nothing was sent."
          : "Change applied. Undo is in Activity.");
    } catch (error) {
      const failed = workMutationFeedback(command, turn.id, "FAILED");
      if (failed) onWorkMutationFeedback?.(failed);
      if (error instanceof ApiError && error.status === 401) return;
      const reason = error instanceof Error ? error.message : "That action could not be approved.";
      const fallback: OpsAssistantResponse = {
        kind: "CLARIFICATION",
        text: isWhatsAppAction
          ? `${isScheduled ? "Nothing was scheduled." : "Nothing was sent."} ${reason}`
          : isWhatsAppRouteConfiguration
            ? `WhatsApp access was not changed. ${reason}`
          : "That task changed before approval. Ask again to use the current board.",
        operatingModelVersion: turn.response.operatingModelVersion
      };
      setTurns((current) => current.map((item) => presentOpsApplyFailure(item, turn.id, fallback)));
      notify(error instanceof Error ? error.message : "That change was not applied.", "error");
      await afterBoardChange();
    } finally {
      setApplying(null);
    }
  };

  const cancel = (turn: Turn) => {
    if (turn.historyVerified) return;
    const publicTurn = opsVisiblePublicTurn(turn, turnIdentityId);
    const response = (publicTurn?.result.response as OpsAssistantResponse | undefined) ?? turn.response;
    if (response) {
      const targeted = targetedWorkMutationFeedback(response, turn.id);
      if (targeted) onWorkMutationFeedback?.(clearWorkMutationFeedback(turn.id));
    }
    if (publicTurn) {
      void api.rejectOpsPublicTurn({
        turnId: publicTurn.turnId,
        requestId: publicTurn.requestId,
        revision: publicTurn.revision,
        releaseId: publicTurn.releaseId,
        resultHash: publicTurn.resultHash
      }).then((next) => {
        const install = (item: Turn): Turn => item.id === turn.id
          ? acceptOpsPublicTurnRevision(item, next) ?? item
          : item;
        turnsRef.current = turnsRef.current.map(install);
        setTurns((current) => current.map(install));
      }).catch((error) => {
        if (!(error instanceof ApiError && error.status === 401)) {
          notify("That decision could not be recorded. The original Luna result is still visible.", "error");
        }
      });
      return;
    }
    setTurns((current) => current.map((item) => item.id === turn.id ? {
      ...item,
      response: {
        kind: "CLARIFICATION",
        text: "Rejected. Nothing changed or sent.",
        operatingModelVersion: item.response?.operatingModelVersion ?? 0
      }
    } : item));
  };

  const upscaleImage = async (turnId: string, artifactId: string, factor: 2 | 4) => {
    if (upscaling || turnsRef.current.find((turn) => turn.id === turnId)?.historyVerified) return;
    const retryIdentity = `${turnId}:${artifactId}:${factor}`;
    const requestKey = upscaleRequestKeysRef.current.get(retryIdentity)
      ?? `${turnId}-upscale-${factor}-${crypto.randomUUID()}`;
    upscaleRequestKeysRef.current.set(retryIdentity, requestKey);
    setUpscaling({ turnId, factor });
    setTurns((current) => current.map((turn) => turn.id === turnId
      ? { ...turn, imageNotice: undefined }
      : turn));
    try {
      const job = await api.upscaleOpsImage(
        artifactId,
        factor,
        requestKey
      );
      if (job.state === "SUCCEEDED" || job.state === "FAILED") {
        upscaleRequestKeysRef.current.delete(retryIdentity);
      }
      setTurns((current) => current.map((turn) => {
        if (turn.id !== turnId) return turn;
        if (job.state !== "SUCCEEDED") {
          return { ...turn, imageNotice: { text: job.message, tone: "error" } };
        }
        return {
          ...turn,
          imageNotice: undefined,
          response: {
            kind: "IMAGE_JOB",
            text: job.message,
            job,
            operatingModelVersion: turn.response?.operatingModelVersion ?? 0
          }
        };
      }));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) return;
      const message = error instanceof Error ? error.message : "Upscaling did not finish.";
      setTurns((current) => current.map((turn) => turn.id === turnId
        ? { ...turn, imageNotice: { text: message, tone: "error" } }
        : turn));
    } finally {
      setUpscaling(null);
    }
  };

  // Seven-day continuity is the same collection Luna and the operator see.
  const visibleTurns = identityTurns;
  const voiceChatDraft = historyReady
    ? opsVoiceChatDraftPresentation(voice.state, visibleTurns)
    : null;
  const showWelcome = visibleTurns.length === 0 && voiceChatDraft === null;

  // WHY [BOL-CHAT-HISTORY-PENDING-STATE-001]: until the authenticated history
  // has hydrated, the thread is neither empty nor populated, so the section
  // carries `ops-assistant--history-pending`. CSS keeps the welcome hero and
  // the phone summary sentence hidden in that state instead of painting the
  // empty-chat layout and then swapping it once turns arrive (the load flash
  // the human reported on 2026-09-15). Falsifier: a fresh phone load shows
  // the two-line sentence before it collapses, or the hero flashes.
  return (
    <section className={`ops-assistant ${embedded ? "ops-assistant--embedded" : ""} ${workspaceSurface ? "ops-assistant--workspace-open" : ""} ${historyReady ? "" : "ops-assistant--history-pending"}`} aria-label={embedded ? "Ops Agent chat" : undefined} aria-labelledby={embedded ? undefined : "ops-assistant-title"}>
      {!embedded ? <header className="ops-assistant__header">
        <span aria-hidden="true"><MessageSquareText size={20} /></span>
        <div><h2 id="ops-assistant-title">Ops Agent</h2><p>Ask about any task or WhatsApp conversation.</p></div>
      </header> : null}

      {renderedWorkspaceSurface ? <div className="ops-assistant__workspace">{renderedWorkspaceSurface}</div> : null}

      {/* WHY [BOL-TODAY-PINNED-001]: entering Today must explain the day before
          old conversation history. Keeping this read-only summary outside the
          scroll owner leaves it visible while the newest chat and approvals
          still settle directly above the composer. Falsifier: Today opens on
          an old turn with the daily summary off-screen, or the header covers
          the composer/workspace tools on a 360px phone. */}
      {renderedConversationHeader ? <div className="ops-conversation-header">{renderedConversationHeader}</div> : null}

      <div className="ops-thread-frame">
        <div
          className="ops-thread"
          ref={threadRef}
          role="log"
          aria-live="polite"
          aria-label="Ops Agent conversation"
          onScroll={(event) => {
            updateFollowLatest(opsThreadFollowLatestAfterScroll(
              event.currentTarget,
              previousThreadScrollTopRef.current,
              followLatestThreadRef.current
            ));
            previousThreadScrollTopRef.current = event.currentTarget.scrollTop;
          }}
          onWheel={(event) => {
            cancelPendingThreadSettle();
            pendingEvidenceThreadReturnRef.current = null;
            if (!shouldReleaseOpsThreadAutoFollow({ kind: "wheel", deltaY: event.deltaY })) return;
            updateFollowLatest(false);
          }}
          onTouchStart={(event) => {
            threadGestureActiveRef.current = true;
            threadTouchYRef.current = event.touches[0]?.clientY ?? null;
            cancelPendingThreadSettle();
            pendingEvidenceThreadReturnRef.current = null;
          }}
          onTouchMove={(event) => {
            const currentY = event.touches[0]?.clientY;
            const previousY = threadTouchYRef.current;
            if (currentY === undefined || previousY === null) return;
            if (shouldReleaseOpsThreadAutoFollow({ kind: "touch", previousY, currentY })) {
              updateFollowLatest(false);
            }
            threadTouchYRef.current = currentY;
          }}
          onTouchEnd={() => {
            threadGestureActiveRef.current = false;
            threadTouchYRef.current = null;
          }}
          onTouchCancel={() => {
            threadGestureActiveRef.current = false;
            threadTouchYRef.current = null;
          }}
        >
          {beforeConversation}
          {/* WHY [BOL-CLEAR-CHAT-001]: first child of the thread so it has room above the
              welcome note on phones as well as on the desktop. */}
          {(historyCursor || (feedClearedAt && !earlierShown)) && historyReady ? <div className="ops-thread__feed-tools ops-thread__feed-tools--top"><button type="button" className="quiet-review-action" disabled={earlierLoading} onClick={() => void showEarlierTurns()}>Show earlier messages</button></div> : null}
          {showWelcome ? <OpsWelcome
            ask={(prompt) => void runTextPrompt(prompt)}
            showTour={() => {
              // WHY [BOL-OPS-INSTANT-TOUR-001]: this button is a typed UI action,
              // not an arbitrary business question. Sending it through the model
              // caused a needless WhatsApp scan and proxy timeout instead of an
              // instant guide. Free-form chat still uses the full Agent. Falsifier:
              // tapping Show me around makes any API/model request or shows Retry.
              appendLocalAnswer("Show me around", opsWelcomeTourResponse());
            }}
          /> : null}
          {visibleTurns.map((turn) => {
            const publicTurn = opsVisibleHistoryTurn(turn, turnIdentityId);
            const proposalIds = proposalRenderIdsForTurn(turn, proposalOwners, proposalRenderGroups);
            const publicProposals = publicProposalProjectionsForTurn(
              turn,
              turnIdentityId,
              currentReviewProposalIds
            );
            return <OpsTurnRenderBoundary
              key={`${historyScope}:${publicTurn?.turnId ?? turn.id}:${publicTurn?.revision ?? 0}`}
              resetKey={`${publicTurn?.resultHash ?? turn.id}:${turn.response?.kind ?? "pending"}`}
              fallback={publicTurn ? <OpsPublicTurnFallback
                turn={turn}
                publicTurn={publicTurn}
                {...(openConversations ? { openConversations } : {})}
              /> : null}
            >
              <OpsTurn
                  turn={turn}
                  applying={applying === turn.id}
                  apply={apply}
                  cancel={cancel}
                  // Retry is a new authenticated turn with a fresh request ID.
                  // Reusing the terminal row's ID would re-execute stale
                  // failed/cancelled authority instead of asking again.
                  retry={() => void runTextPrompt(turn.prompt, undefined, turn.pendingFile ?? null)}
                  openWork={openWork}
                  workTitle={workTitle}
                  unmaterializedWorkProposalIds={unmaterializedWorkProposalIds}
                  currentWorkItemIds={currentWorkItemIds}
                  {...(openWorkspace ? { openWorkspace } : {})}
                  {...(workspaceReferenceSummary ? { workspaceReferenceSummary } : {})}
                  renderProposal={renderProposal
                    ? (id) => renderProposal(id, () => settlePublicTurn(turn.id), publicProposals)
                    : undefined}
                  proposalIds={proposalIds}
                  openConversations={openConversations}
                  upscaling={upscaling?.turnId === turn.id ? upscaling.factor : null}
                  upscale={(artifactId, factor) => void upscaleImage(turn.id, artifactId, factor)}
                />
            </OpsTurnRenderBoundary>;
          })}
          {voiceChatDraft ? <OpsVoiceChatDraft draft={voiceChatDraft} /> : null}
          {typeof afterConversation === "function" ? afterConversation(renderedProposalIds) : afterConversation}
          {visibleTurns.length > 0 && historyReady ? <div className="ops-thread__feed-tools" data-ops-clear-chat={clearPending ? "confirm" : "idle"}>
            {clearPending
              ? <><span>Start a fresh chat? Earlier messages stay in history.</span><button type="button" className="secondary-button" onClick={() => void clearChat()}>Clear chat</button><button type="button" className="quiet-review-action" onClick={() => setClearPending(false)}>Keep</button></>
              : <button type="button" className="quiet-review-action" onClick={() => setClearPending(true)}>Clear chat</button>}
          </div> : null}
        </div>
        {!followingLatest ? <button
          className="ops-latest"
          type="button"
          aria-label="Jump to latest message"
          onClick={restoreLatestThread}
        ><ArrowDown size={16} aria-hidden="true" /><span>Latest</span></button> : null}
      </div>

      <form className="ops-composer" onSubmit={(event) => void submit(event)}>
        <OpsVoiceControl
          voice={voice}
          state={historyReady ? voice.state : {
            ...voice.state,
            status: historyError ?? "Loading conversation…",
            error: historyError
          }}
          busy={busy || !historyReady}
          workActive={workActive}
          meetingActive={activeMediaOwner.owner === "meeting"}
        />
        <label className="sr-only" htmlFor="ops-question">Message Ops Agent</label>
        <div className="ops-composer__row">
          <details className="ops-attachment-menu">
            <summary aria-label="Attach a file or record a meeting"><Paperclip size={19} aria-hidden="true" /></summary>
            <div className="ops-attachment-menu__panel">
              <label className="ops-file-picker"><Paperclip size={17} aria-hidden="true" /><span>Choose file</span><input type="file" accept={OPS_CHAT_FILE_ACCEPT} disabled={busy} onChange={(event) => {
                const file = event.currentTarget.files?.[0] ?? null;
                const error = file ? validateOpsChatFileSelection(file) : null;
                if (error) {
                  setSelectedFile(null);
                  notify(error, "error");
                } else {
                  setSelectedFile(file);
                }
                event.currentTarget.value = "";
              }} /></label>
              {attachmentTools}
            </div>
          </details>
          <textarea
            id="ops-question"
            rows={2}
            maxLength={MAX_OWNER_MESSAGE_CHARS}
            value={text}
            disabled={busy && replacementPendingRef.current !== null}
            onChange={(event) => setText(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Message Ops Agent"
            aria-describedby={composerControls || !embedded ? "ops-composer-help" : undefined}
          />
          {busy ? <><button
            className="primary-button ops-send-button ops-stop-button"
            type="button"
            disabled={activePrompt?.stopRequested === true || replacementPendingRef.current !== null}
            aria-label={activePrompt?.stopRequested ? "Stopping message" : "Stop message"}
            onClick={stopTextPrompt}
          >
            {activePrompt?.stopRequested
              ? <LoaderCircle className="spin" size={18} aria-hidden="true" />
              : <Square size={16} fill="currentColor" aria-hidden="true" />}
            <span>{activePrompt?.stopRequested ? "Stopping" : "Stop"}</span>
          </button>{text.trim() ? <button className="primary-button ops-send-button" type="submit"
            disabled={!historyReady || replacementPendingRef.current !== null} aria-label="Replace message">
            <ArrowUp size={18} aria-hidden="true" /><span>Replace</span>
          </button> : null}</> : <button className="primary-button ops-send-button" type="submit" disabled={!historyReady || !text.trim()} aria-label="Send message">
            <ArrowUp size={18} aria-hidden="true" />
            <span>Send</span>
          </button>}
        </div>
        {selectedFile ? <div className="ops-selected-file"><span><Paperclip size={14} aria-hidden="true" /><strong>{selectedFile.name}</strong><small>{formatFileSize(selectedFile.size)}</small></span><button type="button" onClick={() => setSelectedFile(null)} aria-label={`Remove ${selectedFile.name}`}><X size={15} aria-hidden="true" /></button></div> : null}
        {composerControls || !embedded ? <div className="ops-composer__footer">{composerControls}<small id="ops-composer-help"><ShieldCheck size={14} aria-hidden="true" />Manual approval on</small></div> : null}
      </form>
    </section>
  );
  } finally { finishTiming?.(); }
}

function OpsWelcome({ ask, showTour }: { ask: (prompt: string) => void; showTour: () => void }) {
  return (
    <div className="ops-welcome">
      <strong>Hi, I’m your Ops Agent.</strong>
      {/* WHY [BOL-WELCOME-CAPABILITIES-803]: the welcome omitted real document,
          PDF and image work. Keep this approved, capability-grounded overview
          and the business-change/outgoing-message approval boundary; no future
          integrations or autonomous business effects are promised here. */}
      <p>I can summarize WhatsApp and files with proof, organize tasks and your calendar, prepare follow-ups, draft and edit documents, create and edit PDFs and images, and search the web.</p>
      <p>You approve business changes and outgoing messages.</p>
      <div className="ops-suggestions" aria-label="Suggested questions">
        <button type="button" onClick={() => ask("What needs attention?")}>What needs attention?</button>
        <button type="button" onClick={showTour}>Show me around</button>
      </div>
    </div>
  );
}

function OpsContextScan({ oneShot = false }: { oneShot?: boolean }) {
  return (
    <span className={oneShot ? "ops-context-scan ops-context-scan--one-shot" : "ops-context-scan"} aria-hidden="true">
      {(["rear", "middle", "front"] as const).map((cloud) => (
        <span className={`ops-context-scan__cloud ops-context-scan__cloud--${cloud}`} key={cloud}>
          {Array.from({ length: 40 }, (_, particle) => (
            <i className="ops-context-scan__particle" key={`${cloud}-${particle}`} />
          ))}
        </span>
      ))}
    </span>
  );
}

class OpsTurnRenderBoundary extends Component<{
  resetKey: string;
  fallback: ReactNode;
  children: ReactNode;
}, { failed: boolean; resetKey: string }> {
  state = { failed: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(): { failed: true } {
    return { failed: true };
  }

  static getDerivedStateFromProps(
    props: { resetKey: string },
    state: { failed: boolean; resetKey: string }
  ): { failed: false; resetKey: string } | null {
    return props.resetKey === state.resetKey ? null : { failed: false, resetKey: props.resetKey };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo): void {
    // The immutable result below is already the history owner. The boundary
    // contains only this turn's optional presentation failure.
  }

  render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

type OpsAnswerResponse = Extract<OpsAssistantResponse, { kind: "ANSWER" }>;
type OpsEvidenceClaim = NonNullable<OpsAnswerResponse["evidenceClaims"]>[number];
type OpsClaimEvidenceSource = OpsEvidenceClaim["evidenceSources"][number];
type OpsEvidenceContactAction = NonNullable<OpsClaimEvidenceSource["contact"]>["actions"][number];

export function opsEvidenceContactHref(action: OpsEvidenceContactAction, phoneE164: string): string | null {
  if (!/^\+[1-9][0-9]{7,14}$/u.test(phoneE164)) return null;
  return action === "CALL" ? `tel:${phoneE164}` : `https://wa.me/${phoneE164.slice(1)}`;
}

export function shouldDismissOpsEvidenceBackdrop(target: EventTarget | null, currentTarget: EventTarget | null): boolean {
  return target !== null && target === currentTarget;
}

interface OpsEvidenceAnchorHistory {
  replaceState(data: unknown, unused: string): void;
}

const OPS_EVIDENCE_PROOF_HISTORY_SCHEMA = "ops.evidence-proof-history.v1";
const OPS_EVIDENCE_THREAD_RETURN_SCHEMA = "ops.evidence-thread-return.v1";
const OPS_EVIDENCE_PROOF_TURN_PARAM = "opsProofTurn";
const OPS_EVIDENCE_PROOF_CLAIM_PARAM = "opsProofClaim";
const OPS_EVIDENCE_PROOF_SOURCE_PARAM = "opsProofSource";
const OPS_EVIDENCE_PROOF_START_PARAM = "opsProofStart";
const OPS_EVIDENCE_PROOF_END_PARAM = "opsProofEnd";
const OPS_EVIDENCE_PROOF_EXCERPT_PARAM = "opsProofExcerpt";
// The canonical visual digest distinguishes pages/regions of one source on
// Verify, reload and Back, without putting pixels or captions into navigation.
const OPS_EVIDENCE_PROOF_VISUAL_PARAM = "opsProofVisual";
const OPS_EVIDENCE_PROOF_REACTION_PARAM = "opsProofReaction";

export interface OpsEvidenceVerificationRoute {
  turnId: string;
  claimIndex: number;
  sourceEventId: string;
  highlightStart?: number;
  highlightEnd?: number;
  excerptHash?: string;
  visualHash?: string;
  reactionHash?: string;
}

export interface OpsEvidenceThreadReturnReceipt {
  schema: typeof OPS_EVIDENCE_THREAD_RETURN_SCHEMA;
  turnId: string;
  scrollTop: number;
}

interface OpsEvidenceThreadScrollTarget extends OpsThreadScrollBox {
  querySelectorAll(selectors: string): ArrayLike<Pick<Element, "getAttribute">>;
}

interface OpsEvidencePopStateTarget {
  history: Pick<History, "state">;
  addEventListener(type: "popstate", listener: EventListener): void;
  removeEventListener(type: "popstate", listener: EventListener): void;
}

interface OpsEvidenceVerificationTarget {
  location: Pick<Location, "href">;
  history: {
    readonly state: unknown;
    pushState(data: unknown, unused: string, url?: string | URL | null): void;
    replaceState(data: unknown, unused: string, url?: string | URL | null): void;
    back(): void;
  };
}

function browserOpsEvidenceVerificationTarget(): OpsEvidenceVerificationTarget | null {
  return typeof window === "undefined" ? null : window;
}

function opsEvidenceHistoryRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? { ...(value as Record<string, unknown>) }
    : {};
}

function opsEvidenceVerificationPath(url: URL): string {
  return `${url.pathname}${url.search}${url.hash}`;
}

export function opsEvidenceVerificationFromUrl(value: string): OpsEvidenceVerificationRoute | null {
  try {
    const url = new URL(value, "https://business-ops.local");
    const turnId = url.searchParams.get(OPS_EVIDENCE_PROOF_TURN_PARAM)?.trim() ?? "";
    const sourceEventId = url.searchParams.get(OPS_EVIDENCE_PROOF_SOURCE_PARAM)?.trim() ?? "";
    const claimValue = url.searchParams.get(OPS_EVIDENCE_PROOF_CLAIM_PARAM) ?? "";
    const startValue = url.searchParams.get(OPS_EVIDENCE_PROOF_START_PARAM);
    const endValue = url.searchParams.get(OPS_EVIDENCE_PROOF_END_PARAM);
    const excerptHash = url.searchParams.get(OPS_EVIDENCE_PROOF_EXCERPT_PARAM);
    const visualHash = url.searchParams.get(OPS_EVIDENCE_PROOF_VISUAL_PARAM);
    const reactionHash = url.searchParams.get(OPS_EVIDENCE_PROOF_REACTION_PARAM);
    if (excerptHash !== null && !/^sha256:[a-f0-9]{64}$/u.test(excerptHash)) return null;
    if (!turnId || turnId.length > 256 || !sourceEventId || sourceEventId.length > 256 || !/^(0|[1-9]\d*)$/u.test(claimValue)) return null;
    const claimIndex = Number(claimValue);
    if (!Number.isSafeInteger(claimIndex) || (startValue === null) !== (endValue === null)) return null;
    if (visualHash !== null && reactionHash !== null) return null;
    if (reactionHash !== null) {
      return /^sha256:[a-f0-9]{64}$/u.test(reactionHash)
        && startValue === null && endValue === null && excerptHash === null
        ? { turnId, claimIndex, sourceEventId, reactionHash } : null;
    }
    if (visualHash !== null) {
      return /^sha256:[a-f0-9]{64}$/u.test(visualHash)
        && startValue === null && endValue === null && excerptHash === null
        ? { turnId, claimIndex, sourceEventId, visualHash } : null;
    }
    if (startValue === null || endValue === null) return { turnId, claimIndex, sourceEventId };
    if (!/^(0|[1-9]\d*)$/u.test(startValue) || !/^(0|[1-9]\d*)$/u.test(endValue)) return null;
    const highlightStart = Number(startValue);
    const highlightEnd = Number(endValue);
    return Number.isSafeInteger(highlightStart)
      && Number.isSafeInteger(highlightEnd)
      && highlightEnd > highlightStart
      ? { turnId, claimIndex, sourceEventId, highlightStart, highlightEnd,
          ...(excerptHash ? { excerptHash } : {}) }
      : null;
  } catch {
    return null;
  }
}

function opsEvidenceRouteHasExactRange(route: OpsEvidenceVerificationRoute): route is OpsEvidenceVerificationRoute & {
  highlightStart: number;
  highlightEnd: number;
} {
  return route.highlightStart !== undefined && route.highlightEnd !== undefined;
}

export function opsEvidenceOccurrenceKey(
  source: Pick<OpsClaimEvidenceSource, "sourceEventId" | "excerpt" | "highlightStart" | "highlightEnd" | "visual" | "reaction">
): string {
  if (source.visual) return `${source.sourceEventId}:${opsVisualProofKey(source.visual)}`;
  if (source.reaction) return `${source.sourceEventId}:reaction:${opsSha256(source.reaction)}`;
  // Public crops use crop-local offsets. Two distant passages can have equal
  // offsets after cropping, so the already-used excerpt identity is essential.
  return `${source.sourceEventId}:${opsSha256Utf8(source.excerpt)}:${source.highlightStart}:${source.highlightEnd}`;
}

function opsEvidenceVisibleSourceKey(source: OpsClaimEvidenceSource): string {
  return source.visual
    ? `visual:${opsEvidenceOccurrenceKey(source)}`
    : source.reaction
    ? `reaction:${opsEvidenceOccurrenceKey(source)}`
    : `text:${source.sourceEventId}:${opsSha256Utf8(source.excerpt)}`;
}

// WHY [BOL-OPS-SAME-MESSAGE-PROOF-GROUP-001]: the real Recruitment claim
// selected three exact ranges from one immutable message. Rendering one button
// per range made the operator reopen the same message three times to judge one
// claim. Group only identical textual source bytes within that claim for the
// visible control/view; retain every occurrence and exact route underneath.
// UUID-only grouping would conflate separately cropped source bytes, while
// grouping visuals would conflate distinct pages/regions. Falsifier: one
// identical message still renders several controls, any selected range loses
// its mark, distinct UUIDs/crops/visuals collapse, or an old exact occurrence
// URL no longer resolves through reload and Back.
export function opsEvidenceControlGroups(
  sources: readonly OpsClaimEvidenceSource[]
): OpsClaimEvidenceSource[][] {
  const groups = new Map<string, OpsClaimEvidenceSource[]>();
  for (const source of sources) {
    const key = opsEvidenceVisibleSourceKey(source);
    const group = groups.get(key);
    if (group) group.push(source);
    else groups.set(key, [source]);
  }
  return [...groups.values()];
}

function opsEvidenceSourcesForProofView(
  claim: Pick<OpsEvidenceClaim, "evidenceSources">,
  selectedSource: OpsClaimEvidenceSource
): OpsClaimEvidenceSource[] {
  const selectedOccurrence = opsEvidenceOccurrenceKey(selectedSource);
  const group = opsEvidenceControlGroups(claim.evidenceSources)
    .find((sources) => sources.some((source) => opsEvidenceOccurrenceKey(source) === selectedOccurrence));
  return group ?? [selectedSource];
}

export function opsEvidenceSourceForVerificationRoute(
  claim: Pick<OpsEvidenceClaim, "evidenceSources">,
  route: OpsEvidenceVerificationRoute
): OpsClaimEvidenceSource | null {
  const sameSource = claim.evidenceSources.filter((source) => source.sourceEventId === route.sourceEventId);
  const matches = route.reactionHash
    ? sameSource.filter((source) => source.reaction && opsSha256(source.reaction) === route.reactionHash)
    : route.visualHash
    ? sameSource.filter((source) => source.visual && opsVisualProofKey(source.visual) === route.visualHash)
    : opsEvidenceRouteHasExactRange(route)
    ? sameSource.filter((source) => source.highlightStart === route.highlightStart
      && source.highlightEnd === route.highlightEnd
      && (route.excerptHash === undefined || opsSha256Utf8(source.excerpt) === route.excerptHash))
    : sameSource;
  // Legacy links carried only the source UUID. They remain valid only when that
  // UUID names one occurrence in the claim; guessing between two ranges would
  // open proof the operator did not choose.
  return matches.length === 1 ? matches[0]! : null;
}

function opsEvidenceProofHistoryMatchesRoute(
  marker: Record<string, unknown>,
  route: OpsEvidenceVerificationRoute
): boolean {
  if (marker.schema !== OPS_EVIDENCE_PROOF_HISTORY_SCHEMA
    || marker.turnId !== route.turnId
    || marker.claimIndex !== route.claimIndex
    || marker.sourceEventId !== route.sourceEventId) return false;
  if (route.visualHash) return marker.visualHash === route.visualHash
    && marker.reactionHash === undefined
    && marker.highlightStart === undefined && marker.highlightEnd === undefined;
  if (route.reactionHash) return marker.reactionHash === route.reactionHash
    && marker.visualHash === undefined
    && marker.highlightStart === undefined && marker.highlightEnd === undefined;
  if (marker.visualHash !== undefined || marker.reactionHash !== undefined) return false;
  return opsEvidenceRouteHasExactRange(route)
    ? marker.highlightStart === route.highlightStart && marker.highlightEnd === route.highlightEnd
      && (route.excerptHash === undefined || marker.excerptHash === route.excerptHash)
    : marker.highlightStart === undefined && marker.highlightEnd === undefined;
}

export function opsEvidenceThreadReturnReceipt(turnId: string, scrollTop: number): OpsEvidenceThreadReturnReceipt | null {
  if (!turnId || turnId.length > 256 || !Number.isFinite(scrollTop) || scrollTop < 0) return null;
  return { schema: OPS_EVIDENCE_THREAD_RETURN_SCHEMA, turnId, scrollTop };
}

export function captureOpsSourceReturn(
  turnId: string,
  scrollTop: number,
  target: OpsEvidenceVerificationTarget | null = browserOpsEvidenceVerificationTarget()
): OpsEvidenceThreadReturnReceipt | null {
  const receipt = opsEvidenceThreadReturnReceipt(turnId, scrollTop);
  if (!target || !receipt) return null;
  // WHY: putting the receipt only on the new source entry fixes the visible
  // Back button but loses the original turn/scroll on browser Back. Capture
  // both sides of the transition, as Verify already does, without inventing
  // a claim/proof for an unread source. No message content enters history.
  target.history.replaceState({ ...opsEvidenceHistoryRecord(target.history.state),
    businessOpsEvidenceReturn: receipt }, "", target.location.href);
  return receipt;
}

export function opsEvidenceThreadReturnFromHistory(value: unknown): OpsEvidenceThreadReturnReceipt | null {
  const candidate = opsEvidenceHistoryRecord(value).businessOpsEvidenceReturn;
  if (candidate === null || typeof candidate !== "object" || Array.isArray(candidate)) return null;
  const receipt = candidate as Record<string, unknown>;
  return receipt.schema === OPS_EVIDENCE_THREAD_RETURN_SCHEMA
    && typeof receipt.turnId === "string"
    && receipt.turnId.length > 0
    && receipt.turnId.length <= 256
    && typeof receipt.scrollTop === "number"
    && Number.isFinite(receipt.scrollTop)
    && receipt.scrollTop >= 0
    ? { schema: OPS_EVIDENCE_THREAD_RETURN_SCHEMA, turnId: receipt.turnId, scrollTop: receipt.scrollTop }
    : null;
}

export function restoreOpsEvidenceThreadScroll(
  thread: OpsEvidenceThreadScrollTarget,
  receipt: OpsEvidenceThreadReturnReceipt
): boolean {
  const ownsTurn = Array.from(thread.querySelectorAll("[data-ops-turn-id]"))
    .some((turn) => turn.getAttribute("data-ops-turn-id") === receipt.turnId);
  if (!ownsTurn) return false;
  thread.scrollTop = Math.min(receipt.scrollTop, Math.max(0, thread.scrollHeight - thread.clientHeight));
  return true;
}

export function settleOpsThreadAfterEvidenceRestore(
  thread: OpsEvidenceThreadScrollTarget,
  followLatest: boolean,
  receipt: OpsEvidenceThreadReturnReceipt | null
): "RESTORED" | "LATEST" | "UNCHANGED" {
  if (receipt) return restoreOpsEvidenceThreadScroll(thread, receipt) ? "RESTORED" : "UNCHANGED";
  return settleOpsThreadToLatest(thread, followLatest) ? "LATEST" : "UNCHANGED";
}

export function opsEvidenceThreadRestoreDecision(
  outcome: "RESTORED" | "LATEST" | "UNCHANGED",
  historySettled: boolean
): "RESTORED" | "WAIT_FOR_HISTORY" | "FALLBACK_LATEST" {
  if (outcome === "RESTORED") return "RESTORED";
  return historySettled ? "FALLBACK_LATEST" : "WAIT_FOR_HISTORY";
}

export function listenOpsEvidenceThreadRestore(
  target: OpsEvidencePopStateTarget,
  restore: (receipt: OpsEvidenceThreadReturnReceipt) => void
): () => void {
  const onPopState: EventListener = () => {
    const receipt = opsEvidenceThreadReturnFromHistory(target.history.state);
    if (receipt) restore(receipt);
  };
  target.addEventListener("popstate", onPopState);
  return () => target.removeEventListener("popstate", onPopState);
}

export function pushOpsEvidenceVerification(
  turnId: string,
  claimIndex: number,
  source: OpsClaimEvidenceSource,
  returnReceipt: OpsEvidenceThreadReturnReceipt | null = null,
  target: OpsEvidenceVerificationTarget | null = browserOpsEvidenceVerificationTarget()
): boolean {
  if (!target || !turnId || !Number.isSafeInteger(claimIndex) || claimIndex < 0) return false;
  try {
    const url = new URL(target.location.href);
    url.searchParams.set(OPS_EVIDENCE_PROOF_TURN_PARAM, turnId);
    url.searchParams.set(OPS_EVIDENCE_PROOF_CLAIM_PARAM, String(claimIndex));
    url.searchParams.set(OPS_EVIDENCE_PROOF_SOURCE_PARAM, source.sourceEventId);
    const occurrence = source.visual ? { visualHash: opsVisualProofKey(source.visual) }
      : source.reaction ? { reactionHash: opsSha256(source.reaction) } : {
      highlightStart: source.highlightStart, highlightEnd: source.highlightEnd,
      excerptHash: opsSha256Utf8(source.excerpt)
    };
    if (source.visual) {
      url.searchParams.delete(OPS_EVIDENCE_PROOF_START_PARAM);
      url.searchParams.delete(OPS_EVIDENCE_PROOF_END_PARAM);
      url.searchParams.delete(OPS_EVIDENCE_PROOF_EXCERPT_PARAM);
      url.searchParams.set(OPS_EVIDENCE_PROOF_VISUAL_PARAM, opsVisualProofKey(source.visual));
      url.searchParams.delete(OPS_EVIDENCE_PROOF_REACTION_PARAM);
    } else if (source.reaction) {
      url.searchParams.delete(OPS_EVIDENCE_PROOF_START_PARAM);
      url.searchParams.delete(OPS_EVIDENCE_PROOF_END_PARAM);
      url.searchParams.delete(OPS_EVIDENCE_PROOF_EXCERPT_PARAM);
      url.searchParams.delete(OPS_EVIDENCE_PROOF_VISUAL_PARAM);
      url.searchParams.set(OPS_EVIDENCE_PROOF_REACTION_PARAM, opsSha256(source.reaction));
    } else {
      url.searchParams.delete(OPS_EVIDENCE_PROOF_VISUAL_PARAM);
      url.searchParams.delete(OPS_EVIDENCE_PROOF_REACTION_PARAM);
      url.searchParams.set(OPS_EVIDENCE_PROOF_START_PARAM, String(source.highlightStart));
      url.searchParams.set(OPS_EVIDENCE_PROOF_END_PARAM, String(source.highlightEnd));
      url.searchParams.set(OPS_EVIDENCE_PROOF_EXCERPT_PARAM, opsSha256Utf8(source.excerpt));
    }
    // WHY [BOL-OPS-INLINE-EXACT-PROOF-001]: claim proof is a bounded view of
    // the already verified public envelope, not a route into the WhatsApp
    // directory. Keep the Ops summary mounted and put only immutable identity,
    // digest, and offsets in the route/history so reload/back preserve the exact
    // occurrence, including two disjoint ranges from one source, without
    // exposing source words or unrelated conversations.
    // Falsifier: Verify mounts the conversation directory, two ranges from one
    // source open the same highlight, reload loses the claim/span, summary
    // scroll changes, or excerpt/highlight text enters URL or history state.
    const summaryState = {
      ...opsEvidenceHistoryRecord(target.history.state),
      ...(returnReceipt ? { businessOpsEvidenceReturn: returnReceipt } : {})
    };
    if (returnReceipt) target.history.replaceState(summaryState, "", target.location.href);
    target.history.pushState({
      ...summaryState,
      businessOpsEvidenceAnchor: source.visual || source.reaction ? undefined : opsEvidenceAnchor(source),
      businessOpsEvidenceProof: {
        schema: OPS_EVIDENCE_PROOF_HISTORY_SCHEMA,
        turnId,
        claimIndex,
        sourceEventId: source.sourceEventId,
        ...occurrence
      }
    }, "", opsEvidenceVerificationPath(url));
    return true;
  } catch {
    return false;
  }
}

export function closeOpsEvidenceVerification(
  route: OpsEvidenceVerificationRoute,
  target: OpsEvidenceVerificationTarget | null = browserOpsEvidenceVerificationTarget()
): "BACK" | "REPLACED" | "UNAVAILABLE" {
  if (!target) return "UNAVAILABLE";
  try {
    const state = opsEvidenceHistoryRecord(target.history.state);
    const proof = state.businessOpsEvidenceProof;
    if (proof !== null && typeof proof === "object" && !Array.isArray(proof)) {
      const marker = proof as Record<string, unknown>;
      if (opsEvidenceProofHistoryMatchesRoute(marker, route)) {
        target.history.back();
        return "BACK";
      }
    }
    const url = new URL(target.location.href);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_TURN_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_CLAIM_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_SOURCE_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_START_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_END_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_EXCERPT_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_VISUAL_PARAM);
    url.searchParams.delete(OPS_EVIDENCE_PROOF_REACTION_PARAM);
    delete state.businessOpsEvidenceAnchor;
    delete state.businessOpsEvidenceProof;
    target.history.replaceState(state, "", opsEvidenceVerificationPath(url));
    return "REPLACED";
  } catch {
    return "UNAVAILABLE";
  }
}

export function opsEvidenceAnchor(source: Extract<OpsClaimEvidenceSource, { highlight: string }>): OpsEvidenceAnchor {
  return {
    schema: OPS_EVIDENCE_ANCHOR_SCHEMA,
    sourceEventId: source.sourceEventId,
    excerptHash: opsSha256Utf8(source.excerpt),
    excerptLength: source.excerpt.length,
    highlightStart: source.highlightStart,
    highlightEnd: source.highlightEnd
  };
}

export function writeOpsEvidenceAnchor(
  source: OpsClaimEvidenceSource,
  target: OpsEvidenceAnchorHistory | null = typeof window === "undefined" ? null : window.history
): boolean {
  if (!target || source.visual || source.reaction) return false;
  try {
    // WHY [BOL-OPS-SOURCE-SPAN-ANCHOR-001]: a UUID opens the right message but
    // cannot identify repeated claim words. Persist only source/range/digest
    // metadata on the new history entry; source text and canonical turn bytes
    // stay untouched. Falsifier: reload loses the exact span, stale source bytes
    // receive a mark, or any excerpt/highlight words enter browser history.
    target.replaceState({ businessOpsEvidenceAnchor: opsEvidenceAnchor(source) }, "");
    return true;
  } catch {
    return false;
  }
}

function opsPrivatePresentationText(text: string, maskPhones: boolean): string {
  return maskPhones ? maskPersonalPhoneNumbers(text) : text;
}

function OpsGroupedHighlightedEvidenceText({ proof }: {
  proof: OpsEvidenceGroupedHighlightPreview;
}) {
  return <>{proof.segments.map((segment, index) => segment.highlighted
    ? <mark data-ops-evidence-highlight="true" key={`${index}:${segment.text}`}><SafeLinkedText text={segment.text} /></mark>
    : <Fragment key={`${index}:${segment.text}`}><SafeLinkedText text={segment.text} /></Fragment>)}</>;
}

function opsEvidenceClaimKindLabel(claim: OpsEvidenceClaim): string {
  return claim.kind === "SOURCE_LINKED"
    ? "Source-linked"
    : claim.kind === "UNRESOLVED"
    ? "Unresolved"
    : claim.kind === "INFERENCE"
      ? "Inference"
      : claim.kind === "CONFLICT"
        ? "Conflict"
        : "Verified fact";
}

export function OpsAnswerText({ text, separateItems, linkify = true }: {
  text: string;
  separateItems: boolean;
  linkify?: boolean;
}) {
  const lines = separateItems ? text.split("\n") : [text];
  if (lines.length === 1) {
    return <p data-ops-visible-answer="true">{linkify ? <SafeLinkedText text={text} /> : text}</p>;
  }
  // WHY [BOL-OPS-REPORT-ITEM-SPACING-001]: a selected canonical answer already
  // carries immutable newlines between its authored items, but pre-wrap painted
  // those lines as one dense wall. Give only those existing boundaries paragraph
  // semantics. Hidden separators keep textContent byte-for-byte equal to the
  // masked canonical answer for the paint receipt; Copy and Voice still consume
  // response.text. Falsifier: a selected item line has no visual separation or
  // the paint receipt, Copy, or Voice loses or invents a byte.
  return <div className="ops-answer-text ops-answer-text--items" data-ops-visible-answer="true">
    {lines.map((line, index) => <Fragment key={index}>
      {index > 0 ? <span hidden>{"\n"}</span> : null}
      {line ? <p data-ops-visible-answer-part="true">{linkify ? <SafeLinkedText text={line} /> : line}</p> : null}
    </Fragment>)}
  </div>;
}

export function opsAnswerUsesItemSpacing(response: OpsAssistantResponse): boolean {
  if (response.kind !== "ANSWER" || !response.text.includes("\n")) return false;
  // WHY [BOL-OPS-CHAT-ITEM-SPACING-001]: real saved operational summaries are
  // multi-claim ANSWER/CHAT results, so the old REPORT-only gate left their
  // authored item boundaries dense even though the renderer itself was ready.
  // Evidence-claim cardinality is an explicit response contract, not inferred
  // text semantics: keep ordinary Chat/list/code as one pre-wrapped flow and
  // never classify prose with keywords or regex. Falsifier: a multi-claim Chat
  // summary stays dense, or newline-heavy ordinary Chat is split into items.
  return response.format === "REPORT" || (response.evidenceClaims?.length ?? 0) > 1;
}

export function opsAnswerPresentationText(response: OpsAssistantResponse): string {
  if (response.kind !== "ANSWER" || !response.sourceAvailability) return response.text;
  // WHY [BOL-J1-INLINE-PROOF-001]: the server's record counters are inspection
  // metadata, not the model's business answer. The existing exact suffix owner
  // separates them without rewriting a word, a saved hash, Copy, or Voice.
  // Unbound legacy data stays intact rather than deleting guessed prose.
  try { return opsAnswerBusinessText(response); } catch { return response.text; }
}

export function opsInlineAnswerSpans(response: OpsAssistantResponse, maskPhones: boolean) {
  if (response.kind !== "ANSWER" || !response.evidenceClaims?.length) return null;
  const text = opsAnswerPresentationText(response);
  const claims = response.evidenceClaims;
  const spans = bindOpsOrderedAnswerClaimSpans(text, claims.map((claim) => claim.text));
  if (!spans || spans.some((span, index) => {
    const bound = claims[index]?.answerSpan;
    return !bound || bound.start !== span.start || bound.end !== span.end || bound.text !== span.text;
  })) return null;
  // Mask the whole answer before splitting. A phone spanning a boundary must
  // not leak, and an ambiguous private projection falls back to ordinary text.
  return bindOpsOrderedAnswerClaimSpans(opsPrivatePresentationText(text, maskPhones),
    claims.map((claim) => opsPrivatePresentationText(claim.text, maskPhones)));
}

// The shared coverage-page schema owns validation; saved answers stay unchanged.
// A second permissive parser here could silently drop an affected source.
type OpsCoverageSource = OpsAssistantCoverageSource;
type OpsCoverageAttachment = NonNullable<OpsCoverageSource["attachment"]>;

function opsCoverageAttachmentLabel(attachment: OpsCoverageAttachment): string {
  const mime = attachment.declaredMimeType?.split(";", 1)[0]?.trim().toLocaleLowerCase() ?? "";
  if (mime.startsWith("video/")) return "Video";
  if (mime.startsWith("image/")) return "Image";
  if (mime.startsWith("audio/")) return "Audio";
  if (mime === "application/pdf") return "Document";
  return attachment.mediaKind.charAt(0) + attachment.mediaKind.slice(1).toLocaleLowerCase();
}

export function opsCoverageSourceReason(source: OpsCoverageSource): string {
  const reasons: string[] = [];
  if (source.recordKind === "ALBUM_CONTAINER") {
    reasons.push("Album container with no standalone message text.");
  } else if (source.unavailableReason === "SECRET_ENCRYPTED_MESSAGE") {
    reasons.push("This message arrived in an encrypted format the capture could not read. Open the source message to see what was captured.");
  } else if (source.unavailableReason === "UNKNOWN_UNREADABLE") {
    reasons.push("This record has no readable text; the capture did not identify a more specific reason. Open its source message to inspect it.");
  } else if (source.textUnavailable && !source.attachment && !source.archiveMembers?.length) {
    reasons.push("This record has no readable text. Open its source message to inspect it.");
  }
  if (source.attachment) {
    const label = opsCoverageAttachmentLabel(source.attachment);
    const status = source.attachment.statusCode?.trim() ?? "";
    if (label === "Video") {
      reasons.push("Video content was not reviewed.");
      if (status === "TOO_LARGE") reasons.push("The original exceeded the download limit; open the source message to check it.");
    } else if (status === "TOO_LARGE") {
      reasons.push(`${label} body unavailable because it exceeded the file-size limit.`);
    } else if (source.attachment.state === "SKIPPED") {
      reasons.push(`${label} body was not retained${status ? ` (status ${status})` : "."}`);
    } else if (status) {
      reasons.push(`${label} body unavailable (status ${status}).`);
    } else if (source.attachment.state) {
      reasons.push(`${label} body unavailable (state ${source.attachment.state}).`);
    } else {
      reasons.push(`${label} body unavailable; no reason was recorded.`);
    }
  }
  if (source.archiveMembers?.length) {
    reasons.push(`${source.archiveMembers.length} archive ${source.archiveMembers.length === 1 ? "member was" : "members were"} not fully read; details follow.`);
  }
  return reasons.join(" ");
}

/**
 * WHY [BOL-J1-UNREADABLE-COVERAGE-UI-001]: the public availability disclosure
 * previously reduced exact unavailable source identities to counts, so a human
 * could not inspect the four affected records without reading everything. Keep
 * this list collapsed, server-owned, and outside answer/proof text; only the
 * existing permission-bound message callback may open an exact source. Never
 * turn an album container into a child-file claim or expose an unavailable file
 * as openable. Falsifier: old turns without sources change, a raw UUID becomes
 * a friendly label, response.text/hash changes, or SKIPPED/TOO_LARGE gets an
 * Open file action.
 */
export function OpsSourceCoverage({ sourceAvailability, turnId, openConversations }: {
  sourceAvailability?: OpsAnswerSourceAvailability | undefined;
  turnId: string;
  openConversations?: ((sourceEventId?: string, returnReceipt?: OpsEvidenceThreadReturnReceipt | null) => void) | undefined;
}) {
  const [sources, setSources] = useState<OpsCoverageSource[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [loadState, setLoadState] = useState<"IDLE" | "LOADING" | "LOADED" | "ERROR">("IDLE");
  const requestedOffset = useRef<number | null>(null);
  const loadedOffsets = useRef(new Set<number>());
  const requestController = useRef<AbortController | null>(null);
  const sourceIds = useRef(new Set<string>());

  useEffect(() => {
    requestController.current?.abort();
    requestController.current = null;
    requestedOffset.current = null;
    loadedOffsets.current = new Set();
    sourceIds.current = new Set();
    setSources([]);
    setNextOffset(0);
    setTotalCount(null);
    setLoadState("IDLE");
    return () => requestController.current?.abort();
  }, [turnId]);

  const loadPage = useCallback(async (offset: number, replace: boolean) => {
    if (requestedOffset.current === offset || (!replace && loadedOffsets.current.has(offset))) return;
    const controller = new AbortController();
    requestController.current?.abort();
    requestController.current = controller;
    requestedOffset.current = offset;
    setLoadState("LOADING");
    try {
      const page = await api.opsAssistantCoverage(turnId, offset, controller.signal, true);
      if (controller.signal.aborted) return;
      const seen = replace ? new Set<string>() : new Set(sourceIds.current);
      const merged: OpsCoverageSource[] = replace ? [] : [...sources];
      for (const source of page.sources) {
        if (seen.has(source.sourceEventId)) continue;
        seen.add(source.sourceEventId);
        merged.push(source);
      }
      sourceIds.current = seen;
      loadedOffsets.current.add(offset);
      setSources(merged);
      setTotalCount(page.totalCount);
      setNextOffset(page.nextOffset);
      setLoadState("LOADED");
    } catch {
      if (!controller.signal.aborted) setLoadState("ERROR");
    } finally {
      if (requestController.current === controller) {
        requestController.current = null;
        requestedOffset.current = null;
      }
    }
  }, [sources, turnId]);

  if (!sourceAvailability) return null;
  const files = sources.filter((source) => source.attachment || source.archiveMembers?.length);
  const otherSources = sources.filter((source) => !source.attachment && !source.archiveMembers?.length);
  const actionableSources = otherSources.filter((source) => source.recordKind !== "ALBUM_CONTAINER");
  const albumSources = otherSources.filter((source) => source.recordKind === "ALBUM_CONTAINER");
  const onlyVideos = nextOffset === null && files.length > 0
    && files.every((source) => source.attachment && opsCoverageAttachmentLabel(source.attachment) === "Video");
  const sourceRow = (source: OpsCoverageSource, index: number) => <blockquote key={source.sourceEventId}>
    <strong>{source.recordKind === "ALBUM_CONTAINER"
      ? "Album record"
      : source.archiveMembers?.length ? "Archive attachment"
      : source.attachment ? `${opsCoverageAttachmentLabel(source.attachment)} ${index + 1}` : "Source message"}</strong>
    <p>{opsCoverageSourceReason(source)}</p>
    {source.archiveMembers?.length ? <ul>
      {source.archiveMembers.map((member) => <li key={`${member.memberIndex}:${member.path}`}>
        <span>{member.path}</span>{" — "}
        {member.status === "DEFERRED" ? "Not reviewed" : "Unavailable"}
        {" ("}{member.reason.replaceAll("_", " ").toLocaleLowerCase()}{")"}
      </li>)}
    </ul> : null}
    {openConversations ? <span className="ops-source-actions">
      <button className="ops-source-message-link" type="button" onClick={(event) => {
        const thread = event.currentTarget.closest<HTMLElement>(".ops-thread");
        const returnReceipt = thread ? captureOpsSourceReturn(turnId, thread.scrollTop) : null;
        openConversations(source.sourceEventId, returnReceipt ?? undefined);
      }}>Open source message</button>
    </span> : null}
  </blockquote>;
  return <details className="ops-answer-sources ops-coverage-sources" onToggle={(event) => {
    if (event.currentTarget.open && loadState === "IDLE") void loadPage(0, true);
  }}>
    <summary>{onlyVideos ? "Videos not reviewed" : sourceAvailability.unavailableAttachmentCount ? "Files not reviewed" : "Source notes"}
      {sourceAvailability.unavailableAttachmentCount ? <span>{sourceAvailability.unavailableAttachmentCount}</span> : null}
    </summary>
    <div>
      {files.map(sourceRow)}
      {actionableSources.map(sourceRow)}
      {albumSources.length ? <details className="ops-coverage-record-notes">
        <summary>Other source notes</summary>{albumSources.map(sourceRow)}
      </details> : null}
      {loadState === "LOADING" ? <p role="status">Loading source links…</p> : null}
      {loadState === "ERROR" ? <p role="alert">Source details could not be loaded. <button type="button" onClick={() => void loadPage(nextOffset ?? 0, nextOffset === 0)}>Retry</button></p> : null}
      {loadState === "LOADED" && sources.length === 0 ? <p>No source details were returned.</p> : null}
      {nextOffset !== null && loadState === "LOADED" ? <button type="button" onClick={() => void loadPage(nextOffset, false)}>Show more source links</button> : null}
    </div>
  </details>;
}

export type OpsEvidenceReplyLoadState =
  | { sourceEventId: string; status: "LOADING" }
  | { sourceEventId: string; status: "FAILED" }
  | { sourceEventId: string; status: "READY"; context: WhatsAppProofReplyContext };

export function OpsEvidenceReplyContext({ state, maskPhones }: {
  state: OpsEvidenceReplyLoadState;
  maskPhones: boolean;
}) {
  if (state.status === "LOADING") {
    return <aside className="ops-evidence-proof__reply" aria-live="polite"><small>Replied to</small><p>Loading original message…</p></aside>;
  }
  if (state.status === "FAILED") {
    return <aside className="ops-evidence-proof__reply is-unavailable" role="status"><small>Replied to</small><p>Original message could not be loaded.</p></aside>;
  }
  if (state.context.kind === "NOT_A_REPLY") return null;
  if (state.context.kind === "ORIGINAL_UNAVAILABLE") {
    return <aside className="ops-evidence-proof__reply is-unavailable"><small>Replied to</small><p>Original message is not in captured history.</p></aside>;
  }
  const content = state.context.content || "Message without readable text";
  return <aside
    className="ops-evidence-proof__reply"
    data-ops-evidence-reply-source={state.context.sourceEventId}
  >
    <small>Replied to</small>
    <strong>{opsPrivatePresentationText(state.context.senderLabel, maskPhones)}</strong>
    <time dateTime={state.context.occurredAt}>{formatEvidenceTime(state.context.occurredAt)}</time>
    <p><SafeLinkedText text={opsPrivatePresentationText(content, maskPhones)} /></p>
  </aside>;
}

export function OpsEvidenceProofView({ claim, source, maskPhones, onBack, openConversations }: {
  claim: OpsEvidenceClaim;
  source: OpsClaimEvidenceSource;
  maskPhones: boolean;
  onBack: () => void;
  openConversations?: ((sourceEventId?: string) => void) | undefined;
}) {
  const backButtonRef = useRef<HTMLButtonElement>(null);
  const proofSources = opsEvidenceSourcesForProofView(claim, source);
  const groupedTextProof = source.visual || source.reaction
    ? null : opsGroupedEvidenceHighlightPreview(proofSources, maskPhones);
  const privateSubject = opsPrivatePresentationText(claim.subject, maskPhones);
  const privateClaimName = privateSubject || opsPrivatePresentationText(claim.text, maskPhones);
  const privateSender = opsPrivatePresentationText(source.senderLabel, maskPhones);
  const canReadReplyContext = Boolean(
    openConversations && !source.reaction && source.sourceActions?.includes("OPEN_MESSAGE")
  );
  const [replyState, setReplyState] = useState<OpsEvidenceReplyLoadState | null>(null);
  useEffect(() => {
    if (!canReadReplyContext) {
      setReplyState(null);
      return;
    }
    const abort = new AbortController();
    setReplyState({ sourceEventId: source.sourceEventId, status: "LOADING" });
    void api.whatsAppProofReplyContext(source.sourceEventId, abort.signal).then((context) => {
      if (!abort.signal.aborted) {
        setReplyState({ sourceEventId: source.sourceEventId, status: "READY", context });
      }
    }).catch(() => {
      if (!abort.signal.aborted) {
        setReplyState({ sourceEventId: source.sourceEventId, status: "FAILED" });
      }
    });
    return () => abort.abort();
  }, [canReadReplyContext, source.sourceEventId]);
  useEffect(() => {
    backButtonRef.current?.focus({ preventScroll: true });
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onBack();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onBack]);
  if (!source.visual && !source.reaction && !groupedTextProof) return null;
  const SourceContainer = source.visual || source.reaction ? "div" : "blockquote";
  return (
    <div
      className="ops-evidence-proof"
      role="dialog"
      aria-modal="true"
      aria-label={`Source proof for ${privateClaimName}`}
      onPointerDown={(event) => {
        if (shouldDismissOpsEvidenceBackdrop(event.target, event.currentTarget)) onBack();
      }}
    >
      <section className="ops-evidence-proof__panel">
        <header className="ops-evidence-proof__header">
          <button type="button" ref={backButtonRef} onClick={onBack}><ArrowLeft size={17} aria-hidden="true" />Back</button>
          <span><small>Exact source</small>{privateSubject ? <strong>{privateSubject}</strong> : null}</span>
          <button className="ops-evidence-proof__close" type="button" onClick={onBack} aria-label="Close source proof"><X size={18} aria-hidden="true" /></button>
        </header>
        <div className="ops-evidence-proof__body">
          <article className="ops-evidence-proof__claim">
            <small>{opsEvidenceClaimKindLabel(claim)}</small>
            <p>{opsPrivatePresentationText(claim.text, maskPhones)}</p>
          </article>
          <SourceContainer className="ops-evidence-proof__source" data-ops-evidence-source={source.sourceEventId}>
            <strong>{opsPrivatePresentationText(source.conversationLabel, maskPhones)} · {privateSender}</strong>
            <small>{source.reaction ? "Reaction" : source.visual ? source.visual.kind === "PDF_PAGE" ? `PDF · Page ${source.visual.pageNumber}` : "Image" : source.kind === "FILE" ? "File" : source.kind === "VOICE_TRANSCRIPT" ? "Transcript" : "Message"} · {formatEvidenceTime(source.occurredAt)}</small>
            {source.reaction ? <div className="ops-evidence-proof__reaction">
              <small>Exact provider reaction</small>
              <strong>{source.reaction.text === null
                ? "Reaction text unavailable"
                : source.reaction.text === ""
                  ? "Reaction removed"
                  : opsPrivatePresentationText(source.reaction.text, maskPhones)}</strong>
              {/* WHY [BOL-WA-REACTION-TARGET-AVAILABILITY-001]: UNAVAILABLE
                  carries no reason. Do not present an unknown capture, scope,
                  identity, or retention cause as established source truth. */}
              <span>{source.reaction.target.kind === "AVAILABLE"
                ? "Reacted-to message is available in this proof scope."
                : "Reacted-to message unavailable."}</span>
            </div> : null}
            {source.deletions?.length ? <div className="ops-evidence-proof__reaction">
              <small>Provider deletion status</small>
              <strong>Message revoked</strong>
              {/* WHY [BOL-WA-REVOKE-TARGET-PRESENTATION-001]: the selected
                  proof still belongs to the original target sender/time. A
                  provider REVOKE edge proves deletion status only; do not
                  relabel the target as a protocol event, attribute the revoke
                  to that sender, or imply deleted content was recovered. */}
              <span>{source.deletions.length === 1
                ? "A provider REVOKE event targets this exact message. It does not identify who deleted it or recover deleted content."
                : `${source.deletions.length} provider REVOKE events target this exact message. They do not identify who deleted it or recover deleted content.`}</span>
            </div> : null}
            {!source.visual && groupedTextProof ? <p>
              <OpsGroupedHighlightedEvidenceText proof={groupedTextProof} />
            </p> : null}
            {/* WHY [BOL-OPS-PROOF-REPLY-CONTEXT-001]: an exact selected reply
                previously hid the one canonical message that gave its words a
                referent, forcing the operator to leave proof and inspect a group.
                Read only the server-authorized immutable one-hop relation; never
                infer a neighboring message, mutate the selected quote/hash, or
                carry reply state across source UUIDs. Falsifier: the referenced
                original is absent here, a non-WhatsApp proof fetches context, or
                an old/stale/cross-workspace reply appears under another source. */}
            {replyState?.sourceEventId === source.sourceEventId
              ? <OpsEvidenceReplyContext state={replyState} maskPhones={maskPhones} />
              : null}
            {source.contact ? <div className="ops-evidence-proof__contact">
              <span><small>Verified contact</small><strong>{opsPrivatePresentationText(source.contact.phoneE164, maskPhones)}</strong></span>
              {!maskPhones ? <span className="ops-evidence-proof__contact-actions">
                {source.contact.actions.map((action) => {
                  const href = opsEvidenceContactHref(action, source.contact!.phoneE164);
                  if (!href) return null;
                  const label = action === "CALL" ? `Call ${privateSender}` : `WhatsApp ${privateSender}`;
                  return <a
                    href={href}
                    key={action}
                    data-phone-action="true"
                    aria-label={label}
                    {...(action === "WHATSAPP" ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >{action === "CALL" ? <Phone size={16} aria-hidden="true" /> : <MessageCircle size={16} aria-hidden="true" />}{action === "CALL" ? "Call" : "WhatsApp"}</a>;
                })}
              </span> : null}
            </div> : null}
            {/* WHY [BOL-OPS-PROOF-SOURCE-ACTION-001]: source.kind is shared by
                WhatsApp and non-WhatsApp transcripts. Open message therefore
                renders only from the server-authorized immutable-source
                capability; it is never inferred from labels, text, or kind.
                Falsifier: Meet/document proof gains WhatsApp navigation, or the
                action opens without the exact source-event UUID. */}
            {source.sourceActions?.includes("OPEN_MESSAGE")
              && openConversations ? <span className="ops-source-actions">
                <button className="ops-source-message-link" type="button" onClick={() => openConversations(source.sourceEventId)}>Open message</button>
              </span> : null}
            {/* WHY [BOL-OPS-FILE-PROOF-001]: FILE keeps its original link;
                WhatsApp attachments keep their message kind and gain a link
                only after the exact authenticated source metadata resolves.
                No directory dump or modification of the saved proof/hash. */}
            <span className="ops-source-actions">
              <OpsSourceFileAction sourceEventId={source.sourceEventId} maskPhones={maskPhones} visual={source.visual} archiveMember={source.archiveMember} discoverAttachment={!source.visual && source.kind !== "FILE"} />
            </span>
          </SourceContainer>
        </div>
      </section>
    </div>
  );
}

function OpsEvidenceClaims({ claims, proofScope, openWork, workTitle, maskPhones, openConversations, inlineAnswer }: {
  claims: readonly OpsEvidenceClaim[];
  proofScope: string;
  openWork?: ((id: string) => void) | undefined;
  workTitle?: ((id: string) => string) | undefined;
  maskPhones: boolean;
  openConversations?: ((sourceEventId?: string) => void) | undefined;
  inlineAnswer?: { text: string; spans: readonly { start: number; end: number }[]; linkify?: boolean } | undefined;
}) {
  const [activeProof, setActiveProof] = useState<OpsEvidenceVerificationRoute | null>(null);
  const proofTriggerRef = useRef<HTMLButtonElement | null>(null);
  const proofClosePendingRef = useRef(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const syncFromHistory = () => {
      proofClosePendingRef.current = false;
      const route = opsEvidenceVerificationFromUrl(window.location.href);
      const claim = route?.turnId === proofScope ? claims[route.claimIndex] : undefined;
      const source = claim && route ? opsEvidenceSourceForVerificationRoute(claim, route) : null;
      setActiveProof(route && claim && source ? route : null);
    };
    syncFromHistory();
    window.addEventListener("popstate", syncFromHistory);
    return () => window.removeEventListener("popstate", syncFromHistory);
  }, [claims, proofScope]);
  useEffect(() => {
    if (activeProof || !proofTriggerRef.current) return;
    proofTriggerRef.current.focus({ preventScroll: true });
    proofTriggerRef.current = null;
  }, [activeProof]);
  if (claims.length === 0) return null;
  const selectedClaim = activeProof?.turnId === proofScope ? claims[activeProof.claimIndex] : undefined;
  const selectedSource = selectedClaim && activeProof
    ? opsEvidenceSourceForVerificationRoute(selectedClaim, activeProof)
    : null;
  const closeProof = () => {
    if (!activeProof || proofClosePendingRef.current) return;
    // History traversal completes asynchronously. Keep this modal as the
    // interaction boundary until popstate makes the destination authoritative;
    // otherwise a reload or another proof can race the still-current route.
    proofClosePendingRef.current = true;
    const outcome = closeOpsEvidenceVerification(activeProof);
    if (outcome === "BACK") return;
    proofClosePendingRef.current = false;
    if (outcome === "REPLACED") setActiveProof(null);
  };
  const Container = inlineAnswer ? "div" : "details";
  return (
    <>
      <Container className={inlineAnswer ? "ops-answer-inline-evidence" : "ops-evidence-claims"}
        data-ops-visible-answer={inlineAnswer ? "true" : undefined}>
        {!inlineAnswer ? <summary>Verify facts <span>({claims.length})</span></summary> : null}
        <section aria-label="Evidence by claim">
          {claims.map((claim, claimIndex) => {
            const privateSubject = opsPrivatePresentationText(claim.subject, maskPhones);
            const privateClaimName = privateSubject || opsPrivatePresentationText(claim.text, maskPhones);
            const controlGroups = opsEvidenceControlGroups(claim.evidenceSources);
            const span = inlineAnswer?.spans[claimIndex];
            const separator = inlineAnswer && span
              ? inlineAnswer.text.slice(inlineAnswer.spans[claimIndex - 1]?.end ?? 0, span.start) : "";
            return <Fragment key={`${claim.kind}:${claimIndex}`}>
              {inlineAnswer ? <span data-ops-answer-text-part="true" hidden={!separator.trim()}>{separator}</span> : null}
              <article
              className={inlineAnswer ? "ops-answer-item" : "ops-evidence-claim"}
              data-ops-evidence-claim={String(claimIndex)}
            >
              <span className="ops-evidence-claim__summary">
                {!inlineAnswer ? <small>{opsEvidenceClaimKindLabel(claim)}</small> : null}
                {!inlineAnswer && privateSubject ? <strong>{privateSubject}</strong> : null}
                <p data-ops-answer-text-part={inlineAnswer ? "true" : undefined}>{inlineAnswer && span
                  ? inlineAnswer.linkify === false ? inlineAnswer.text.slice(span.start, span.end)
                    : <SafeLinkedText text={inlineAnswer.text.slice(span.start, span.end)} />
                  : opsPrivatePresentationText(claim.text, maskPhones)}</p>
              </span>
              {controlGroups.length > 0 ? <span className="ops-evidence-claim__verify">
                {controlGroups.map((sources, sourceIndex) => {
                  const source = sources[0]!;
                  return <button
                  type="button"
                  key={opsEvidenceVisibleSourceKey(source)}
                  aria-label={`Verify ${privateClaimName}${controlGroups.length > 1 ? `, source ${sourceIndex + 1}` : ""}`}
                  onClick={(event) => {
                    proofTriggerRef.current = event.currentTarget;
                    const route = {
                      turnId: proofScope,
                      claimIndex,
                      sourceEventId: source.sourceEventId,
                      ...(source.visual ? { visualHash: opsVisualProofKey(source.visual) }
                        : source.reaction ? { reactionHash: opsSha256(source.reaction) } : {
                        highlightStart: source.highlightStart,
                        highlightEnd: source.highlightEnd,
                        excerptHash: opsSha256Utf8(source.excerpt)
                      })
                    };
                    const thread = event.currentTarget.closest<HTMLElement>(".ops-thread");
                    pushOpsEvidenceVerification(
                      proofScope,
                      claimIndex,
                      source,
                      thread ? opsEvidenceThreadReturnReceipt(proofScope, thread.scrollTop) : null
                    );
                    setActiveProof(route);
                  }}
                >View proof{controlGroups.length > 1 ? ` ${sourceIndex + 1}` : ""}</button>})}
              </span> : null}
              {claim.workItemIds.length > 0 ? <div className="ops-work-links ops-evidence-claim__work">
                {claim.workItemIds.map((id) => openWork
                  ? <button type="button" key={id} onClick={() => openWork(id)}><span><small>Task evidence</small><strong>{workTitle?.(id) ?? "Open task"}</strong></span><ArrowRight size={16} aria-hidden="true" /></button>
                  : <span className="ops-evidence-claim__work-label" data-ops-evidence-work={id} key={id}><small>Task evidence</small><strong>{workTitle?.(id) ?? id}</strong></span>)}
              </div> : null}
            </article></Fragment>;
          })}
          {inlineAnswer ? <span data-ops-answer-text-part="true" hidden>{inlineAnswer.text.slice(inlineAnswer.spans.at(-1)?.end ?? 0)}</span> : null}
        </section>
      </Container>
      {selectedClaim && selectedSource ? <OpsEvidenceProofView
        claim={selectedClaim}
        source={selectedSource}
        maskPhones={maskPhones}
        onBack={closeProof}
        {...(openConversations ? { openConversations } : {})}
      /> : null}
    </>
  );
}

function opsAnswerNavigationReferences(response: Extract<OpsAssistantResponse, { kind: "ANSWER" }>) {
  // WHY [BOL-OPS-CLAIM-REFERENCE-DUPLICATE-001]: the live answer showed 20
  // WhatsApp source cards outside closed Verify although every source already
  // had claim proof. Filter only that duplicate navigation in normal/fallback
  // presentation; never rewrite the canonical result or hide unrelated links.
  // Keep the existing parse-before-use boundary: malformed optional references
  // must not throw before normal Chat can discard them and retain its answer.
  // Falsifier: a claim-bound WhatsApp UUID gains another card, malformed refs
  // lose the answer, or legacy/uncovered/Task/File/Google references disappear.
  const claimSourceIds = new Set(response.evidenceClaims?.flatMap((claim) =>
    claim.evidenceSources.map((source) => source.sourceEventId)
  ));
  return (response.references ?? []).flatMap((value) => {
    const parsed = OpsAssistantReferenceSchema.safeParse(value);
    if (!parsed.success) return [];
    const reference = parsed.data;
    return reference.kind === "WHATSAPP" && claimSourceIds.has(reference.sourceEventId)
      ? [] : [reference];
  });
}

export function OpsPublicTurnFallback({ turn, publicTurn, displayFailed = true, openConversations }: {
  turn: Pick<Turn, "prompt" | "attachments" | "cancelled">;
  publicTurn: OpsPublicTurnEnvelope;
  displayFailed?: boolean;
  openConversations?: ((sourceEventId?: string) => void) | undefined;
}) {
  const response = publicTurn.result.response;
  const maskPhones = usePrivateModePhoneMasking();
  const [copied, setCopied] = useState(false);
  const answerText = opsAnswerPresentationText(response);
  const inlineSpans = opsInlineAnswerSpans(response, maskPhones);
  return (
    <article
      className="ops-turn"
      data-ops-turn-id={publicTurn.turnId}
      data-ops-turn-revision={String(publicTurn.revision)}
      data-ops-answer-hash={publicTurn.answerHash}
      data-ops-result-hash={publicTurn.resultHash}
      data-ops-phone-privacy={maskPhones ? "on" : "off"}
      data-ops-answer-presentation={inlineSpans ? "claim-spans" : "plain"}
      data-ops-answer-projection="business"
      data-response-ready="true"
      data-display-failed={displayFailed ? "true" : undefined}
      data-ops-summary={isSummaryPrompt(turn.prompt) ? "true" : undefined}
    >
      {turn.prompt || turn.attachments?.length ? <div className="ops-message ops-message--user"><p>{opsPrivatePresentationText(turn.prompt, maskPhones)}</p><OpsSharedAttachments attachments={turn.attachments} privateMode={maskPhones} retired={turn.cancelled === true} scope={`${publicTurn.turnId}:${publicTurn.revision}`} /></div> : null}
      <div className={`ops-message ops-message--assistant ops-message--${response.kind.toLowerCase()}`}>
        <div className="ops-message__body">
          <div className="ops-answer-copy-row">
            {inlineSpans && response.kind === "ANSWER" ? <OpsEvidenceClaims
              claims={response.evidenceClaims!}
              proofScope={publicTurn.turnId}
              maskPhones={maskPhones}
              inlineAnswer={{ text: opsPrivatePresentationText(answerText, maskPhones), spans: inlineSpans, linkify: false }}
              {...(openConversations ? { openConversations } : {})}
            /> : <OpsAnswerText
              text={opsPrivatePresentationText(answerText, maskPhones)}
              separateItems={opsAnswerUsesItemSpacing(response)}
              linkify={false}
            />}
            {response.kind === "ANSWER" ? <button type="button" title="Copy answer" aria-label="Copy answer" onClick={() => {
              void copyOpsAnswer(response.text, { maskPhones }).then((didCopy) => {
                if (!didCopy) return;
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1_500);
              });
            }}>{copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}<span>{copied ? "Copied" : "Copy"}</span></button> : null}
          </div>
          {response.kind === "ANSWER" ? <OpsSourceCoverage
            key={publicTurn.turnId}
            sourceAvailability={response.sourceAvailability}
            turnId={publicTurn.turnId}
            {...(openConversations ? { openConversations } : {})}
          /> : null}
          {!inlineSpans && response.kind === "ANSWER" && response.evidenceClaims !== undefined ? <OpsEvidenceClaims
            claims={response.evidenceClaims}
            proofScope={publicTurn.turnId}
            maskPhones={maskPhones}
            {...(openConversations ? { openConversations } : {})}
          /> : null}
          {response.kind === "ANSWER" ? <>
            {/* WHY [BOL-OPS-CLEAN-ANSWER-001]: proof call/row/byte counts are
                private transport diagnostics, not useful answer content. Exact
                claim-bound source proof stays collapsed behind Verify, including
                this persisted-display fallback. Falsifier: telemetry or a flat
                source dump appears beside the answer, or display failure hides
                the exact evidence needed to verify it. */}
            {response.evidenceClaims === undefined && response.evidenceSources?.length ? (
              <details className="ops-answer-sources">
                <summary>Sources <span>{response.evidenceSources.length}</span></summary>
                <div>{response.evidenceSources.map((source) => <blockquote key={source.sourceEventId}>
                  <strong>{opsPrivatePresentationText(source.conversationLabel, maskPhones)} · {opsPrivatePresentationText(source.senderLabel, maskPhones)}</strong>
                  <small>{formatEvidenceTime(source.occurredAt)}</small>
                  <p>{opsPrivatePresentationText(source.excerpt, maskPhones)}</p>
                </blockquote>)}</div>
              </details>
            ) : null}
            {response.webSources?.length ? (
              <details className="ops-answer-sources">
                <summary>Web sources <span>{response.webSources.length}</span></summary>
                <div>{response.webSources.map((source) => <blockquote key={source.id}>
                  <strong>{source.title}</strong><p>{source.summary}</p>
                </blockquote>)}</div>
              </details>
            ) : null}
            {opsAnswerNavigationReferences(response).map((reference, index) => <p key={`${reference.kind}:${index}`}>{reference.label}</p>)}
            {response.artifacts?.map((artifact) => <article key={artifact.id}>
              <strong>{artifact.displayName}</strong><pre>{artifact.preview.text}</pre><small>{artifact.sha256}</small>
            </article>)}
          </> : null}
          {response.kind === "IMAGE_JOB" && response.job.state === "SUCCEEDED" ? <>
            <p>{response.job.artifact.altText}</p><small>{response.job.artifact.sha256}</small>
          </> : null}
          {response.kind === "MUTATION_PREVIEW" ? <>
            <strong>{response.preview.title}</strong>
            <dl>{response.preview.changes.map((change) => <div key={change.label}>
              <dt>{change.label}</dt><dd>{change.current}</dd><dd>{change.next}</dd>
            </div>)}</dl>
          </> : null}
          {publicTurn.result.proposals.length ? <dl>
            {publicTurn.result.proposals.map((proposal) => <div key={proposal.id}>
              <dt>{proposal.id}</dt>
              <dd>{proposal.state}</dd>
              <dd>{proposal.currentRevision}</dd>
              {proposal.receipt ? <><dd>{proposal.receipt.status}</dd><dd>{proposal.receipt.proposalRevision}</dd></> : null}
            </div>)}
          </dl> : null}
        </div>
      </div>
    </article>
  );
}

export function OpsTurn({ turn, applying, apply, cancel, retry, openWork, workTitle, unmaterializedWorkProposalIds = NO_CURRENT_REVIEW_PROPOSAL_IDS, currentWorkItemIds = NO_CURRENT_REVIEW_PROPOSAL_IDS, openWorkspace, workspaceReferenceSummary, renderProposal, proposalIds, openConversations, upscaling = null, upscale }: {
  turn: Turn;
  applying: boolean;
  apply: (
    turn: Turn & {
      response: Extract<OpsAssistantResponse, { kind: "MUTATION_PREVIEW" }>;
      publicTurn: OpsPublicTurnEnvelope;
    },
    file: File | null,
    commandOverride?: OpsAssistantMutationCommand
  ) => Promise<void>;
  cancel: (turn: Turn) => void;
  retry: () => void;
  openWork: ((id: string) => void) | undefined;
  workTitle: ((id: string) => string) | undefined;
  unmaterializedWorkProposalIds?: ReadonlySet<string>;
  currentWorkItemIds?: ReadonlySet<string>;
  openWorkspace?: (reference: OpsAssistantWorkspaceReference) => void;
  workspaceReferenceSummary?: (reference: OpsAssistantWorkspaceReference, workItemIds: readonly string[]) => OpsWorkspaceReferenceSummary;
  renderProposal: ((id: string) => ReactNode) | undefined;
  proposalIds: readonly string[];
  openConversations?: ((sourceEventId?: string) => void) | undefined;
  upscaling?: 2 | 4 | null;
  upscale?: (artifactId: string, factor: 2 | 4) => void;
}) {
  const maskPhones = usePrivateModePhoneMasking();
  const publicTurn = opsVisiblePublicTurn(turn);
  const response = turn.publicTurn
    ? (publicTurn?.result.response as OpsAssistantResponse | undefined) ?? null
    : turn.response;
  const navigationReferences = response?.kind === "ANSWER"
    ? opsAnswerNavigationReferences(response)
    : [];
  // WHY [BOL-OPS-PENDING-NEW-WORK-REFERENCE-001]: a retained pre-fix answer may
  // call its unmaterialized NEW_WORK proposal a Task. Rejection, expiry or the
  // approval-to-execution bridge removes the current Review row, so queue-only
  // suppression resurrects a dead button. Treat only the response's exact
  // proposal/Task ID collision as the legacy defect, and let the complete
  // authenticated Board release navigation once Work with that ID exists.
  // Historical bytes remain continuity, never Work or approval authority.
  // Falsifier: a terminal draft exposes a dead Task button, an unrelated absent
  // Task is hidden, or current Work with the proposal ID cannot be opened.
  const legacyUnmaterializedWorkProposalIds = response?.kind === "ANSWER"
    ? new Set((response.proposalIds ?? []).filter((proposalId) =>
        !currentWorkItemIds.has(proposalId)
        && (response.workItemIds.includes(proposalId)
          || navigationReferences.some((reference) =>
            reference.kind === "TASK" && reference.workItemId === proposalId))))
    : NO_CURRENT_REVIEW_PROPOSAL_IDS;
  const suppressesWorkNavigation = (id: string) =>
    unmaterializedWorkProposalIds.has(id) || legacyUnmaterializedWorkProposalIds.has(id);
  const safeReferences = response?.kind === "ANSWER"
    ? navigationReferences.filter((reference) =>
        reference.kind !== "TASK" || !suppressesWorkNavigation(reference.workItemId))
    : [];
  const navigableWorkItemIds = response?.kind === "ANSWER"
    ? response.workItemIds.filter((id) => !suppressesWorkNavigation(id))
    : [];
  const safeArtifacts = response?.kind === "ANSWER"
    ? (response.artifacts ?? []).flatMap((value) => {
        const parsed = OpsAssistantArtifactSchema.safeParse(value);
        return parsed.success ? [parsed.data] : [];
      })
    : [];
  const [copied, setCopied] = useState(false);
  const answerText = response ? opsAnswerPresentationText(response) : "";
  const inlineSpans = response ? opsInlineAnswerSpans(response, maskPhones) : null;
  const whatsAppCommand = response?.kind === "MUTATION_PREVIEW"
    && ["DRAFT_WHATSAPP", "SCHEDULE_WHATSAPP"].includes(response.preview.command.action)
    ? response.preview.command as Extract<OpsAssistantMutationCommand, { action: "DRAFT_WHATSAPP" | "SCHEDULE_WHATSAPP" }>
    : null;
  const isWhatsAppMessage = whatsAppCommand !== null;
  const isPrivatePhoneAction = Boolean(whatsAppCommand
    && containsPersonalPhoneNumber(whatsAppCommand.recipientLabel));
  const isScheduledWhatsApp = whatsAppCommand?.action === "SCHEDULE_WHATSAPP";
  const isWhatsAppDraft = whatsAppCommand?.action === "DRAFT_WHATSAPP";
  const isWhatsAppRouteConfiguration = response?.kind === "MUTATION_PREVIEW"
    && response.preview.command.action === "CONFIGURE_WHATSAPP_ROUTE";
  const mutationProgress = response?.kind === "MUTATION_PREVIEW"
    ? opsMutationProgressLabel(response.preview.command)
    : "Applying…";
  const hasWorkMutationTarget = response?.kind === "MUTATION_PREVIEW"
    && workMutationTarget(response.preview.command) !== null;
  const [selectedFile, setSelectedFile] = useState<File | null>(turn.pendingFile ?? null);
  const [draftMessage, setDraftMessage] = useState(
    whatsAppCommand?.messageDraft ?? ""
  );
  useEffect(() => {
    if (response && !isWhatsAppDraft) setSelectedFile(null);
    if (whatsAppCommand) setDraftMessage(whatsAppCommand.messageDraft);
  }, [isWhatsAppDraft, response, whatsAppCommand]);
  // A restored immutable preview is history, not renewed approval authority.
  // Fresh Text/Voice installation carries the non-persisted capability needed
  // to reach the existing controls; every effect still requires the server's
  // row-locked public-turn CAS.
  if (publicTurn && response?.kind === "MUTATION_PREVIEW"
    && (turn.historyVerified || !turn.publicTurnSessionFresh)) {
    return <OpsPublicTurnFallback
      turn={turn}
      publicTurn={publicTurn}
      displayFailed={false}
      {...(openConversations ? { openConversations } : {})}
    />;
  }
  const workspaceReference = response?.kind === "ANSWER" ? response.workspaceReference : undefined;
  const workspaceSummary = response?.kind === "ANSWER" && workspaceReference
    ? workspaceReferenceSummary?.(workspaceReference, response.workItemIds)
      ?? defaultOpsWorkspaceReferenceSummary(workspaceReference)
    : null;
  return (
    <article
      className="ops-turn"
      data-ops-turn-id={publicTurn?.turnId ?? turn.id}
      data-ops-turn-revision={publicTurn ? String(publicTurn.revision) : undefined}
      data-ops-answer-hash={publicTurn?.answerHash}
      data-ops-result-hash={publicTurn?.resultHash}
      data-ops-phone-privacy={maskPhones ? "on" : "off"}
      data-ops-answer-presentation={inlineSpans ? "claim-spans" : "plain"}
      data-ops-answer-projection="business"
      data-response-ready={response ? "true" : "false"}
      data-ops-summary={isSummaryPrompt(turn.prompt) ? "true" : undefined}
    >
      {turn.prompt || turn.attachments?.length ? <div className="ops-message ops-message--user"><p><SafeLinkedText text={opsPrivatePresentationText(turn.prompt, maskPhones)} /></p><OpsSharedAttachments attachments={turn.attachments} privateMode={maskPhones} retired={turn.cancelled === true} scope={`${turn.id}:${publicTurn?.revision ?? 0}`} /></div> : null}
      {turn.spokenChatMessages?.map((message, index) => (
        <div
          className="ops-message ops-message--assistant ops-message--spoken-update"
          data-ops-spoken-update={String(index)}
          key={`${turn.id}:spoken:${index}`}
        ><div className="ops-message__body"><p><SafeLinkedText text={opsPrivatePresentationText(message, maskPhones)} /></p></div></div>
      ))}
      {!response ? (
        turn.cancelled || turn.publicTurn ? (
          turn.cancellationOwner === "TEXT_STOP"
            ? <small className="ops-turn__cancelled" role="status">Stopped</small>
            : null
        ) : (
          <div className="ops-message ops-message--assistant ops-message--loading" role="status" aria-live="polite" aria-atomic="true" aria-busy="true">
            {/* WHY [BOL-OPS-CONTEXT-SCAN-001]: checking can span several real
                retrieval stages. Keep explicit Text transport progress or an
                exact spoken Voice cue authoritative; textless Voice preparation
                gets only this contained scan—not invented assistant prose, an
                emoji, generic dots, or fake percentage progress. Falsifier: it
                covers text, shifts layout, invents an unsaid status, loses the
                particle identity, or keeps travelling under reduced-motion. */}
            <OpsContextScan />
            {turn.progress ? <div><p>{turn.progress}</p></div> : null}
          </div>
        )
      ) : response.kind !== "MUTATION_PREVIEW" || !publicTurn ? (
        <div className={`ops-message ops-message--assistant ops-message--${response.kind.toLowerCase()}`}>
          <span className="ops-turn__mark" aria-hidden="true">{response.kind === "FAILURE" ? <X size={17} /> : <ShieldCheck size={17} />}</span>
          <div className="ops-message__body">
            {/* WHY [BOL-OPS-MODEL-OWNED-SUMMARY-001]: keep the model's exact
                authored business text. Existing bound answer spans locate the
                proof controls; they never regenerate/paraphrase the answer.
                Only the server-owned technical suffix moves to source notes.
                Copy, Voice, persisted result/hash and source proof stay intact. */}
            <div className="ops-answer-copy-row">
              {inlineSpans && response.kind === "ANSWER" ? <OpsEvidenceClaims
                claims={response.evidenceClaims!}
                proofScope={publicTurn?.turnId ?? turn.id}
                maskPhones={maskPhones}
                inlineAnswer={{ text: opsPrivatePresentationText(answerText, maskPhones), spans: inlineSpans }}
                {...(openWork ? { openWork } : {})}
                {...(workTitle ? { workTitle } : {})}
                {...(openConversations ? { openConversations } : {})}
              /> : <OpsAnswerText
                text={opsPrivatePresentationText(answerText, maskPhones)}
                separateItems={opsAnswerUsesItemSpacing(response)}
              />}
              {response.kind === "ANSWER" ? <button type="button" title="Copy answer" aria-label="Copy answer" onClick={() => {
                void copyOpsAnswer(response.text, { maskPhones }).then((didCopy) => {
                  if (!didCopy) return;
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1_500);
                });
              }}>{copied ? <Check size={15} aria-hidden="true" /> : <Copy size={15} aria-hidden="true" />}<span>{copied ? "Copied" : "Copy"}</span></button> : null}
            </div>
            {response.kind === "ANSWER" ? <OpsSourceCoverage
              key={publicTurn?.turnId ?? turn.id}
              sourceAvailability={response.sourceAvailability}
              turnId={publicTurn?.turnId ?? turn.id}
              {...(openConversations ? { openConversations } : {})}
            /> : null}
            {!inlineSpans && response.kind === "ANSWER" && response.evidenceClaims !== undefined ? <OpsEvidenceClaims
              claims={response.evidenceClaims}
              proofScope={publicTurn?.turnId ?? turn.id}
              maskPhones={maskPhones}
              {...(openWork ? { openWork } : {})}
              {...(workTitle ? { workTitle } : {})}
              {...(openConversations ? { openConversations } : {})}
            /> : null}
            {response.kind === "IMAGE_JOB" ? <OpsImageArtifactCard
              job={response.job}
              {...(upscale && !turn.historyVerified ? { onUpscale: upscale } : {})}
              upscalePending={upscaling}
              notice={turn.imageNotice ?? null}
              privateMode={maskPhones}
            /> : null}
            {workspaceReference && workspaceSummary && openWorkspace ? (
              <button className="ops-workspace-reference" type="button" onClick={() => openWorkspace(workspaceReference)}>
                <span><small>{workspaceSummary.eyebrow}</small><strong>{workspaceSummary.title}</strong><em>{workspaceSummary.detail}</em></span>
                <ArrowRight size={17} aria-hidden="true" />
              </button>
            ) : null}
            {response.kind === "ANSWER" && response.evidenceClaims === undefined && response.evidenceSources?.length ? (
              <details className="ops-answer-sources">
                <summary>Sources <span>{response.evidenceSources.length}</span></summary>
                <div>
                  {response.evidenceSources.map((source) => {
                    const privateExcerpt = opsPrivatePresentationText(source.excerpt, maskPhones);
                    const proof = opsEvidencePreview(privateExcerpt);
                    return (
                      <blockquote key={source.sourceEventId}>
                        <strong>{opsPrivatePresentationText(source.conversationLabel, maskPhones)} · {opsPrivatePresentationText(source.senderLabel, maskPhones)}</strong>
                        <small>{source.kind === "FILE"
                          ? "File"
                          : source.channel === "MEETING" || source.channel === "GOOGLE_MEET"
                            ? "Transcript"
                            : source.kind === "VOICE_TRANSCRIPT" ? "Voice memo" : "Message"} · {formatEvidenceTime(source.occurredAt)}</small>
                        <p className="ops-source-proof-preview"><SafeLinkedText text={proof.text} /></p>
                        {proof.hasMore ? (
                          <details className="ops-source-full">
                            <summary>Full source</summary>
                            <p><SafeLinkedText text={privateExcerpt} /></p>
                          </details>
                        ) : null}
                        <span className="ops-source-actions">
                          {openConversations && source.sourceActions?.includes("OPEN_MESSAGE") ? <button className="ops-source-message-link" type="button" onClick={() => openConversations(source.sourceEventId)}>Open message</button> : null}
                          {source.kind === "FILE" ? <OpsSourceFileAction sourceEventId={source.sourceEventId} maskPhones={maskPhones} archiveMember={source.archiveMember} /> : null}
                        </span>
                      </blockquote>
                    );
                  })}
                </div>
              </details>
            ) : null}
            {/* BOL-OPS-CLEAN-ANSWER-001 also owns the normal render: transport
                telemetry remains bound in the public envelope but never enters
                visible, copied, or spoken answer prose. */}
            {response.kind === "ANSWER" && response.webSources?.length ? (
              <details className="ops-answer-sources">
                <summary>Web sources <span>{response.webSources.length}</span></summary>
                <div>
                  {response.webSources.map((source) => (
                    <blockquote key={source.id}>
                      <strong>{safeExternalHttpUrl(source.url)
                        ? <a href={source.url} target="_blank" rel="noreferrer">{source.title}</a>
                        : source.title}</strong>
                      <p>{source.summary}</p>
                    </blockquote>
                  ))}
                </div>
              </details>
            ) : null}
            {response.kind === "ANSWER" && safeReferences.length > 0 ? (
              <div className="ops-reference-links" aria-label="References">
                {safeReferences.map((reference) => {
                  if (reference.kind === "TASK") return openWork ? (
                    <button type="button" key={`task:${reference.workItemId}`} onClick={() => openWork(reference.workItemId)}>
                      <span><small>Task</small><strong>{workTitle?.(reference.workItemId) ?? reference.label}</strong></span><ArrowRight size={16} aria-hidden="true" />
                    </button>
                  ) : null;
                  if (reference.kind === "WHATSAPP") return openConversations ? (
                    <button type="button" key={`whatsapp:${reference.sourceEventId}`} onClick={() => openConversations(reference.sourceEventId)}>
                      <span><small>WhatsApp</small><strong>{reference.label}</strong></span><ArrowRight size={16} aria-hidden="true" />
                    </button>
                  ) : null;
                  if (reference.kind === "FILE") return (
                    <a key={`file:${reference.sourceEventId}`} href={api.sourceAttachmentUrl(reference.sourceEventId)} target="_blank" rel="noreferrer">
                      <span><small>File</small><strong>{reference.label}</strong></span><ArrowRight size={16} aria-hidden="true" />
                    </a>
                  );
                  return safeGoogleWorkspaceUrl(reference.url) ? (
                    <a key={`google:${reference.url}`} href={reference.url} target="_blank" rel="noreferrer">
                      <span><small>Google Workspace</small><strong>{reference.label}</strong></span><ArrowRight size={16} aria-hidden="true" />
                    </a>
                  ) : null;
                })}
              </div>
            ) : response.kind === "ANSWER" && response.evidenceClaims === undefined && navigableWorkItemIds.length > 0 && openWork ? (
              <div className="ops-work-links">
                {navigableWorkItemIds.map((id) => <button type="button" key={id} onClick={() => openWork(id)}><span><small>Task</small><strong>{workTitle?.(id) ?? "Open task"}</strong></span><ArrowRight size={16} aria-hidden="true" /></button>)}
              </div>
            ) : null}
            {response.kind === "ANSWER" && safeArtifacts.length > 0 ? (
              <div className="ops-artifact-list" aria-label="Generated files">
                {safeArtifacts.map((artifact) => <OpsDocumentArtifactCard key={artifact.id} artifact={artifact} privateMode={maskPhones} />)}
              </div>
            ) : null}
            {response.kind === "ANSWER" && proposalIds.length && renderProposal ? (
              <div className="ops-proposal-cards" aria-label="Prepared Manual actions">
                {proposalIds.map((id) => <div key={id}>{renderProposal(id)}</div>)}
              </div>
            ) : null}
            {!turn.historyVerified && !turn.recoveryUncertain && (turn.failed || response.kind === "FAILURE") ? <button className="ops-retry" type="button" onClick={retry}><RotateCcw size={15} aria-hidden="true" />Try again</button> : null}
          </div>
        </div>
      ) : null}
      {response?.kind === "MUTATION_PREVIEW" && publicTurn ? (
        <><div className="ops-message ops-message--assistant ops-message--answer"><div className="ops-message__body"><p>{response.text}</p></div></div>
        <div className="ops-change review-card review-card--embedded" aria-label="Action ready for approval">
          <header className="ops-change__header"><span>Ready to approve</span><strong>{response.preview.title}</strong></header>
          {whatsAppCommand ? <><p className="ops-change__destination"><span>To</span><strong>{whatsAppCommand.recipientLabel}</strong></p>{isScheduledWhatsApp ? <p className="ops-change__schedule"><span>Send at</span><strong>{formatScheduledCommand(whatsAppCommand)}</strong><small>Not sent yet</small></p> : null}<label className="message-draft-field"><span>Message</span><textarea rows={3} maxLength={300} value={draftMessage} disabled={applying} onChange={(event) => setDraftMessage(event.target.value)} /><small>{draftMessage.length}/300</small></label></> : <dl>
            {response.preview.changes.map((change) => <div key={change.label}><dt>{change.label}</dt><dd><small>{change.current}</small><span aria-hidden="true">→</span><strong>{change.next}</strong></dd></div>)}
          </dl>}
          {isWhatsAppDraft ? (
            <div className="ops-change__file">
              <div>
                <label className="secondary-button" aria-label={selectedFile ? "Replace attached file" : "Attach a file"}>
                  <Paperclip size={16} aria-hidden="true" />{selectedFile ? "Replace file" : "Attach file"}
                  <input
                    type="file"
                    disabled={applying}
                    onChange={(event) => setSelectedFile(event.currentTarget.files?.[0] ?? null)}
                  />
                </label>
                {selectedFile ? (
                  <button
                    className="ops-change__file-remove"
                    type="button"
                    disabled={applying}
                    onClick={() => setSelectedFile(null)}
                    aria-label={`Remove ${selectedFile.name}`}
                  ><X size={15} aria-hidden="true" />Remove</button>
                ) : null}
              </div>
              {selectedFile
                ? <p><strong>{selectedFile.name}</strong><span>{formatFileSize(selectedFile.size)}</span></p>
                : <small>Optional file</small>}
            </div>
          ) : null}
          {/* WHY [BOL-OPS-INLINE-FEEDBACK-001]: every inline approval is an
              asynchronous guarded effect, not necessarily a WhatsApp send.
              Name the real operation immediately and expose it as a status;
              the later answer may say Sent only after proposal settlement.
              Falsifier: an approval looks frozen, all actions say Sending, or
              chat claims success before its durable effect/transport receipt. */}
          {hasWorkMutationTarget ? <p className="ops-change__lifecycle" data-stage={applying ? "CHANGING" : "TARGETED"} role="status" aria-live="polite"><i aria-hidden="true" />{applying ? "CHANGING" : "TARGETED"}</p> : null}
          {applying ? <p className="ops-change__progress" role="status" aria-live="polite" aria-atomic="true"><OpsContextScan oneShot />{mutationProgress}</p> : null}
          <div className="ops-change__actions" aria-busy={applying}>
            <button className="primary-button" type="button" data-phone-action={isPrivatePhoneAction ? "true" : undefined} aria-busy={applying} disabled={applying || (isWhatsAppMessage && !draftMessage.trim())} onClick={() => {
              const command = response.preview.command;
              const reviewedCommand = command.action === "DRAFT_WHATSAPP" || command.action === "SCHEDULE_WHATSAPP"
                ? { ...command, messageDraft: draftMessage }
                : command;
              if (!publicTurn) return;
              void apply({
                ...turn,
                response,
                publicTurn
              }, selectedFile, reviewedCommand);
            }}>
              {applying ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}{applying ? mutationProgress : isScheduledWhatsApp ? "Approve schedule" : isWhatsAppDraft ? "Approve & send" : isWhatsAppRouteConfiguration ? "Approve access" : "Approve"}
            </button>
            <button className="secondary-button" type="button" disabled={applying} onClick={() => cancel(turn)}><X size={16} aria-hidden="true" />Reject</button>
          </div>
        </div></>
      ) : null}
    </article>
  );
}

export function opsMutationProgressLabel(command: OpsAssistantMutationCommand): string {
  if (command.action === "DRAFT_WHATSAPP") return "Preparing to send…";
  if (command.action === "SCHEDULE_WHATSAPP") return "Scheduling…";
  if (command.action === "CONFIGURE_WHATSAPP_ROUTE") return "Saving WhatsApp access…";
  if (command.action === "CREATE") return "Adding task…";
  if (command.action === "TASK_RANGE") return "Changing Timeline…";
  return "Updating Work…";
}

function formatFileSize(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`;
  if (bytes < 1_024 * 1_024) return `${Math.ceil(bytes / 1_024)} KB`;
  return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
}

export function formatScheduledCommand(
  command: Extract<OpsAssistantMutationCommand, { action: "SCHEDULE_WHATSAPP" }>
): string {
  const label = new Intl.DateTimeFormat(undefined, {
    timeZone: command.schedule.timeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(new Date(command.schedule.executeAt));
  return `${label} · ${command.schedule.timeZone} · UTC${command.schedule.utcOffset}`;
}

/** Keep bounded authenticated dialogue server-side so "tell me more" refers
 * to the actual preceding turn. The complete current owner message remains in
 * the scoped conversation; only this model-history projection is bounded.
 * WhatsApp evidence never enters browser history and is retrieved again under
 * the current workspace boundary. */
export async function requestLunaOps(
  prompt: string,
  turns: readonly (Pick<Turn, "prompt" | "response" | "publicTurn"> & { id?: string })[],
  expectedReleaseId: OpsSha256,
  client: OpsAskClient = api
): Promise<OpsAssistantResponse> {
  const envelope = decodeOpsPublicTurnEnvelope(
    await client.askOps(prompt, opsRequestHistory(turns, expectedReleaseId)),
    expectedReleaseId
  );
  if (!envelope) throw new ApiError(502, "INVALID_RESPONSE", "Luna's response could not be verified for this release.");
  return envelope.result.response;
}

export function opsTurnPaintReceiptMatches(
  node: (Pick<HTMLElement, "dataset"> & {
    querySelector(selectors: string): { textContent: string | null } | null;
    querySelectorAll?(selectors: string): ArrayLike<{ textContent: string | null }>;
  }) | null,
  publicTurn: Pick<OpsPublicTurnEnvelope, "turnId" | "revision" | "answerHash" | "resultHash" | "result">
): boolean {
  if (node?.dataset.responseReady !== "true"
    || node.dataset.opsTurnId !== publicTurn.turnId
    || node.dataset.opsTurnRevision !== String(publicTurn.revision)
    || node.dataset.opsAnswerHash !== publicTurn.answerHash
    || node.dataset.opsResultHash !== publicTurn.resultHash
    || !["on", "off"].includes(node.dataset.opsPhonePrivacy ?? "")) return false;
  const visibleAnswer = node.dataset.opsAnswerPresentation === "claim-spans"
    ? node.querySelectorAll && Array.from(node.querySelectorAll('[data-ops-answer-text-part="true"]'))
      .map((part) => part.textContent ?? "").join("")
    : node.querySelector('[data-ops-visible-answer="true"]')?.textContent;
  if (visibleAnswer === undefined || visibleAnswer === null) return false;
  try {
    const canonicalAnswer = publicTurn.result.response.text;
    if (opsSha256Utf8(canonicalAnswer) !== publicTurn.answerHash) return false;
    // WHY [BOL-OPS-PRIVATE-PAINT-RECEIPT-001]: phone privacy deliberately makes
    // visible bytes differ from canonical stored bytes. Verify the declared
    // projection while retaining the canonical envelope hash as authority.
    // Falsifier: raw private text or a projection from another answer settles.
    // Inline proof labels are controls, not answer bytes. The exact typed
    // source-availability suffix is the only extra permitted visual projection;
    // always verify the full stored hash before accepting either presentation.
    const projectedAnswer = node.dataset.opsAnswerProjection === "business"
      ? opsAnswerPresentationText(publicTurn.result.response) : canonicalAnswer;
    const expectedPresentation = node.dataset.opsPhonePrivacy === "on"
      ? maskPersonalPhoneNumbers(projectedAnswer)
      : projectedAnswer;
    return visibleAnswer === expectedPresentation;
  } catch {
    return false;
  }
}

async function waitForOpsAnswerPaint(
  identity: string | OpsPublicTurnEnvelope,
  isCurrent: () => boolean
): Promise<boolean> {
  if (typeof document === "undefined" || typeof window === "undefined") return false;
  const publicTurn = typeof identity === "string"
    ? null
    : OpsPublicTurnEnvelopeSchema.safeParse(identity);
  if (publicTurn && !publicTurn.success) return false;
  for (let frame = 0; frame < 8; frame += 1) {
    await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
    if (!isCurrent()) return false;
    const turn = document.querySelector<HTMLElement>(`[data-ops-turn-id="${typeof identity === "string" ? identity : identity.turnId}"]`);
    const matches = publicTurn
      ? opsTurnPaintReceiptMatches(turn, publicTurn.data)
      : turn?.dataset.responseReady === "true";
    if (matches) {
      // One more frame makes the metric a painted user-visible answer, not only
      // a queued React state update. This adds at most one display frame before
      // TTS and makes final->visible/first-audio proof causally ordered.
      await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      if (!isCurrent()) return false;
      if (!publicTurn) return true;
      const painted = document.querySelector<HTMLElement>(`[data-ops-turn-id="${publicTurn.data.turnId}"]`);
      return opsTurnPaintReceiptMatches(painted, publicTurn.data);
    }
  }
  return false;
}

export function opsRequestHistory(
  turns: readonly (Pick<Turn, "prompt" | "response" | "publicTurn" | "historyVerified" | "cancelled" | "failed"> & { id?: string })[],
  expectedReleaseId?: OpsSha256 | null,
  currentRequestId?: string
): readonly OpsAssistantHistoryMessage[] {
  const finishTiming = beginOpsVoiceSyncTiming("requestHistory");
  try {
  // WHY [BOL-OPS-CURRENT-REQUEST-HISTORY-001]: an awaited file upload lets the
  // pending row reach turnsRef before HTTP dispatch. That exact request is
  // already request.text, not history. Exclude only its ID so latest authorized
  // context and distinct repeated prompts survive, including retries.
  const identifiedTurns = opsVisibleSessionTurns(turns
    .filter((turn) => currentRequestId === undefined || turn.id !== currentRequestId)
    .map((turn, index) => ({
    ...turn,
    id: turn.id ?? `legacy-history-${index}`
  })), undefined, expectedReleaseId);
  const messages = identifiedTurns.flatMap((turn): OpsAssistantHistoryMessage[] => {
    // A cancelled turn is visible local control history, not conversational
    // content to seed into Luna or resurrect as a clarification after reload.
    // Falsifier: Stop/barge makes a later short confirmation bind to an
    // unanswered cancelled prompt or reappears as assistant prose.
    if (turn.cancelled || turn.failed) return [];
    const publicTurn = turn.publicTurn && turn.id
      ? opsVisibleHistoryTurn({ ...turn, id: turn.id }, expectedReleaseId)
      : null;
    if (turn.publicTurn && !publicTurn) return [];
    const prompt = unicodeScalarPrefix(turn.prompt.trim(), MAX_USER_HISTORY_PROJECTION_SCALARS) ?? "";
    const canonicalResponse = publicTurn?.result.response ?? turn.response;
    const responseText = unicodeScalarPrefix(
      canonicalResponse?.text ?? "",
      MAX_ASSISTANT_HISTORY_PROJECTION_SCALARS
    ) ?? "";
    return [
      ...(prompt ? [{ role: "USER" as const, text: prompt }] : []),
      ...(responseText ? [{
        role: "ASSISTANT" as const,
        ...(canonicalResponse?.kind ? { kind: canonicalResponse.kind } : {}),
        text: responseText
      }] : [])
    ];
  });
  return messages.slice(-12);
  } finally { finishTiming?.(); }
}

export async function copyOpsAnswer(text: string, options: {
  clipboard?: Pick<Clipboard, "writeText"> | null;
  maskPhones?: boolean;
} = {}): Promise<boolean> {
  const clipboard = options.clipboard
    ?? (typeof navigator === "undefined" ? null : navigator.clipboard);
  const maskPhones = options.maskPhones ?? privateModePhoneMaskingFromRoot(
    typeof document === "undefined" ? null : document.getElementById("root")
  );
  if (!clipboard || typeof clipboard.writeText !== "function") return false;
  try {
    // WHY [BOL-OPS-PRIVATE-COPY-001]: installing a global clipboard wrapper can
    // fail on a hardened browser object. This button owns its export boundary;
    // the wrapper remains defense in depth, never the only phone mask.
    // Falsifier: Copy writes raw private answer bytes when wrapping fails.
    await clipboard.writeText(opsPrivatePresentationText(text, maskPhones));
    return true;
  } catch {
    return false;
  }
}

const AUTHENTICATED_HISTORY_TERMINAL_STATES = new Set<OpsPublicProposalProjection["state"]>([
  "SUCCEEDED",
  "CANCELLED",
  "FAILED",
  "DELIVERY_UNCERTAIN"
]);

function hasAuthenticatedHistoryTerminalReceipt(
  proposal: OpsPublicProposalProjection
): boolean {
  return AUTHENTICATED_HISTORY_TERMINAL_STATES.has(proposal.state)
    && proposal.effectReceipt !== undefined;
}

// WHY [BOL-ONE-PROPOSAL-RENDER-001]: React keys only identify siblings; they
// cannot merge a command card with the autonomous feed below it. Give each
// proposal or server-declared bundle to its latest chat answer, then let the
// queue exclude every member while that one compact unit owns the decision.
// Falsifier: either half exposes a second preview/decision, or a title/Work ID
// rather than approvalBundle identity changes ownership.
export function proposalRenderOwners(
  turns: readonly Pick<Turn, "id" | "response" | "publicTurn" | "historyVerified">[],
  groups: readonly OpsProposalRenderGroup[] = [],
  currentReviewProposalIds: ReadonlySet<string> = NO_CURRENT_REVIEW_PROPOSAL_IDS
): ReadonlyMap<string, string> {
  const groupByProposalId = proposalRenderGroupByProposalId(groups);
  const owners = new Map<string, string>();
  for (const turn of turns) {
    const historyPublicTurn = turn.historyVerified ? opsVisibleHistoryTurn(turn) : null;
    const response = turn.publicTurn
      ? (historyPublicTurn ?? opsVisiblePublicTurn(turn))?.result.response
      : turn.response;
    const historicalTerminalProposalIds = new Set(
      historyPublicTurn?.result.proposals
        .filter(hasAuthenticatedHistoryTerminalReceipt)
        .map((proposal) => proposal.id) ?? []
    );
    if (response?.kind !== "ANSWER") continue;
    for (const id of new Set(response.proposalIds ?? [])) {
      const group = groupByProposalId.get(id);
      const proposalIds = group?.proposalIds ?? [id];
      // WHY [BOL-OPS-HISTORY-CURRENT-REVIEW-001]: authenticated Chat history
      // cannot authorize controls. A complete exact terminal receipt set is
      // immutable result evidence and may remain visible without /review;
      // otherwise every member still needs the current authenticated Review row
      // that supplies editable revision/CAS authority. Prefer that row if it is
      // present. Falsifier: reload hides a complete terminal receipt, or a stale,
      // nonterminal, or partial projection regains controls/displaces Review.
      if (turn.historyVerified) {
        const ownsCompleteTerminalReceipt = proposalIds.every((proposalId) =>
          historicalTerminalProposalIds.has(proposalId)
            && !currentReviewProposalIds.has(proposalId));
        if (!ownsCompleteTerminalReceipt
          && proposalIds.some((proposalId) => !currentReviewProposalIds.has(proposalId))) continue;
      }
      if (group) {
        for (const proposalId of group.proposalIds) owners.set(proposalId, turn.id);
      } else {
        owners.set(id, turn.id);
      }
    }
  }
  return owners;
}

// WHY [BOL-OPS-INLINE-EXACT-PROJECTION-001]: /api/review history is a bounded
// activity window, while the immutable public turn names the exact proposal
// state/receipt that its visible answer owns. A fresh release-valid turn may
// supply its full projection; authenticated history may supply only structurally
// terminal receipts absent from current Review. That narrow set has no mutation
// authority and cannot trigger an exact lookup. Falsifier: reload omits a
// complete receipt, or stale/nonterminal history supplies a control or lookup.
export function publicProposalProjectionsForTurn(
  turn: Pick<Turn, "id" | "response" | "publicTurn" | "historyVerified">,
  expectedReleaseId?: OpsSha256 | null,
  currentReviewProposalIds: ReadonlySet<string> = NO_CURRENT_REVIEW_PROPOSAL_IDS
): readonly OpsPublicProposalProjection[] {
  if (!turn.publicTurn) return [];
  if (turn.historyVerified) {
    return (opsVisibleHistoryTurn(turn, expectedReleaseId)?.result.proposals ?? [])
      .filter((proposal) => hasAuthenticatedHistoryTerminalReceipt(proposal)
        && !currentReviewProposalIds.has(proposal.id));
  }
  return opsVisiblePublicTurn(turn, expectedReleaseId)?.result.proposals ?? [];
}

export function proposalRenderIdsForTurn(
  turn: Pick<Turn, "id" | "response" | "publicTurn" | "historyVerified">,
  owners: ReadonlyMap<string, string>,
  groups: readonly OpsProposalRenderGroup[] = []
): string[] {
  const response = turn.publicTurn ? opsVisiblePublicTurn(turn)?.result.response : turn.response;
  if (response?.kind !== "ANSWER") return [];
  const groupByProposalId = proposalRenderGroupByProposalId(groups);
  const rendered = new Set<string>();
  for (const id of new Set(response.proposalIds ?? [])) {
    const group = groupByProposalId.get(id);
    const renderId = group?.proposalIds[0] ?? id;
    if (owners.get(renderId) === turn.id) rendered.add(renderId);
  }
  return [...rendered];
}

function proposalRenderGroupByProposalId(
  groups: readonly OpsProposalRenderGroup[]
): ReadonlyMap<string, OpsProposalRenderGroup> {
  const byProposalId = new Map<string, OpsProposalRenderGroup>();
  for (const group of groups) {
    for (const proposalId of new Set(group.proposalIds)) byProposalId.set(proposalId, group);
  }
  return byProposalId;
}

export function restoreOpsConversation(
  store: StorageLike | null,
  scope: string,
  now = Date.now(),
  expectedReleaseId?: OpsSha256 | null
): { draft: string; turns: Turn[]; clearedAt?: string | null } {
  if (!store) return { draft: "", turns: [] };
  try {
    const parsed = JSON.parse(store.getItem(storageKey(scope)) ?? "null") as StoredConversation | null;
    if (!parsed || ![1, 2].includes(parsed.version) || typeof parsed.expiresAt !== "number" || parsed.expiresAt <= now || !Array.isArray(parsed.turns)) {
      store.removeItem(storageKey(scope));
      return { draft: "", turns: [] };
    }
    const storedTurns = parsed.turns.slice(-MAX_TURNS);
    const turns = storedTurns.flatMap((turn, index): Turn[] => {
      if (!turn || typeof turn.id !== "string" || typeof turn.prompt !== "string" || turn.prompt.length > MAX_OWNER_MESSAGE_CHARS) return [];
      if (parsed.version === 2 && "presentationOnly" in turn) {
        const messages = safeStoredProgressMessages(turn.messages);
        if (turn.presentationOnly !== true || messages.length === 0
          || (turn.stopped !== undefined && turn.stopped !== true)) return [];
        // WHY [BOL-VOICE-READY-ORDER-UPGRADE-001]: R552 saved real welcome
        // rows without placement metadata. Leaving them unanchored also moves
        // a new welcome chained to one below future replies. Preserve the
        // already-saved position of that exact producer identity; do not clear
        // history or invent its original timing. This is presentation only.
        // Falsifier: a retained pre-anchor cache puts an old/new welcome after
        // a later answer, or a non-welcome presentation acquires this placement.
        const previousStoredId = index === 0 ? null : storedTurns[index - 1]?.id;
        const legacyWelcome = turn.presentationKind === undefined && turn.prompt === ""
          && turn.stopped === undefined && turn.id.endsWith(":voice-ready-welcome")
          && (previousStoredId === null || typeof previousStoredId === "string");
        // WHY [BOL-OPS-PROGRESS-LOCAL-DURABILITY-001]: a reload, Stop, or
        // transport failure can occur after a real server progress row was
        // painted (Text or Voice) before any canonical public-turn ledger exists. Restore
        // only that bounded presentation as settled local continuity after the
        // authenticated history read; it has no answer/history/effect authority.
        // Falsifier: the row disappears, resumes a spinner/request, enters model
        // history, or overrides a server-owned final for the same turn.
        return [{
          id: turn.id,
          prompt: turn.prompt,
          response: null,
          spokenChatMessages: messages,
          textProgressPresentation: true,
          cancelled: true,
          localPresentationHistory: true,
          ...(turn.presentationKind === "voice-ready-welcome"
            && (turn.afterTurnId === null || typeof turn.afterTurnId === "string")
            ? {
              localPresentationKind: "voice-ready-welcome" as const,
              localPresentationAfterTurnId: turn.afterTurnId
            } : legacyWelcome ? {
              localPresentationKind: "voice-ready-welcome" as const,
              localPresentationAfterTurnId: previousStoredId
            } : {}),
          ...(turn.stopped === true ? { cancellationOwner: "TEXT_STOP" as const } : {})
        }];
      }
      if (parsed.version === 2 && "publicTurn" in turn && turn.publicTurn !== undefined) {
        const publicTurn = OpsPublicTurnEnvelopeSchema.safeParse(turn.publicTurn);
        if (!publicTurn.success
          || publicTurn.data.turnId !== turn.id
          || expectedReleaseId === null
          || (expectedReleaseId !== undefined && publicTurn.data.releaseId !== expectedReleaseId)
          || !opsPublicResponseMatches(publicTurn.data.result.response, turn.response)) return [];
        return [{
          id: publicTurn.data.turnId,
          prompt: turn.prompt,
          response: publicTurn.data.result.response as OpsAssistantResponse,
          publicTurn: publicTurn.data,
          ...(safeStoredProgressMessages(turn.messages).length
            ? { spokenChatMessages: safeStoredProgressMessages(turn.messages) } : {})
        }];
      }
      // WHY [BOL-OPS-HISTORY-RELEASE-002]: production always supplies an exact
      // release authority (or explicit null while unavailable). In that mode a
      // legacy/unbound response is continuity data, never verified visible
      // history. Cross-release display requires the authenticated history read,
      // not a self-consistent cached hash. Falsifier: local cache alone becomes
      // authorized history, or callers that omit validation lose legacy
      // compatibility.
      if (expectedReleaseId !== undefined) return [];
      if (!("response" in turn)) return [];
      const response = safeStoredResponse(turn.response);
      return response ? [{ id: turn.id, prompt: turn.prompt, response }] : [];
    });
    return {
      draft: typeof parsed.draft === "string" ? parsed.draft.slice(0, MAX_OWNER_MESSAGE_CHARS) : "", turns,
      ...(parsed.version === 2 && (parsed.clearedAt === null || typeof parsed.clearedAt === "string")
        ? { clearedAt: parsed.clearedAt } : {})
    };
  } catch {
    store.removeItem(storageKey(scope));
    return { draft: "", turns: [] };
  }
}

// WHY [BOL-OPS-RELEASE-HISTORY-001]: a same-member reload after API cutover
// dropped the old release, then saved an empty conversation over its cache.
// Only the authenticated history read can authorize historical display. Keep
// its original envelope and authenticated durable prompt. Legacy cached text
// still requires the server-owned hash. Neither grants fresh controls or
// invents a missing old prompt.
export async function loadOpsHistoryForScope(
  read: () => Promise<unknown>,
  scope: string,
  cache: readonly Turn[],
  isCurrent: () => boolean
): Promise<Turn[] | null> {
  // WHY [BOL-OPS-HISTORY-ADMISSION-001]: SSR/restored-row proof missed the
  // mounted fetch interval: Text and Voice could start with empty history.
  // A deferred, failed or retired read grants no entry. Falsifier: a dispatch
  // consumes a draft/history before this current authenticated read succeeds.
  const value = await read();
  if (!isCurrent()) return null;
  const history = rehydrateOpsHistory(value, scope, cache);
  if (!history) throw new Error("Conversation history could not load.");
  return history;
}

export function rehydrateOpsHistory(value: unknown, scope: string, cache: readonly Turn[]): Turn[] | null {
  if (!value || typeof value !== "object") return null;
  const payload = value as { scope?: unknown; turns?: unknown };
  if (payload.scope !== scope || !Array.isArray(payload.turns) || payload.turns.length > MAX_TURNS) return null;
  const turns: Turn[] = [];
  for (const entry of payload.turns) {
    if (!entry || typeof entry !== "object" || typeof entry.promptHash !== "string"
      || !/^[a-f0-9]{64}$/u.test(entry.promptHash)) return null;
    const parsed = OpsPublicTurnEnvelopeSchema.safeParse(entry.publicTurn);
    if (!parsed.success) return null;
    const cached = cache.find((turn) => turn.id === parsed.data.turnId);
    let prompt = "";
    try {
      // WHY [BOL-OPS-DURABLE-PROMPT-001]: cacheless reload previously sent
      // 12 ASSISTANT and 0 USER messages to live J1. Server-owned text now
      // restores both roles; only legacy entries may use hash-checked cache.
      if (entry.prompt !== undefined) {
        if (typeof entry.prompt !== "string" || !entry.prompt.length
          || entry.prompt.length > MAX_OWNER_MESSAGE_CHARS || entry.prompt.trim() !== entry.prompt
          || opsSha256Utf8(entry.prompt.normalize("NFKC")) !== `sha256:${entry.promptHash}`) return null;
        prompt = entry.prompt;
      } else if (cached && opsSha256Utf8(cached.prompt.normalize("NFKC")) === `sha256:${entry.promptHash}`) {
        prompt = cached.prompt;
      }
    } catch {
      if (entry.prompt !== undefined) return null;
      // Malformed legacy cached text cannot become historical prompt truth.
    }
    const progressPresentations = Array.isArray(entry.progressPresentations)
      && entry.progressPresentations.every((message: unknown) => typeof message === "string"
        && message.length > 0 && message.length <= 20_000 && !/[\r\n]/u.test(message))
      ? entry.progressPresentations as string[] : [];
    const cachedMessages = cached?.publicTurn
      && cached.publicTurn.requestId === parsed.data.requestId
      && cached.publicTurn.releaseId === parsed.data.releaseId
      && cached.publicTurn.answerHash === parsed.data.answerHash
      ? safeStoredProgressMessages(cached.spokenChatMessages) : [];
    // Cache fills only the paint-before-durable-receipt window for this exact
    // authenticated turn. A server prefix conflict wins; cached presentation
    // never enters model history, hashes, facts, or fresh approval capability.
    const presentationMessages = cachedMessages.length > progressPresentations.length
      && progressPresentations.every((message, index) => cachedMessages[index] === message)
      ? cachedMessages : progressPresentations;
    const orderKey = typeof entry.orderKey === "string"
      && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{6}Z:[a-f0-9-]{36}$/u.test(entry.orderKey)
      ? entry.orderKey : undefined;
    turns.push({ id: parsed.data.turnId, prompt, response: parsed.data.result.response,
      publicTurn: parsed.data, historyVerified: true,
      attachments: parseSharedAttachments(entry.attachments),
      ...(orderKey ? { historyOrderKey: orderKey } : {}),
      ...(presentationMessages.length ? { spokenChatMessages: presentationMessages } : {}) });
  }
  return opsVisibleSessionTurns(turns);
}

export function mergeOpsHistory(
  current: readonly Turn[],
  history: readonly Turn[],
  cache: readonly Turn[] = [],
  direction: "latest" | "earlier" = "latest"
): Turn[] {
  // A late history read cannot replace a newly submitted or freshly settled
  // turn, resurrect its request, or revoke its active-session capability.
  const currentIds = new Set(current.map((turn) => turn.id));
  const historyIds = new Set(history.map((turn) => turn.id));
  const cachedPresentations = cache.filter((turn) => turn.localPresentationHistory === true
    && !currentIds.has(turn.id) && !historyIds.has(turn.id));
  const active = [...cachedPresentations, ...current.filter((turn) => !turn.historyVerified)];
  const activeIds = new Set(active.map((turn) => turn.id));
  // WHY [BOL-OPS-HISTORY-PAGES-001]: the previous merge silently discarded all
  // but the newest 20 rows even after an authorized earlier-page read. Keep
  // explicitly loaded pages; only transport pages, cache and model projection
  // remain bounded. Fresh local turns retain their capability/cancel ownership.
  if (direction === "earlier") {
    return orderOpsHistory([...history.filter((turn) => !currentIds.has(turn.id)), ...current.map(turn => {
      const incoming = history.find(item => item.id === turn.id);
      return incoming ? { ...turn, attachments: reconcileSharedAttachments(turn.attachments, incoming.attachments) } : turn;
    })]);
  }
  return orderOpsHistory([
    ...current.filter((turn) => turn.historyVerified && !historyIds.has(turn.id)),
    ...history.map((turn) => {
      const fresh = activeIds.has(turn.id) ? active.find((item) => item.id === turn.id)! : null;
      if (fresh?.failed === true && !fresh.publicTurn
        && turn.historyVerified === true && Boolean(turn.publicTurn)) {
        // WHY [BOL-VOICE-LATE-CANONICAL-FINAL-001]: an admitted Voice turn can
        // outlive its Live presentation transport. The old fatal path painted a
        // local same-ID FAILURE, and generic fresh-row precedence then hid the
        // authenticated final forever. Only that non-authoritative failure may
        // yield to a same-ID verified envelope. Preserve a longer compatible
        // progress prefix for paint continuity; never promote it to history,
        // model, fact, effect, approval, or ordering authority.
        // Falsifier: a pending/fresh settled row is overwritten, conflicting
        // local text beats server text, progress vanishes, or order keys move.
        const freshMessages = safeStoredProgressMessages(fresh.spokenChatMessages);
        const historyMessages = safeStoredProgressMessages(turn.spokenChatMessages);
        const presentationMessages = freshMessages.length > historyMessages.length
          && historyMessages.every((message, index) => freshMessages[index] === message)
          ? freshMessages : historyMessages;
        return {
          ...turn,
          ...(presentationMessages.length ? { spokenChatMessages: presentationMessages } : {})
        };
      }
      return fresh ? { ...fresh,
        attachments: fresh.publicTurn && turn.publicTurn && (fresh.publicTurn.requestId !== turn.publicTurn.requestId
          || fresh.publicTurn.releaseId !== turn.publicTurn.releaseId || fresh.publicTurn.revision > turn.publicTurn.revision)
          ? (fresh.attachments ?? []).map(item => ({ sourceEventId: item.sourceEventId, available: false as const }))
          : reconcileSharedAttachments(fresh.attachments, turn.attachments),
        ...(turn.historyOrderKey ? { historyOrderKey: turn.historyOrderKey } : {}) } : turn;
    }),
    ...active.filter((turn) => !historyIds.has(turn.id))
  ]);
}

const OPS_NATIVE_VOICE_TURN_ID_PATTERN = /^ops-voice-(\d+)-(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[a-z0-9]+)$/u;

function opsNativeVoiceCreationMs(id: string): number | null {
  const baseId = id.endsWith(":voice-ready-welcome")
    ? id.slice(0, -":voice-ready-welcome".length) : id;
  const match = OPS_NATIVE_VOICE_TURN_ID_PATTERN.exec(baseId);
  if (!match) return null;
  const createdAtMs = Number(match[1]);
  return Number.isSafeInteger(createdAtMs) && createdAtMs > 0 ? createdAtMs : null;
}

function orderOpsHistory(turns: readonly Turn[]): Turn[] {
  const visible = opsVisibleSessionTurns(turns);
  const ordered = visible.filter((turn) => turn.historyOrderKey)
    .sort((left, right) => left.historyOrderKey! < right.historyOrderKey! ? -1 : left.historyOrderKey! > right.historyOrderKey! ? 1 : 0);
  if (!ordered.length) return visible;
  // WHY [BOL-VOICE-READY-ORDER-001]: authenticated history has a server order
  // key, while a receiver-started welcome is intentionally local and has no
  // server row. Appending every unkeyed row after the keyed page moved an
  // earlier real welcome below a later answer in the live desktop chat. Only
  // the local welcome kind gets its producer-captured local anchor; ordinary
  // pending/unkeyed turns retain the existing tail behavior. A legacy native
  // Voice presentation, or a settled live native Voice turn that already
  // painted an acknowledgment, has an exact producer-owned creation time and
  // may be placed before a later authenticated native Voice peer. This keeps
  // an interrupted question/ACK ahead of the result which finished after it;
  // it does not infer chronology for active, silent, typed, or unknown rows.
  // WHY [BOL-VOICE-SETTLED-LOCAL-ORDER-001]: a photographed completed native
  // answer and its explicit Stop had no public ledger rows, so refresh sorted a
  // later keyed PDF ahead of both. Their native IDs still own producer time.
  // Admit only a voice-sourced final or producer-receipted explicit Stop to the
  // existing display placement; never give either a server key, verified history,
  // model/fact/effect authority, or broaden placement to pending/typed rows.
  // Falsifier: a text UUID or active pending turn moves, a valid welcome anchor
  // moves, keyed-keyed order changes, or a local row gains history authority.
  const nativeVoicePlacement = new Map<string, Turn[]>();
  const nativeVoicePlacementIds = new Set<string>();
  for (const turn of visible) {
    const retainedPresentation = turn.localPresentationHistory === true;
    const settledLivePresentation = turn.cancelled === true
      && Boolean(turn.spokenChatMessages?.length);
    const settledNativeVoiceFinal = turn.realtimeInputSource === "voice"
      && turn.realtimeFinal === true && !turn.publicTurn;
    const settledNativeVoiceStop = turn.realtimeInputSource === "voice"
      && turn.cancelled === true && turn.cancellationOwner === "VOICE_STOP"
      && !turn.response && !turn.spokenChatMessages?.length;
    if ((!retainedPresentation && !settledLivePresentation
        && !settledNativeVoiceFinal && !settledNativeVoiceStop)
      || turn.historyOrderKey
      || turn.localPresentationKind === "voice-ready-welcome"
      || turn.id.endsWith(":voice-ready-welcome")) continue;
    const createdAtMs = opsNativeVoiceCreationMs(turn.id);
    if (createdAtMs === null) continue;
    // `mergeOpsHistory` deliberately keeps a matching fresh turn's live
    // capability fields and copies only the authenticated server order key.
    // Membership in `ordered` is therefore the chronology proof here; also
    // requiring historyVerified rejects that exact live-then-durable seam.
    const laterPeer = ordered.find((peer) => opsNativeVoiceCreationMs(peer.id) !== null
      && opsNativeVoiceCreationMs(peer.id)! > createdAtMs);
    if (!laterPeer) continue;
    const group = nativeVoicePlacement.get(laterPeer.id) ?? [];
    group.push(turn);
    nativeVoicePlacement.set(laterPeer.id, group);
    nativeVoicePlacementIds.add(turn.id);
  }
  const anchored = visible.filter((turn) => turn.localPresentationHistory === true
    && !turn.historyOrderKey && turn.localPresentationKind === "voice-ready-welcome"
    && turn.localPresentationAfterTurnId !== undefined);
  if (!anchored.length && !nativeVoicePlacementIds.size) {
    return [...ordered, ...visible.filter((turn) => !turn.historyOrderKey)];
  }
  const anchoredIds = new Set(anchored.map((turn) => turn.id));
  const base = [...ordered, ...visible.filter((turn) => !turn.historyOrderKey
    && !anchoredIds.has(turn.id)
    && !nativeVoicePlacementIds.has(turn.id))];
  const baseIds = new Set([...base.map((turn) => turn.id), ...nativeVoicePlacementIds]);
  const byAnchor = new Map<string | null, Turn[]>();
  for (const turn of anchored) {
    const anchor = turn.localPresentationAfterTurnId!;
    if (anchor !== null && !baseIds.has(anchor) && !anchoredIds.has(anchor)) {
      continue;
    }
    const group = byAnchor.get(anchor) ?? [];
    group.push(turn);
    byAnchor.set(anchor, group);
  }
  const emitted = new Set<string>();
  const appendAfter = (anchor: string | null, output: Turn[], trail: Set<string>): void => {
    for (const turn of byAnchor.get(anchor) ?? []) {
      if (emitted.has(turn.id) || trail.has(turn.id)) continue;
      emitted.add(turn.id);
      output.push(turn);
      const nextTrail = new Set(trail).add(turn.id);
      appendAfter(turn.id, output, nextTrail);
    }
  };
  const result: Turn[] = [];
  appendAfter(null, result, new Set());
  for (const turn of base) {
    for (const presentation of nativeVoicePlacement.get(turn.id) ?? []) {
      if (emitted.has(presentation.id)) continue;
      emitted.add(presentation.id);
      result.push(presentation);
      appendAfter(presentation.id, result, new Set());
    }
    result.push(turn);
    appendAfter(turn.id, result, new Set());
  }
  const anchoredResult = [...result, ...anchored.filter((turn) => !emitted.has(turn.id))];
  // WHY [BOL-VOICE-WELCOME-PRODUCER-ORDER-001]: three native Voice welcomes
  // which existed before a later request were photographed below that request
  // after history reconciliation. The phone's exact anchor identity is not
  // recoverable: a pruned anchor and a still-present unkeyed tail anchor both
  // reach that result. Keep the explicit anchor whenever it already places the
  // welcome before a later keyed native peer. Only when that final placement
  // contradicts both producer IDs, move the presentation before the earliest
  // such peer. This does not assign history authority, reorder keyed peers, or
  // infer chronology for unknown/non-native presentation IDs.
  const anchoredResultIndex = new Map(anchoredResult.map((turn, index) => [turn.id, index]));
  const displacedByLaterPeer = new Map<string, Turn[]>();
  const displacedIds = new Set<string>();
  for (const turn of anchoredResult) {
    if (turn.localPresentationKind !== "voice-ready-welcome") continue;
    const createdAtMs = opsNativeVoiceCreationMs(turn.id);
    if (createdAtMs === null) continue;
    const laterPeer = ordered.find((peer) => {
      const peerCreatedAtMs = opsNativeVoiceCreationMs(peer.id);
      return peerCreatedAtMs !== null && peerCreatedAtMs > createdAtMs;
    });
    if (!laterPeer) continue;
    const presentationIndex = anchoredResultIndex.get(turn.id);
    const peerIndex = anchoredResultIndex.get(laterPeer.id);
    if (presentationIndex === undefined || peerIndex === undefined
      || presentationIndex < peerIndex) continue;
    const group = displacedByLaterPeer.get(laterPeer.id) ?? [];
    group.push(turn);
    displacedByLaterPeer.set(laterPeer.id, group);
    displacedIds.add(turn.id);
  }
  if (!displacedIds.size) return anchoredResult;
  const repaired: Turn[] = [];
  for (const turn of anchoredResult) {
    if (displacedIds.has(turn.id)) continue;
    repaired.push(...(displacedByLaterPeer.get(turn.id) ?? []), turn);
  }
  return repaired;
}

export function persistOpsConversation(
  store: StorageLike | null,
  scope: string,
  draft: string,
  turns: readonly Turn[],
  now = Date.now(),
  expectedReleaseId?: OpsSha256 | null,
  preserveUnverifiedHistory = false,
  clearedAt?: string | null
): void {
  const finishTiming = beginOpsVoiceSyncTiming("persistConversation");
  try {
  if (!store) return;
  let storedTurns: StoredConversationV2["turns"] = opsVisibleSessionTurns(turns, undefined, expectedReleaseId)
    .slice(-MAX_TURNS)
    .flatMap((turn): StoredConversationV2["turns"] => {
      const progressMessages = safeStoredProgressMessages(turn.spokenChatMessages);
      if ((turn.cancelled || turn.failed || turn.response === null)
        && progressMessages.length) {
        return [{
          id: turn.id,
          prompt: turn.prompt.slice(0, MAX_OWNER_MESSAGE_CHARS),
          presentationOnly: true,
          messages: progressMessages,
          ...(turn.localPresentationKind === "voice-ready-welcome"
            && (turn.localPresentationAfterTurnId === null || typeof turn.localPresentationAfterTurnId === "string")
            ? {
              presentationKind: "voice-ready-welcome" as const,
              afterTurnId: turn.localPresentationAfterTurnId
            } : {}),
          ...(turn.cancellationOwner === "TEXT_STOP" ? { stopped: true as const } : {})
        }];
      }
      // WHY [BOL-VOICE-BACK-PRESENTATION-CACHE-001]: R411's final survived
      // Open message/Back, but finalized cache rows omitted the ACK/progress.
      // Retain their bounded presentation separately; authenticated hydration
      // admits only this exact turn's non-conflicting prefix. Never restore a
      // request, capability, model context, or hash authority from these strings.
      // Pending, cancelled and failed rows remain local control state, never a
      // durable assistant answer. Only separately validated progress presentation
      // survives; no request, failure, or fabricated response is replayed.
      if (turn.cancelled || turn.failed || turn.response === null) return [];
      // Apply the same release fence on write so an in-flight/unverified turn
      // cannot become durable authority merely because the browser reloads.
      if (expectedReleaseId !== undefined && !turn.publicTurn) return [];
      const publicTurn = opsVisibleHistoryTurn(turn, expectedReleaseId);
      if (turn.publicTurn && !publicTurn) return [];
      return [{
        id: publicTurn?.turnId ?? turn.id,
        prompt: turn.prompt.slice(0, MAX_OWNER_MESSAGE_CHARS),
        response: publicTurn?.result.response ?? storableResponse(turn.response),
        ...(publicTurn ? { publicTurn } : {}),
        ...(progressMessages.length ? { messages: progressMessages } : {})
      }];
    });
  let cacheBoundary = clearedAt;
  if (preserveUnverifiedHistory) {
    // Unavailable/failed hydration is not permission to erase prior cache.
    // These rows remain untrusted on reload; restore never copies capability
    // flags and authenticated hydration always supplies the visible envelope.
    try {
      const previous = JSON.parse(store.getItem(storageKey(scope)) ?? "null") as StoredConversation | null;
      if (previous && [1, 2].includes(previous.version) && previous.expiresAt > now && Array.isArray(previous.turns)) {
        const replacedIds = new Set(storedTurns.map((turn) => turn.id));
        storedTurns = [...previous.turns.filter((turn) => turn && !replacedIds.has(turn.id)), ...storedTurns].slice(-MAX_TURNS);
        // WHY [BOL-CLEAR-CHAT-PRESENTATION-002]: mount persists the draft before
        // history returns. Relabeling old rows with a not-yet-authorized boundary
        // would either erase new speech or resurrect pre-clear presentation.
        cacheBoundary = previous.version === 2 ? previous.clearedAt : undefined;
      }
    } catch { /* Preserve current verified turns without trusting malformed cache. */ }
  }
  store.setItem(storageKey(scope), JSON.stringify({
    version: 2,
    expiresAt: now + HISTORY_TTL_MS,
    draft: draft.slice(0, MAX_OWNER_MESSAGE_CHARS),
    ...(cacheBoundary === null || typeof cacheBoundary === "string" ? { clearedAt: cacheBoundary } : {}),
    turns: storedTurns
  } satisfies StoredConversationV2));
  } finally { finishTiming?.(); }
}

function safeStoredProgressMessages(value: unknown): string[] {
  if (!Array.isArray(value) || value.length === 0) return [];
  // Continuity cache retains the full delivered prefix. Existing Storage quota
  // remains a cache-only safeguard; authenticated history is durable.
  const newest = value;
  if (!newest.every((message: unknown) => typeof message === "string"
      && message.length > 0 && message.length <= 20_000
      && isUnicodeScalarTextWithinLength(message, 20_000)
      && !/[\r\n]/u.test(message)
      && !/\b(?:api[-_ ]?key|password|secret|access[-_ ]?token)\s*=\s*(?:"[^"\r\n]+"|'[^'\r\n]+'|[^\s,;]{4,})/iu.test(message)
      && !/\b(?:api[-_ ]?key|password|secret|access[-_ ]?token)\s*:\s*(?:"[^"\r\n]+"|'[^'\r\n]+')/iu.test(message)
      && !/\bauthorization\s*[:=]\s*(?:bearer|basic)\s+[A-Z0-9._~+/=-]{8,}/iu.test(message))) return [];
  return newest as string[];
}

function storableResponse(response: OpsAssistantResponse | null): Exclude<OpsAssistantResponse, { kind: "MUTATION_PREVIEW" }> {
  if (!response || response.kind === "MUTATION_PREVIEW") {
    return {
      kind: "CLARIFICATION",
      text: response ? "A change was prepared but not applied. Ask again to check the current board." : "This request did not finish. Send it again.",
      operatingModelVersion: response?.operatingModelVersion ?? 0
    };
  }
  return response;
}

function safeStoredResponse(value: unknown): Exclude<OpsAssistantResponse, { kind: "MUTATION_PREVIEW" }> | null {
  if (!value || typeof value !== "object") return null;
  const response = value as Record<string, unknown>;
  if (typeof response.text !== "string" || response.text.length > MAX_STORED_ANSWER_CHARS || typeof response.operatingModelVersion !== "number") return null;
  if (response.kind === "CLARIFICATION") return { kind: "CLARIFICATION", text: response.text, operatingModelVersion: response.operatingModelVersion };
  if (response.kind === "FAILURE") return { kind: "FAILURE", text: response.text, operatingModelVersion: response.operatingModelVersion };
  if (response.kind === "IMAGE_JOB") {
    const job = OpsImageJobProjectionSchema.safeParse(response.job);
    return job.success
      ? { kind: "IMAGE_JOB", text: response.text, job: job.data, operatingModelVersion: response.operatingModelVersion }
      : null;
  }
  // A valid answer is one canonical projection: parsing the whole value keeps
  // channel/action identity as well as claim/source/highlight bytes. Legacy
  // field-by-field salvage below exists only for older stored values.
  if (response.kind === "ANSWER") {
    const parsed = OpsAssistantPublicResponseSchema.safeParse(value);
    if (parsed.success && parsed.data.kind === "ANSWER") return parsed.data;
    // Field-by-field salvage could drop one claim highlight or channel/action,
    // keep its global source union, and repaint an unverifiable answer.
    if (Object.prototype.hasOwnProperty.call(response, "evidenceClaims")) return null;
  }
  if (response.kind !== "ANSWER" || !["Current board", "Business rules", "Business sources", "WhatsApp sources", "WhatsApp route", "Web sources", "Google Workspace", "Google Meet transcript"].includes(String(response.evidenceLabel)) || !Array.isArray(response.workItemIds)) return null;
  // WHY [BOL-OPS-STORED-PROOF-PARITY-001]: the live response admitted complete
  // File/voice/text proof, but a reload silently kept only three short non-File
  // citations and ten Work IDs. Restore the same closed public envelope as the
  // canonical response; reject malformed entries instead of truncating valid
  // proof at a second UI seam. Falsifier: reload changes a valid answer's proof
  // union, kind, excerpt, Work/proposal references, or visible claim text.
  const workItemIds = response.workItemIds.filter((id): id is string => typeof id === "string" && /^[0-9a-f-]{36}$/i.test(id)).slice(0, MAX_STORED_WORK_IDS);
  const proposalIds = Array.isArray(response.proposalIds)
    ? response.proposalIds.filter((id): id is string => typeof id === "string" && /^[0-9a-f-]{36}$/i.test(id)).slice(0, MAX_STORED_PROPOSAL_IDS)
    : undefined;
  const evidenceSources = Array.isArray(response.evidenceSources)
    ? response.evidenceSources.flatMap((value): NonNullable<Extract<OpsAssistantResponse, { kind: "ANSWER" }>["evidenceSources"]>[number][] => {
        if (!value || typeof value !== "object") return [];
        const source = value as Record<string, unknown>;
        if (typeof source.sourceEventId !== "string" || !/^[0-9a-f-]{36}$/i.test(source.sourceEventId)
          || typeof source.conversationLabel !== "string" || source.conversationLabel.length > 100
          || typeof source.senderLabel !== "string" || source.senderLabel.length > 80
          || typeof source.occurredAt !== "string" || !Number.isFinite(Date.parse(source.occurredAt))
          || !["TEXT", "VOICE_TRANSCRIPT", "FILE"].includes(String(source.kind))
          || typeof source.excerpt !== "string" || source.excerpt.length > MAX_STORED_EVIDENCE_EXCERPT_CHARS) return [];
        return [{
          sourceEventId: source.sourceEventId,
          conversationLabel: source.conversationLabel,
          senderLabel: source.senderLabel,
          occurredAt: source.occurredAt,
          kind: source.kind as "TEXT" | "VOICE_TRANSCRIPT" | "FILE",
          excerpt: source.excerpt
        }];
      }).slice(0, MAX_STORED_EVIDENCE_SOURCES)
    : undefined;
  const webSources = Array.isArray(response.webSources)
    ? response.webSources.flatMap((value): NonNullable<Extract<OpsAssistantResponse, { kind: "ANSWER" }>["webSources"]>[number][] => {
        if (!value || typeof value !== "object") return [];
        const source = value as Record<string, unknown>;
        if (typeof source.id !== "string" || !/^web-[1-9][0-9]{0,2}$/u.test(source.id)
          || typeof source.title !== "string" || source.title.length > 500
          || typeof source.url !== "string" || source.url.length > 4_000
          || typeof source.summary !== "string" || source.summary.length > 4_000) return [];
        try {
          if (safeExternalHttpUrl(source.url) === null) return [];
        } catch { return []; }
        return [{ id: source.id, title: source.title, url: source.url, summary: source.summary }];
      }).slice(0, MAX_STORED_WEB_SOURCES)
    : undefined;
  const references = Array.isArray(response.references)
    ? response.references.flatMap((value) => {
        const parsed = OpsAssistantReferenceSchema.safeParse(value);
        return parsed.success ? [parsed.data] : [];
      }).slice(0, 20)
    : undefined;
  const artifacts = Array.isArray(response.artifacts)
    ? response.artifacts.flatMap((value) => {
        const parsed = OpsAssistantArtifactSchema.safeParse(value);
        return parsed.success ? [parsed.data] : [];
      }).slice(0, 4)
    : undefined;
  const proof = OpsPublicProofSummarySchema.safeParse(response.proof);
  return {
    kind: "ANSWER",
    text: response.text,
    ...(response.format === "REPORT" ? { format: "REPORT" as const } : {}),
    evidenceLabel: response.evidenceLabel as "Current board" | "Business rules" | "Business sources" | "WhatsApp sources" | "WhatsApp route" | "Web sources" | "Google Workspace" | "Google Meet transcript",
    workItemIds,
    ...(proposalIds?.length ? { proposalIds } : {}),
    ...(evidenceSources?.length ? { evidenceSources } : {}),
    ...(webSources?.length ? { webSources } : {}),
    ...(references?.length ? { references } : {}),
    ...(artifacts?.length ? { artifacts } : {}),
    ...(proof.success ? { proof: proof.data } : {}),
    operatingModelVersion: response.operatingModelVersion
  };
}

export function opsPublicProofLabel(proof: {
  calls: number;
  rows: number;
  bytes: number;
}): string {
  return `Verified live read · ${proof.calls} ${proof.calls === 1 ? "call" : "calls"} · ${proof.rows} ${proof.rows === 1 ? "row" : "rows"} · ${proof.bytes} bytes`;
}

function formatEvidenceTime(value: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(new Date(value));
  } catch {
    return "Recorded in WhatsApp";
  }
}

const OPS_EVIDENCE_PREVIEW_CHARS = 280;

export interface OpsEvidenceHighlightPreview {
  before: string;
  highlight: string;
  after: string;
  hasMore: boolean;
}

export interface OpsEvidenceGroupedHighlightPreview {
  segments: Array<{ text: string; highlighted: boolean }>;
  hasMore: boolean;
}

interface OpsEvidencePreviewWindow {
  start: number;
  end: number;
}

function opsEvidencePreviewWindow(
  excerptLength: number,
  highlightStart: number,
  highlightEnd: number
): OpsEvidencePreviewWindow {
  if (excerptLength <= OPS_EVIDENCE_PREVIEW_CHARS) return { start: 0, end: excerptLength };
  const spanLength = highlightEnd - highlightStart;
  const targetLength = Math.max(OPS_EVIDENCE_PREVIEW_CHARS, spanLength);
  const contextLength = targetLength - spanLength;
  let start = Math.max(0, highlightStart - Math.floor(contextLength / 2));
  let end = Math.min(excerptLength, highlightEnd + Math.ceil(contextLength / 2));
  const missingContext = targetLength - (end - start);
  if (missingContext > 0) {
    if (start === 0) end = Math.min(excerptLength, end + missingContext);
    else start = Math.max(0, start - missingContext);
  }
  return { start, end };
}

// WHY [BOL-OPS-SAME-MESSAGE-UNION-PROOF-001]: rendering each selected range's
// bounded preview repeated overlapping source text inside one proof dialog.
// Mechanically union only the existing bounded windows for one immutable
// sourceEventId + excerpt, then mark every retained exact range over those
// unchanged bytes. Falsifier: source text repeats, a selected range loses its
// highlight, masking moves an offset, or distinct source crops/visuals merge.
export function opsGroupedEvidenceHighlightPreview(
  sources: readonly OpsClaimEvidenceSource[],
  maskPhones = true
): OpsEvidenceGroupedHighlightPreview | null {
  const first = sources[0];
  if (!first || first.visual) return null;
  const ranges: Array<{ start: number; end: number }> = [];
  let presentation: string | null = null;
  for (const source of sources) {
    if (source.visual || source.sourceEventId !== first.sourceEventId || source.excerpt !== first.excerpt
      || source.excerpt.slice(source.highlightStart, source.highlightEnd) !== source.highlight) return null;
    const projected = projectPrivateTextSpan(first.excerpt, source.highlightStart, source.highlightEnd, maskPhones);
    if (!projected) return null;
    presentation ??= projected.presentation;
    ranges.push({ start: source.highlightStart, end: source.highlightEnd });
  }
  ranges.sort((left, right) => left.start - right.start || left.end - right.end);
  const windows: OpsEvidencePreviewWindow[] = [];
  for (const range of ranges) {
    const next = opsEvidencePreviewWindow(first.excerpt.length, range.start, range.end);
    const previous = windows[windows.length - 1];
    if (previous && next.start <= previous.end) previous.end = Math.max(previous.end, next.end);
    else windows.push({ ...next });
  }
  const segments: OpsEvidenceGroupedHighlightPreview["segments"] = [];
  const append = (text: string, highlighted: boolean) => {
    if (!text) return;
    const previous = segments[segments.length - 1];
    if (previous?.highlighted === highlighted) previous.text += text;
    else segments.push({ text, highlighted });
  };
  windows.forEach((window, windowIndex) => {
    if ((windowIndex === 0 && window.start > 0) || windowIndex > 0) append("…", false);
    const boundaries = new Set([window.start, window.end]);
    for (const range of ranges) {
      if (range.end <= window.start || range.start >= window.end) continue;
      boundaries.add(Math.max(window.start, range.start));
      boundaries.add(Math.min(window.end, range.end));
    }
    const ordered = [...boundaries].sort((left, right) => left - right);
    for (let index = 0; index < ordered.length - 1; index += 1) {
      const start = ordered[index]!;
      const end = ordered[index + 1]!;
      append(presentation!.slice(start, end), ranges.some((range) => range.start < end && range.end > start));
    }
  });
  const lastWindow = windows[windows.length - 1]!;
  if (lastWindow.end < first.excerpt.length) append("…", false);
  return {
    segments,
    hasMore: windows.length > 1 || windows[0]!.start > 0 || lastWindow.end < first.excerpt.length
  };
}

function opsExactEvidenceHighlight(
  excerpt: string,
  highlight: string,
  highlightStart: number,
  highlightEnd: number,
  maskPhones: boolean
): OpsEvidenceHighlightPreview | null {
  if (!highlight
    || excerpt.slice(highlightStart, highlightEnd) !== highlight) return null;
  const projected = projectPrivateTextSpan(excerpt, highlightStart, highlightEnd, maskPhones);
  if (!projected) return null;
  return {
    before: projected.before,
    highlight: projected.span,
    after: projected.after,
    hasMore: false
  };
}

// WHY [BOL-OPS-CLAIM-HIGHLIGHT-PREVIEW-001]: bounding a global source preview
// could hide the exact words that entailed the adjacent claim. Center the
// preview on the server-bound half-open span and never shorten that span; the
// complete excerpt remains behind Full source. Falsifier: a valid highlight is
// absent or changed in the card, a source from another claim appears beside it,
// or a long highlight is truncated to satisfy presentation width.
export function opsClaimEvidencePreview(
  excerpt: string,
  highlight: string,
  highlightStart: number,
  highlightEnd: number,
  maskPhones = true
): OpsEvidenceHighlightPreview | null {
  const exact = opsExactEvidenceHighlight(excerpt, highlight, highlightStart, highlightEnd, maskPhones);
  if (!exact) return null;
  const projected = projectPrivateTextSpan(excerpt, highlightStart, highlightEnd, maskPhones);
  if (!projected) return null;
  const { start, end } = opsEvidencePreviewWindow(excerpt.length, highlightStart, highlightEnd);
  return {
    before: `${start > 0 ? "…" : ""}${projected.presentation.slice(start, highlightStart)}`,
    highlight: projected.span,
    after: `${projected.presentation.slice(highlightEnd, end)}${end < excerpt.length ? "…" : ""}`,
    hasMore: start > 0 || end < excerpt.length
  };
}

export function opsEvidencePreview(value: string): { text: string; hasMore: boolean } {
  const trimmed = value.trim();
  if (trimmed.length <= OPS_EVIDENCE_PREVIEW_CHARS) return { text: trimmed, hasMore: false };
  const compact = trimmed.replace(/\s+/gu, " ");
  const naturalBoundary = compact.lastIndexOf(" ", OPS_EVIDENCE_PREVIEW_CHARS);
  const boundary = naturalBoundary >= 180 ? naturalBoundary : OPS_EVIDENCE_PREVIEW_CHARS;
  // WHY [BOL-OPS-CONCISE-PROOF-CARD-001]: a valid image inspection filled the
  // chat with its entire retained description, hiding the few lines needed to
  // judge a claim. This is presentation only: show one short scan preview and
  // keep every original character behind Full source/Open file. Never shorten
  // the stored/model evidence. Falsifier: full proof becomes unavailable, a
  // short message changes, or the default card again dumps a giant source.
  return { text: `${compact.slice(0, boundary).trimEnd()}…`, hasMore: true };
}

function browserStorage(): StorageLike | null {
  try { return typeof window === "undefined" ? null : window.localStorage; }
  catch { return null; }
}

function storageKey(scope: string): string {
  return `${STORAGE_PREFIX}:${scope}`;
}

type PendingOpsTextHint = Readonly<{
  turnId: string;
  prompt: string;
  releaseId: OpsSha256;
}>;

function pendingTextKey(scope: string): string {
  return `${PENDING_TEXT_PREFIX}:${scope}`;
}

// WHY [BOL-TEXT-DETACHED-CONTINUITY-001]: a tab can lose its response stream
// while the admitted server-owned turn still runs. This bounded local record is
// only a recovery *hint*; it never becomes model history, proof or effect
// authority. The authenticated exact-member status/history must validate it.
// Falsifier: reload starts another POST, a forged/cross-release hint grants a
// response or action, or a stale completion clears a newer turn's hint.
export function rememberPendingOpsTextTurn(
  store: StorageLike | null, scope: string, hint: PendingOpsTextHint, now = Date.now()
): void {
  if (!store || !PENDING_TEXT_ID.test(hint.turnId)
    || !/^sha256:[a-f0-9]{64}$/u.test(hint.releaseId)
    || !hint.prompt.trim() || hint.prompt.length > MAX_OWNER_MESSAGE_CHARS) return;
  try {
    store.setItem(pendingTextKey(scope), JSON.stringify({
      version: 1, ...hint, expiresAt: now + HISTORY_TTL_MS
    }));
  } catch { /* Same mounted tab still holds the turn; storage is a recovery hint. */ }
}

export function readPendingOpsTextTurn(
  store: StorageLike | null, scope: string, expectedReleaseId: OpsSha256 | null, now = Date.now()
): PendingOpsTextHint | null {
  if (!store || !expectedReleaseId) return null;
  try {
    const value: unknown = JSON.parse(store.getItem(pendingTextKey(scope)) ?? "null");
    if (!value || typeof value !== "object") return null;
    const hint = value as Record<string, unknown>;
    if (hint.version !== 1 || hint.releaseId !== expectedReleaseId
      || typeof hint.expiresAt !== "number" || hint.expiresAt <= now
      || typeof hint.turnId !== "string" || !PENDING_TEXT_ID.test(hint.turnId)
      || typeof hint.prompt !== "string" || !hint.prompt.trim()
      || hint.prompt.length > MAX_OWNER_MESSAGE_CHARS) return null;
    return { turnId: hint.turnId, prompt: hint.prompt, releaseId: expectedReleaseId };
  } catch { return null; }
}

export function forgetPendingOpsTextTurn(store: StorageLike | null, scope: string, turnId: string): void {
  if (!store) return;
  try {
    const value: unknown = JSON.parse(store.getItem(pendingTextKey(scope)) ?? "null");
    if (value && typeof value === "object" && (value as { turnId?: unknown }).turnId === turnId) {
      store.removeItem(pendingTextKey(scope));
    }
  } catch { /* A malformed hint has no authority and cannot affect the turn. */ }
}

type OpsTextStatus = Awaited<ReturnType<typeof api.opsTurnStatus>>;

export async function awaitOpsTextTurnStatus(input: Readonly<{
  turnId: string;
  releaseId: OpsSha256;
  signal: AbortSignal;
  readStatus: (turnId: string) => Promise<OpsTextStatus>;
  wait?: (ms: number, signal: AbortSignal) => Promise<void>;
  onAttachments?: (attachments: SharedAttachment[]) => void;
}>): Promise<OpsPublicTurnEnvelope | null> {
  const wait = input.wait ?? ((ms, signal) => new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => { signal.removeEventListener("abort", onAbort); resolve(); }, ms);
    const onAbort = () => { clearTimeout(timer); reject(signal.reason); };
    signal.addEventListener("abort", onAbort, { once: true });
    if (signal.aborted) onAbort();
  }));
  while (true) {
    input.signal.throwIfAborted();
    let status: OpsTextStatus;
    try {
      status = await input.readStatus(input.turnId);
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) throw error;
      await wait(3_000, input.signal); // 20/min, below the authenticated 30/min route limit.
      continue;
    }
    input.signal.throwIfAborted();
    if (status.turnId !== input.turnId) throw new Error("TEXT_TURN_STATUS_IDENTITY_INVALID");
    if (status.outcome === "NOT_ACTIVE") return null;
    if (status.outcome === "COMPLETED") {
      const final = decodeOpsPublicTurnEnvelope(status.publicTurn, input.releaseId);
      if (!final || final.turnId !== input.turnId || final.requestId !== input.turnId) {
        throw new Error("TEXT_TURN_STATUS_FINAL_INVALID");
      }
      input.onAttachments?.(parseSharedAttachments(status.attachments));
      return final;
    }
    await wait(3_000, input.signal);
  }
}
